/**
 * PWA の画面（M6a）。**縦に1本だけ通す。**
 *
 *   ログイン → （必要なら）パスワード変更 → 日次売上の入力と表示
 *
 * ## ★DOM は組み立てる。`innerHTML` を使わない
 *
 * 文字列で HTML を作ると、入力値の描画が XSS の経路になる。
 * `textContent` と `createElement` だけで組む（絶対ルール：ユーザー入力の描画）。
 *
 * ## ★書体はまだ決めない
 *
 * 和文・欧文の選定は M6 着手前の人間チェックポイント。
 * ここでは OS の既定に任せる。決める前に見た目を作り込むと、
 * 決めたときに全部やり直しになる。
 */
import "./styles.css";
import {
  ApiError,
  type Account,
  api,
  PASSWORD_RULE_TEXT,
  LOGIN_ID_RULE_TEXT,
  type AttendanceAnomaly,
  type AuditPage,
  type CalendarDraft,
  type ClosingChecks,
  type ClosingStatus,
  type DailySales,
  type DailySalesTarget,
  type InputCeilings,
  type MonthlyTargetCeilings,
  type SalesTargetCeilings,
  type InventoryCeilings,
  type PettyCashCeilings,
  type PurchaseCeilings,
  type FixedCost,
  type FixedCostCeilings,
  type Inventory,
  type MonthlyTarget,
  type LossRecord,
  type Me,
  type MinimumWageCheck,
  type ParametersResult,
  type CashBalance,
  type CashCount,
  type PettyCashAccount,
  type PettyCashEntry,
  type PunchCorrection,
  type PunchEvent,
  type PunchLogResult,
  type Purchase,
  type SelfDiagnosisResult,
  type Shift,
  type ShiftNoticeResult,
  type ShiftChangeRequestsResult,
  type ShiftRequest,
  type ShiftRequestsResult,
  type ShiftsResult,
  type Staff,
  type StaffAccountLink,
  type Vendor,
  type WageExclusion,
  type WagesResult,
} from "./api.js";
import { isMasked as isMaskedValue } from "../domain/auditDiff.js";
import { auditActionLabel, auditTableLabel } from "./audit-labels.js";
import {
  STATUS_LABEL,
  closingReasonNote,
  describeCheck,
  notesFor,
  ownerActions,
  ownerSubmittedReason,
  summarizeChecks,
} from "./closing-view.js";
import {
  accountReviewBanner,
  accountRowViews,
  brokenHashCaveat,
  ROLE_LABEL,
  roleChangeConfirmText,
  stopConfirmText,
} from "./account-review-view.js";
import {
  showedFullWageBanner,
  wageAlertBlocks,
  type WageAlertBlock,
  type WageAlertState,
} from "./wage-alert-view.js";
import { attendanceAnomalyHeading, attendanceAnomalyLines } from "./attendance-anomaly-view.js";
import { describeExpiry, describeKintaiHeldReason, describeKintaiIngest, hasNoIssues } from "./self-diagnosis-view.js";
import { barGeometry, summaryView } from "./sales-summary-view.js";
import type { SummaryLine } from "./sales-summary-view.js";
import type { DailyAnomalies, ProfitLoss, SalesSummary } from "./api.js";
import { profitLossView, trendCharts } from "./profit-loss-view.js";
import { anomalyView } from "./anomaly-view.js";
import {
  dailyTargetRows,
  dailyTargetsSummary,
  monthlyTargetFields,
} from "./targets-view.js";
import {
  ALLOC_LABEL,
  amountToSend,
  fixedCostRows,
  fixedCostSummary,
} from "./fixed-cost-view.js";
import { calendarRows, calendarSummary } from "./calendar-view.js";
import {
  PUNCH_LABEL,
  PUNCH_TYPES,
  correctionLine,
  correctionTypeChoices,
  nextPunchOptions,
  normalizeOccurredAtInput,
} from "./punch-view.js";
import {
  formatHhmm,
  formatShiftDate,
  formatShiftMonth,
  groupByDate,
  parseHhmm,
  requestStatusLabel,
  shiftSummary,
} from "./shift-view.js";
import { confirmDiscardDocumentDrafts, documentIntakeView, hasUnsavedDocumentDrafts } from "./document-intake-view.js";
import { dailyReportView, discardDailyReportDraft, hasUnsavedDailyReport } from "./daily-report-view.js";
import { purchaseTotals, todayHomeView } from "./today-home-view.js";

const root = document.getElementById("app");

/**
 * 日本時間の暦日（`YYYY-MM-DD`）。
 *
 * ★★**`toISOString()` を使わない。** あれは UTC の日付なので、
 *   **日本時間の 00:00〜08:59 のあいだ、1日前の日付を返す。**
 *   閉店後（深夜）に前日ぶんを入れるこの店の運用では、ここが毎晩ずれる帯に当たる。
 * ★サーバは一貫して `AT TIME ZONE 'Asia/Tokyo'`（`src/infra/parameters.ts` ほか）。
 *   画面だけ UTC だと、同じ「今日」が画面とサーバで食い違う。
 * ★`sv-SE` は `YYYY-MM-DD` で出す唯一の実用ロケール。表示用ではなく値の整形に使う。
 */
export function todayJst(now: Date = new Date()): string {
  return now.toLocaleDateString("sv-SE", { timeZone: "Asia/Tokyo" });
}

/** 日本時間の前日。★「前の営業日」ではない（カレンダーを見ていない） */
export function previousDay(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * 保存していない入力を持つフォームの見張り（引き継ぎ §4「未保存内容の保護」）。
 *
 * ★★**登録は `onSubmit` の中で行う。** 保存フォームの大半はこの1つの関数を通るので、
 *   ここに置けば新しく足したフォームも自動で守られる。
 * ★★**ただし `onSubmit` を通らない保存フォームが3本ある**（修正の申請・希望の申請・
 *   アカウント作成。送る前に自前の検査をするため）。そこは手で `guardForm(form)` を呼ぶ。
 *   ★2026-09-20 に「23本すべてがそこを通る」と書いて3本漏らした（code-reviewer の blocking）——
 *   **呼び出しの数ではなく、画面の `form` の側から数える**。検査も画面の側から全フォームを当てる
 *   （`tests/ui/main-load-tabs.test.ts`「全タブの保存フォームが見張られている」）。
 *   ★フォームごとに `addEventListener("input", …)` を足していく形にしない。
 *
 * ★**変わったかどうかは、描いた直後の値と今の値を比べて決める。**
 *   入力のたびに旗を立てる形だと、打ってから消して元に戻した人にも聞くことになる。
 * ★`FormData` は `name` を持つ欄だけを見る。★ファイル欄（証憑の写真）はここでは判定できないので、
 *   **証憑は従来どおり `hasUnsavedDocumentDrafts()` が持つ**（役割を混ぜない）。
 */
const guardedForms: { form: HTMLFormElement; initial: string }[] = [];

const formSnapshot = (form: HTMLFormElement): string =>
  JSON.stringify([...new FormData(form)].map(([k, v]) => [k, typeof v === "string" ? v : "(file)"]));

function guardForm(form: HTMLFormElement): void {
  /*
   * ★**同じ実行の続きで写す。** 描いたあとに値を入れ直す画面があり（固定費の行など）、
   *   登録したその場で写すと「利用者が触っていないのに変わっている」になる。
   */
  queueMicrotask(() => {
    guardedForms.push({ form, initial: formSnapshot(form) });
  });
}

/**
 * 画面のどこかに、保存していない入力があるか。
 *
 * ★**画面に繋がっているかは見ていない。** 登録は `renderShell` が画面を描き直すたびに
 *   捨てるので、ここに残っているのは**いま出ている画面のフォームだけ**。
 *   `isConnected` を足すと、どの変異でも落ちない枝が1本増える。
 */
export function hasUnsavedFormEdits(): boolean {
  return guardedForms.some(({ form, initial }) => formSnapshot(form) !== initial);
}

/**
 * タブ・日付・画面を移る前の確認。
 *
 * ★★**未保存を持つ画面が増えたら、必ずここへ足す**（引き継ぎ §4「未保存保護」）。
 *   移動の入口は3箇所（日付の変更・タブの切替・気づきの帯からの移動）あり、
 *   **判断をその3箇所に書くと、足し忘れた1箇所だけが黙ってデータを捨てる。**
 * ★聞くのは**一度だけ**。証憑と振り返りで2回聞くと、1回目に「はい」を押した時点で
 *   証憑が捨てられ、2回目に「いいえ」を押しても戻らない。
 *
 * @param confirmFn 検査から差し替える（画面では `window.confirm`）
 */
export function confirmLeave(confirmFn: (message: string) => boolean = window.confirm): boolean {
  const documents = hasUnsavedDocumentDrafts();
  const report = hasUnsavedDailyReport();
  // ★売上・仕入・小口ほか、保存フォームに打ち込んだままの値（2026-09-20）
  const forms = hasUnsavedFormEdits();
  if (!documents && !report && !forms) return true;
  const what = [
    documents ? "画像または確認下書き" : null,
    report ? "振り返り" : null,
    forms ? "入力中の値" : null,
  ]
    .filter((v): v is string => v !== null)
    .join("と");
  if (!confirmFn(`保存していない${what}があります。破棄して移動しますか？`)) return false;
  // ★ここまで来たら1回の返事で両方を捨てる（もう一度は聞かない）
  confirmDiscardDocumentDrafts(() => true);
  discardDailyReportDraft();
  return true;
}

/**
 * リロード・タブを閉じる・ブラウザの戻る（アプリの外へ出る移動）の確認。
 *
 * ★★`confirmLeave` と**同じ判定**を使う。2026-09-21 まで、ここは証憑だけを見ていて
 *   （`document-intake-view.ts` に別に置いていた）、売上フォームや振り返りの打ちかけは
 *   アプリ内の移動では守られるのにリロードでは黙って消えた。
 * ★文言はブラウザが決める（`returnValue` の中身は表示されない）。
 */
export function onBeforeUnload(event: BeforeUnloadEvent): void {
  if (!hasUnsavedDocumentDrafts() && !hasUnsavedDailyReport() && !hasUnsavedFormEdits()) return;
  event.preventDefault();
  event.returnValue = "";
}

/** 今日ではなく**前日**を初期値にする。朝いちばんに前日分を入れる運用のため */
function yesterday(): string {
  return previousDay(todayJst());
}

/**
 * 月を単位に見るタブ（＝ヘッダのピッカーが「対象月」になるタブ）。
 *
 * ★**`date` に `YYYY-MM-01` を押し込むのはこの2つだけ。** 日次のタブへ移るときに
 *   その値を持ち出さない判断（`renderShell` の `tabDate`）が、ここの答えに寄りかかっている。
 * ★タブを足すときは、ここに足すかどうかを**先に**決めること。
 */
function isMonthlyTab(tab: Tab): boolean {
  return tab === "inventory" || tab === "shift";
}

/**
 * 打刻タブで取りに行く月（`YYYY-MM`）。★次に押せるボタンの判定には使わない
 * （そちらはサーバが焼き付けた `businessDate` から `nextPunchOptions` が決める）。
 * ここは「自分の直近の勤務を含む月であれば足りる」というだけの範囲指定。
 */
function punchMonth(): string {
  // ★日本時間で切る。UTC だと 1日の 00:00〜08:59 に前の月を取りに行く
  return todayJst().slice(0, 7);
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: { text?: string; className?: string } = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (props.text !== undefined) node.textContent = props.text;
  if (props.className !== undefined) node.className = props.className;
  return node;
}

/**
 * @param autocomplete ★パスワード欄で `current-password` / `new-password` を渡す。
 *   付けないと、パスワード管理ツールが**新旧を取り違えて**古い値を新欄へ入れる。
 *   金額欄（`type === "number"`）は従来どおり `off` に固定する。
 */
function field(
  label: string,
  name: string,
  value = "",
  type = "number",
  autocomplete?: AutoFill,
): HTMLLabelElement {
  const wrap = el("label", { className: "field" });
  wrap.append(el("span", { text: label }));
  const input = el("input");
  input.name = name;
  input.value = value;
  if (autocomplete !== undefined) input.autocomplete = autocomplete;
  if (type === "number") {
    /*
     * ★`type="number"` を使わない（2026-08-20）。GOV.UK Design System が2020年に
     *   捨てたのと同じ理由——**マウスホイールで値が勝手に増減する**、
     *   Safari が桁区切りのカンマを勝手に入れる、Chrome が大きい数を指数表記にする、
     *   口述入力で選べない、読み上げの要素一覧で無ラベルになる。
     *   金額の誤りがそのまま損益に乗るこのシステムでは、どれも実害になる。
     *   根拠：`.loop/design-research.md` §1
     *
     * ★キーボードは `inputmode` で出す。`pattern` は iOS の古い版のための保険で、
     *   **正の整数の形をなぞるだけ。これも最後の砦ではない**（負数も桁あふれもここでは止まらない）。
     *   桁あふれ・負数の拒否は**サーバ側（`assertAmount`）が最後の砦**で、
     *   `min` / `step` はもともと保証になっていない。
     */
    input.type = "text";
    input.inputMode = "numeric";
    input.pattern = "[0-9]*";
    input.autocomplete = "off";
  } else {
    input.type = type;
  }
  wrap.append(input);
  return wrap;
}

/**
 * 打鍵の段階で桁を止める（`migrations/0061` の業務上限 / AC-4）。
 *
 * ★`maxlength` にするのは**上限そのものではなく桁数**。`field()` は `type="text"` なので
 *   `max` が効かない（`type="number"` を使わない理由は `field()` の注記）。
 * ★**桁数なので 200 の上限に対して 999 までは打てる。**「打ち切れない」ことを防ぐのが役目で、
 *   ★**止める権威はサーバ**（`assertAmount` が業務上限ちょうどで断り、その数字を文言に出す）。
 * ★上限は画面に写さずサーバから受け取る。写すと、パラメータを変えた日に
 *   「打てるのに 400 になる欄」か「打てないのに通る欄」が静かに生まれる。
 */
function capped(wrap: HTMLLabelElement, ceiling: number | undefined): HTMLLabelElement {
  const input = wrap.querySelector("input");
  if (input !== null && ceiling !== undefined && Number.isFinite(ceiling) && ceiling > 0) {
    input.maxLength = String(Math.trunc(ceiling)).length;
  }
  return wrap;
}

function notice(message: string, kind: "error" | "info" = "error"): HTMLParagraphElement {
  const p = el("p", { text: message, className: `notice ${kind}` });
  p.setAttribute("role", kind === "error" ? "alert" : "status");
  return p;
}

/**
 * 画面をまたいで運ぶ通知（UI-NOTICE-KIND）。
 *
 * ★**種別は通知を作る側が決める。** 文字列だけを運んでいたあいだ、描画側が推測で色を決め、
 *   売上・棚卸では「保存しました」が赤（`role=alert`）、他のタブではサーバの断りが
 *   普通のお知らせ（`role=status`）で出ていた。型で種別を必須にし、呼び出し側の書き漏れを `tsc` に拾わせる。
 */
export interface Notice {
  text: string;
  kind: "error" | "info";
}
const infoNotice = (text: string): Notice => ({ text, kind: "info" });
const errorNotice = (text: string): Notice => ({ text, kind: "error" });
/**
 * ★**描いたら画面内へ運ぶ**（UI-NOTICE-KIND 周回2・spec の差し戻し）。
 *   通知はセクションの先頭に出るが、`load` の描き直しはスクロール位置を保つ。
 *   375×667 の設定タブ（高さ 14,131px）で一番下の「改定を保存する」から送ると、
 *   通知は画面の 12,669px 上に出て**見えなかった**（本体が実測）。
 * ★呼ぶのは描画の後（`setTimeout`）。作った時点ではまだ文書に入っていない。
 * ★`block: "nearest"`：既に見えていれば動かさない。
 * ★jsdom は `scrollIntoView` を実装していないので、無ければ何もしない。
 */
const noticeOf = (n: Notice): HTMLParagraphElement => {
  const p = notice(n.text, n.kind);
  setTimeout(() => {
    if (p.isConnected && typeof p.scrollIntoView === "function") p.scrollIntoView({ block: "nearest" });
  }, 0);
  return p;
};

/**
 * 送信の失敗を、**押したボタンの直前に**出す（UI-FORM-KEEP-INPUT / `docs/10` §4）。
 *
 * ★**描き直さない。** 失敗で `load` を呼ぶと、サーバの値で画面ごと描き直して
 *   **打った値が消える**（日別目標・カレンダー・修正申請の理由など）。描き直すのは成功時だけ。
 * ★同じボタンの失敗は1つを差し替える（積み増さない）。`role=alert` は差し込み・書き換えの時点で読み上げられる。
 * ★押せない間にフォーカスが外れるので、ボタンを戻してフォーカスも戻す。
 * ★jsdom は `scrollIntoView` を実装していないので、無ければ何もしない。
 *
 * @param reason 画面側の案内（文字列）か、送信で起きた例外。
 *   ★**401 はログイン画面へ送る**（周回1 security A-2）。描き直していた頃は `load` の catch が拾っていたが、
 *   描き直さなくなると「ログインしてください」と出るのにフォームが無い行き止まりになる。
 *   ★`ApiError` 以外（通信断など）は**保存できたか分からない**と言う（周回1 security A-3）。
 *   打った値が残るので、「もう一度」とだけ言うと、届いていた追加がもう1件増える。
 */
function failAtButton(button: HTMLButtonElement, reason: unknown): void {
  if (reason instanceof ApiError && reason.status === 401) {
    renderLogin(reason.message);
    return;
  }
  const text =
    typeof reason === "string"
      ? reason
      : reason instanceof ApiError
        ? reason.message
        : "通信が途切れ、送信できたか確かめられませんでした。タブを開き直して記録を確かめてから、もう一度お試しください。";
  button.disabled = false;
  const prev = button.previousElementSibling;
  const p =
    prev instanceof HTMLParagraphElement && prev.dataset.failure !== undefined ? prev : notice("", "error");
  p.dataset.failure = "";
  p.textContent = text;
  if (p !== prev) button.before(p);
  button.focus();
  if (typeof p.scrollIntoView === "function") p.scrollIntoView({ block: "nearest" });
}

const yen = (v: string): string => `${Number(v).toLocaleString()}円`;

/**
 * 業務フォームの送信。
 *
 * ★7本の送信ハンドラが同じ形をしていた:
 *   preventDefault → FormData → 二重送信を止める → API →
 *   成功なら再描画 / 失敗なら押せる状態に戻して理由を出す。
 *
 * 写しが増えるほど「1本だけ二重送信を止め忘れる」余地が増えるので1つにまとめる。
 *
 * ★ログインとパスワード変更は**含めない。** あの2本は成功時の行き先が
 *   `load()` ではなく別の画面なので、同じ形に見えて分岐が違う。
 *
 * @param after   再描画。**成功時だけ**呼ぶ（`successMessage` が渡る）。
 *   ★失敗時は呼ばない——描き直すと打った値が消える。理由は `failAtButton` がボタンの直前に出す
 */
function onSubmit(
  form: HTMLFormElement,
  submit: HTMLButtonElement,
  run: (data: FormData) => Promise<unknown>,
  after: (message?: Notice) => void,
  successMessage?: string,
): void {
  // ★未保存の見張りへ登録する（`onSubmit` を通らない3本は手で呼ぶ。`guardForm` の説明）
  guardForm(form);
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    submit.disabled = true;
    void run(new FormData(form))
      .then(() => after(successMessage === undefined ? undefined : infoNotice(successMessage)))
      // ★`ApiError` 以外（通信断など）も黙らない（UI-NOTICE-KIND AC-4）。文言は `failAtButton` が決める
      .catch((error: unknown) => failAtButton(submit, error));
  });
}

/** ラベル表から `<select>` の選択肢を作る。★表と選択肢を二重に持たない */
const optionsOf = (labels: Record<string, string>): { value: string; label: string }[] =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

// ---------------------------------------------------------------------
// ログイン
// ---------------------------------------------------------------------

function renderLogin(message?: string): void {
  // ★別の人がログインし直したら、その人には1回また全面で見せる（AC-8）
  shownFullWageBanner = false;
  /*
   * ★★**セッションの境界で捨てるものは、ここ1箇所に集める**（2026-09-20・security の A-4）。
   *   `guardedForms` の破棄は `renderShell` にもあるが、**ログアウトはそこを通らない**——
   *   離脱済みのフォームへの強参照と写し（★**時給のフォームも通る**）が、
   *   ログイン画面のあいだ残っていた。★**店舗端末は共有**（`docs/05` §5.4）。
   *   ★読み出す経路は無かったが、捨て場が2箇所に分かれている時点で、次に足す状態は片方に付く。
   */
  guardedForms.length = 0;
  if (!root) return;
  root.replaceChildren();
  root.append(el("h1", { text: "おさ和風゛店舗アプリ" }));
  if (message !== undefined) root.append(notice(message));

  const form = el("form");
  form.append(field("ログインID", "loginId", "", "text"));
  form.append(field("パスワード", "password", "", "password"));

  const keep = el("label", { className: "checkbox" });
  const keepInput = el("input");
  keepInput.type = "checkbox";
  keepInput.name = "persistent";
  keep.append(keepInput, el("span", { text: "ログインしたままにする" }));
  // ★店長・オーナー以外は、希望してもサーバ側で無視される（権限マトリクス §5.4）
  keep.append(el("small", { text: "店舗端末は共有のため、店長・オーナーのみ有効です" }));
  form.append(keep);

  const submit = el("button", { text: "ログイン" });
  submit.type = "submit";
  form.append(submit);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const data = new FormData(form);
    submit.disabled = true;
    void api
      .login(
        String(data.get("loginId") ?? ""),
        String(data.get("password") ?? ""),
        data.get("persistent") === "on",
      )
      .then(() => start())
      .catch((error: unknown) => {
        submit.disabled = false;
        renderLogin(error instanceof ApiError ? error.message : "ログインできませんでした");
      });
  });

  root.append(form);
}

// ---------------------------------------------------------------------
// パスワード変更（初回は必須）
// ---------------------------------------------------------------------

function renderPasswordChange(me: Me, message?: string): void {
  if (!root) return;
  root.replaceChildren();
  root.append(el("h1", { text: me.mustChooseLoginId ? "ログインIDとパスワードの設定" : "パスワードの変更" }));
  root.append(
    notice(
      "最初のパスワードは、お渡しする途中で人目に触れています。" +
        "ここで変更するまで、業務の画面は使えません。" +
        (me.mustChangePassword ? "" : "いま使っているパスワードも入力してください。"),
      "info",
    ),
  );
  if (message !== undefined) root.append(notice(message));

  const form = el("form");
  /*
   * ★`method` を書いておく。submit ハンドラが走らなかったとき、既定の GET だと
   *   **パスワードが URL に載る。** いまは先頭で `preventDefault` しているので起きないが、
   *   1語で塞げる保険（2026-08-21 / B-2 の security 指摘）。
   */
  form.method = "post";
  /*
   * ★初回の強制変更では現在のパスワードを聞かない（2026-09-18 クライアント要望 R-08。
   *   仮パスワードで入った直後にもう一度打たせない）。省略できるかはサーバが
   *   `must_change_password` で決める。それ以外の変更では必ず確認する（B-2・共有端末の放置セッション対策）
   */
  if (!me.mustChangePassword) {
    form.append(field("現在のパスワード", "currentPassword", "", "password", "current-password"));
  }
  /*
   * ★ログインIDは本人が最初に1回だけ決める（SELF-LOGIN-ID・2026-09-21 依頼者要望）。
   *   仮のIDを初期値に入れておく（そのままでよければ打たずに済む）。決めた後は変えられない
   */
  if (me.mustChooseLoginId) {
    form.append(field("ログインID（次回からこれで入ります）", "newLoginId", me.loginId, "text", "username"));
    form.append(
      el("p", { text: `${LOGIN_ID_RULE_TEXT}。一度決めると自分では変えられません`, className: "muted" }),
    );
  }
  form.append(
    field("新しいパスワード", "newPassword", "", "password", "new-password"),
  );
  // ★決まりを欄のすぐ下に出す。断られてから知るのでは遅い（2026-09-16 クライアント要望）
  form.append(
    el("p", {
      text: `${PASSWORD_RULE_TEXT}。半角の英数字と記号だけが使えます（かな・漢字・空白は使えません）`,
      className: "muted",
    }),
  );
  const submit = el("button", { text: "変更する" });
  submit.type = "submit";
  form.append(submit);

  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const data = new FormData(form);
    const current = me.mustChangePassword ? undefined : String(data.get("currentPassword") ?? "");
    const value = String(data.get("newPassword") ?? "");
    const loginId = me.mustChooseLoginId ? String(data.get("newLoginId") ?? "").trim() : undefined;
    submit.disabled = true;
    void api
      .changePassword(current, value, loginId)
      // ★変更するとセッションが全部切れる。ログインし直してもらう
      .then(() =>
        renderLogin(
          loginId === undefined
            ? "パスワードを変更しました。新しいパスワードでログインしてください"
            : `設定しました。ログインID「${loginId}」と新しいパスワードでログインしてください`,
        ),
      )
      .catch((error: unknown) => {
        submit.disabled = false;
        renderPasswordChange(me, error instanceof ApiError ? error.message : "変更できませんでした");
      });
  });

  root.append(form);
}

// ---------------------------------------------------------------------
// 日次売上
// ---------------------------------------------------------------------

const CLOSING_LABEL: Record<ClosingStatus, string> = {
  OPEN: "入力中",
  SUBMITTED: "締め申請済み",
  CLOSED: "締め済み",
  REJECTED: "差戻し",
  REOPENED: "締め解除",
};

/** 画面のタブ。★権限で出し分けるのは見た目の都合。安全の担保はサーバ側 */
export type Tab =
  | "workflow"
  | "sales"
  | "purchases"
  | "petty"
  | "inventory"
  | "loss"
  | "punch"
  | "shift"
  | "staff"
  | "settings"
  | "closing"
  | "audit"
  | "diagnosis"
  | "summary";

/**
 * ★`roles` は権限マトリクス §3.1 の「そのロールが読める行」に合わせる。
 *
 * もとは `me.role !== "staff"` の1条件だけで出し分けていた。ロールが2種類しか
 * 無いあいだは足りていたが、**棚卸だけ manager 以上**（§3.1 で leader が `—`）
 * なので、その形のままだと leader に開けないタブが出る。
 */
/**
 * タブの束ね先（`docs/10` §5・2026-09-20 依頼者判断）。
 *
 * ★**主ナビは3本**（今日の業務／記録／月のまとめ）。押すとその束の先頭のタブへ入り、
 *   束の中身は**副ナビ**として2段目に並ぶ。13本を一度に並べていたのをやめた。
 * ★`account: true` の束は**アカウントメニューの中**へ入れる。
 *   「シフト・出退勤」は**別サイトへ移す前提**なので主ナビに混ぜない。
 * ★★**例外は1つだけ：`staff` の「シフト・出退勤」は主ナビに残す**（依頼者判断 2026-09-20）。
 *   staff の主作業は打刻で、アカウントメニューへ畳むと毎日の入口が1段深くなる。
 *   ★例外をここ以外に書かない（`accountGroup()` の1箇所）。
 */
type TabGroup = "today" | "attendance" | "records" | "month" | "admin";

/**
 * 主ナビのアイコン（UI-REFINE・2026-09-24 依頼者がモックを承認）。文字だけだと押せる場所に見えにくい。
 * ★飾り（`aria-hidden`）。名前はボタンの文字が持つ。★束が増えたら線を足す（無ければ空の枠で出る）
 */
const NAV_ICON_PATH: Partial<Record<TabGroup, string>> = {
  today: "M4 11l8-7 8 7v9H4z",
  attendance: "M12 7v5l3 2M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z",
  records: "M5 4h14v16H5zM8 9h8M8 13h8M8 17h5",
  month: "M4 6h16v14H4zM4 10h16M9 3v5M15 3v5",
};
function navIcon(key: TabGroup): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  svg.setAttribute("class", "nav-icon");
  const path = document.createElementNS(NS, "path");
  path.setAttribute("d", NAV_ICON_PATH[key] ?? "");
  svg.append(path);
  return svg;
}

/** ★並び順はこの配列が唯一の根拠。主ナビも、アカウントメニューの中も、ここの順に出る */
const GROUPS: readonly { key: TabGroup; label: string; account?: true }[] = [
  { key: "today", label: "今日の業務" },
  { key: "attendance", label: "シフト・出退勤", account: true },
  { key: "records", label: "記録" },
  { key: "month", label: "月のまとめ" },
  { key: "admin", label: "管理", account: true },
];

const TABS: readonly { key: Tab; label: string; roles: readonly Me["role"][]; group: TabGroup }[] = [
  { key: "workflow", label: "今日の業務", roles: ["leader", "manager", "owner"], group: "today" },
  { key: "sales", label: "売上", roles: ["leader", "manager", "owner"], group: "records" },
  { key: "purchases", label: "仕入", roles: ["leader", "manager", "owner"], group: "records" },
  { key: "petty", label: "小口現金", roles: ["leader", "manager", "owner"], group: "records" },
  { key: "inventory", label: "棚卸", roles: ["manager", "owner"], group: "month" },
  // ★ロスだけ staff も入れる（§3.1 の注記。書けるが読めない）
  { key: "loss", label: "ロス", roles: ["staff", "leader", "manager", "owner"], group: "records" },
  /*
   * ★§3.6「打刻」= 全ロール W(個)（R0102 A-4）。staff が最初に触る面のひとつ。
   *   CSV 出力（owner のみ）はこの画面の中で出し分ける（タブ自体は分けない）。
   */
  { key: "punch", label: "打刻", roles: ["staff", "leader", "manager", "owner"], group: "attendance" },
  /*
   * ★§3.7「確定シフトの閲覧」= 全ロール `R`・**店舗全体・氏名つき**（R-02 / R0102 B-5）。
   *   ★**タブは分けない**（§3.7 が「『自分のシフト』に別の行を作らない」と明示している）。
   *   「自分の分だけ」の絞り込みも作成フォームも、この画面の中で出し分ける
   *   （打刻タブの CSV 出力と同じ形）。
   */
  { key: "shift", label: "シフト", roles: ["staff", "leader", "manager", "owner"], group: "attendance" },
  /*
   * ★§3.4 パラメータ。**読みも書きも manager 以上**（2026-08-18 改訂）。
   *   leader を入れないこと。日次タブから写すと leader が付いてくる。
   *   ★書けるキーは manager と owner で違う（原典 §3.4.1 / R-05）。
   *     **タブを開ける条件はそれとは別**で、ここは read に合わせる。
   */
  /*
   * ★§3.3 従業員マスタ・時給マスタ。**読みはどちらも manager 以上。**
   *   書きはどちらも owner のみ（時給は 2026-08-18 に広げ、2026-08-21 の R-05 で戻した）。
   *   ★**ロール構成がまた同じになったが §3.3 の別の行。** 片方を動かすとき一緒に動かさない。
   *   ここは「開ける人」の指定なので read に合わせる。**書けるかどうかは画面の中で分ける**
   *   （そして本当の担保はサーバ側。絶対ルール1）。
   */
  { key: "staff", label: "従業員", roles: ["manager", "owner"], group: "admin" },
  { key: "settings", label: "設定", roles: ["manager", "owner"], group: "admin" },
  /*
   * ★§3.5「自動検算の結果」（read）と「月次締め 申請」（A）。どちらも manager 以上（M9c）。
   *   確定・解除は **owner だけの別の行**で、まだ実装していない（M10）。
   */
  { key: "closing", label: "締め", roles: ["manager", "owner"], group: "month" },
  /*
   * ★§3.5「監査ログの閲覧」（manager 以上の read）。F-22a。
   *   ★390px では複数段に折り返す。**本数をここに書かない**——
   *     写した数は腐る（CLAUDE.md「本数を転記しない」）。数えるのは `docs/10` §5 と `TABS` の長さ。
   *   構成の見直し（モックアップは4本）は**提案してから**やること（`docs/10` §5）。
   */
  { key: "audit", label: "監査ログ", roles: ["manager", "owner"], group: "admin" },
  /*
   * ★§3.5「システム状態の自己診断」（manager 以上の read。F-19）。
   *   13年間このシステムに無かった「壊れに気づく」仕組み。CLAUDE.md「自動検算は最優先機能」の親戚。
   */
  { key: "diagnosis", label: "自己診断", roles: ["manager", "owner"], group: "admin" },
  /*
   * ★§3.8「売上サマリの閲覧」= **全ロール `R`**（R-03 / C-1 で新設・C-3 で `ROW_TO_RESOURCE` へ移した）。
   *   ★**この epic で `staff` が初めて売上に到達する面。** 担保はサーバ側（T-16）で、
   *   ここに書く `roles` は見た目の都合でしかない。
   * ★タブが1本増えた（`docs/10` §5 の本数を同じ周回で直した。**ここには数を書かない**）。
   * ★末尾に置く：先頭へ入れると全ロールのタブの並びが1つずつずれる。
   *   ★`staff` の初期タブは「打刻」のまま（`initialTabFor`。B-4 の決着）。
   */
  { key: "summary", label: "売上要約", roles: ["staff", "leader", "manager", "owner"], group: "records" },
];

export interface ScreenContext {
  root: HTMLElement;
  me: Me;
  date: string;
  closing: ClosingStatus;
  /** 締め済み・申請済みで、この人は書けない */
  locked: boolean;
}

/**
 * ★人数入りの警告を、このページで**もう出したか**（M5b AC-8）。
 *
 * 店舗端末は共有で、staff / leader も同じ画面の前に立つ。
 * 「10名が1,092円を下回る」は**20名の店舗では個人の推測に近づく**（絶対ルール1）。
 * だが常時隠すと気づけない——それはこの課題が塞いだ穴そのもの。
 * **初回だけ全面に出し、以降は畳む**（2026-08-18・依頼者判断）。
 *
 * ★`sessionStorage` を使わない。読み込み直しは「入り直した」と読んでよく、
 *   そのとき1回また見せるのが正しいため。ログイン画面へ戻ったらここも戻す。
 */
let shownFullWageBanner = false;

/**
 * 監査ログの絞り込み（F-22a）。★画面を描き直しても保つ。
 *
 * ★URL には出していない。この画面はルータを持たないので、
 *   出すなら全タブぶんの設計が要る（`docs/10` の advisory）。
 */
export interface AuditFilter {
  table: string;
  action: string;
  from: string;
  to: string;
  actor: string;
  /** ★接続元アドレス（A6-2b）。空文字は「すべて」。**「記録なし」は表せない**（対象外） */
  ip: string;
  offset: number;
  sessions: boolean;
}

let auditFilter: AuditFilter = {
  table: "",
  action: "",
  from: "",
  to: "",
  actor: "",
  ip: "",
  offset: 0,
  sessions: false,
};

/**
 * 絞り込みの状態を、`/api/audit` のクエリへ写す。
 *
 * ★**関数にしてあるのは検査のため。** `load()` の中に置いたままだと、
 *   1行落としても**どのテストも落ちない**（2026-08-25 に変異で実測。
 *   `action` をクエリに載せない変異が緑のまま通った）。
 *   `.loop/memory.md`「純粋関数だけを検査すると、DB から引数へ写す配線が丸ごと無検査で残る」。
 *
 * ★**空文字は載せない。** 載せると `?actor=` が「空文字の人で絞る」の意味になり、
 *   サーバ側で `""` を `null` に読み替える約束に寄りかかることになる。
 */
export function auditQueryParams(filter: AuditFilter): Record<string, string> {
  return {
    ...(filter.table !== "" ? { table: filter.table } : {}),
    ...(filter.action !== "" ? { action: filter.action } : {}),
    ...(filter.from !== "" ? { from: filter.from } : {}),
    ...(filter.to !== "" ? { to: filter.to } : {}),
    ...(filter.actor !== "" ? { actor: filter.actor } : {}),
    ...(filter.ip !== "" ? { ip: filter.ip } : {}),
    ...(filter.sessions ? { sessions: "1" } : {}),
    offset: String(filter.offset),
  };
}

/**
 * 最低賃金の警告を画面上部に出す（M5b / 案B）。
 *
 * ★**タブごとに書かない。** `renderShell` は全タブが必ず通る唯一の場所なので、
 *   ここに置けば「あのタブだけ出ない」が構造的に起きない
 *   （`.loop/memory.md`「判定を各所に書くと、書き忘れた場所が『制限なし』になる」）。
 *
 * ★**この関数は出す／出さないを決めない。** 何を出すかは `wageAlertBlocks`
 *   （`src/ui/wage-alert-view.ts`）が決め、ここは DOM へ写すだけ。
 *   判断をこちらへ1つでも戻すと、その分岐がまた無検査になる（M5e）。
 */
export function minimumWageBanner(me: Me, blocks: readonly WageAlertBlock[]): HTMLElement[] {
  const out: HTMLElement[] = [];

  for (const b of blocks) {
    if (b.kind === "failure") {
      const body = notice(b.text, "error");
      /*
       * ★その日の判定画面へ移す（AC-7）。直す導線は M5c の一括改定カードにあり、
       *   そこは**画面の営業日でしか改定できない**（M5c で日付欄を外した理由がそれ）。
       *   だから日付ごと移動させる。警告の中に2つ目の改定入口を作らない。
       */
      const jump = el("button", { text: `${b.date} の判定を開く` });
      jump.type = "button";
      jump.addEventListener("click", () => void load(me, b.date, "settings"));

      if (b.summary !== null) {
        /*
         * ★2回目以降は畳む（AC-8）。**消すのではない。**
         *   ★`<details>` は素の HTML。開閉の状態を持つ仕組みを自前で書かない。
         *   ★見出し側に既存の `.notice error` を当てる（新しい CSS を足さない）。
         *     畳んでいても**赤い1行として残る**。
         */
        const fold = el("details");
        fold.append(el("summary", { text: b.summary, className: "notice error" }));
        fold.append(body, jump);
        out.push(fold);
      } else {
        out.push(body, jump);
      }
      continue;
    }

    if (b.kind === "passed") {
      out.push(el("p", { text: b.text, className: "muted" }));
      continue;
    }

    out.push(notice(b.text, b.kind === "unavailable" ? "error" : "info"));
  }

  return out;
}

/** 画面の枠（ヘッダ・日付・締め状態・タブ）を作り、中身は呼び出し側が足す */
/**
 * 気づきの取得結果。★`undefined` は「聞いていない」（シフトタブ自身）、
 * `"unavailable"` は「聞いたが取れなかった」。**取り違えない**（M5b の差し戻し）。
 */
type ShiftNoticeState = ShiftNoticeResult | "unavailable";

function renderShell(
  me: Me,
  date: string,
  closing: ClosingStatus,
  tab: Tab,
  wageAlert?: WageAlertState,
  shiftNotice?: ShiftNoticeState,
): ScreenContext {
  if (!root) throw new Error("app が無い");
  /*
   * ★この描画が**すでに古い**なら、画面には触らず捨て場へ描く（`latestRequest` の説明を参照）。
   *   呼び出し側は続けて `renderXxx(ctx, …)` を書くので、`null` を返して分岐を増やすのではなく
   *   **本物と同じ形の、どこにも繋がっていない入れ物**を返す。
   */
  if (latestRequest !== null && (latestRequest.date !== date || latestRequest.tab !== tab)) {
    const discarded = document.createElement("div");
    return { root: discarded, me, date, closing, locked: false };
  }
  root.replaceChildren();
  // ★前の画面のフォームは、この行で画面から外れる。見張りの登録も一緒に捨てる
  guardedForms.length = 0;

  const header = el("header");
  header.append(el("h1", { text: "おさ和風゛店舗アプリ" }));
  /*
   * ★**ログインID・ロール・ログアウトはアカウントへ畳む**
   *   （引き継ぎ §3.1「アカウントメニューへ」・§8.6／2026-09-20 依頼者判断「上部を詰める」）。
   * ★`<details>` は素の HTML。開閉を持つ仕組みを自前で書かない（最低賃金の畳みと同じ形）。
   * ★**利用者向けの呼び名は日本語**にするが、内部ロールは変えていない（`ROLE_LABEL`）。
   */
  const account = el("details", { className: "account" });
  /*
   * ★★**畳んでも「誰で入っているか」は残す。** 店舗端末は共有で、
   *   ロールだけだと staff が複数いる店では本人を特定できない
   *   （打刻・訂正は本人の記録になるので、取り違えたまま操作させない）。
   */
  const who = el("summary", { text: me.loginId });
  // ★ロールは狭い画面では隠す（店名が「…」で切れるため）。誰で入っているか＝ログインIDは常に出す
  who.append(el("span", { text: `（${ROLE_LABEL[me.role]}）`, className: "account-role" }));
  // ★頭文字の四角（UI-REFINE）。飾りなので読み上げない（名前は summary の文字で読まれる）
  const initial = el("span", { text: me.loginId.slice(0, 1).toUpperCase(), className: "account-initial" });
  initial.setAttribute("aria-hidden", "true");
  who.prepend(initial);
  account.append(who);
  const signOut = el("button", { text: "ログアウト", className: "link" });
  signOut.type = "button";
  // ★ログアウトも「画面を離れる」。打ちかけを黙って捨てない（code-reviewer の advisory・2026-09-20）
  signOut.addEventListener("click", () => {
    if (!confirmLeave()) return;
    void api.logout().then(() => renderLogin());
  });
  // ★打刻・シフトは kintai（別システム）で行う（`docs/19` D-1）。本番は本番の、デモはデモの kintai へ
  const kintaiUrl = kintaiUrlFor(location.hostname);
  /*
   * ★勤怠への移動とログアウトは**一番下にまとめる**（SETTINGS-NAV AC-2）。
   *   先頭に置いていたときは、枠つきのログアウトだけが目立ち、管理の画面は灰色の文字だった。
   *   束（管理など）は日付が決まった後に足すので、この足場は束の後で `account` へ入れる。
   */
  const accountFooter = el("div", { className: "account-footer" });
  if (kintaiUrl !== null) {
    const toKintai = el("a", { text: "勤怠（打刻・シフト）を開く", className: "link" });
    toKintai.href = kintaiUrl;
    accountFooter.append(toKintai);
  }
  accountFooter.append(signOut);
  header.append(account);
  root.append(header);

  /*
   * ★棚卸だけは**月**の作業なので、日付ではなく月を選ばせる。
   *   「営業日」ラベルのまま `2026-10-05` を選ばせて月を切り出すと、
   *   これが月の選択だと読み取れない。月末の作業を翌月にやる運用では
   *   既定値（前営業日）が翌月を指すので、なおさら気づけない。
   *
   * ★★**シフトも月の作業**（R0102 B-8）。ここに足すまで、シフトタブは
   *   `punchMonth()`＝**クライアント時計の今月**しか読まず、
   *   (1) 翌月を誰も見られない (2) 店長が来月ぶんを画面から公開できない
   *   （`publishControls` の `min`/`max` が読み込み中の月に固定される）
   *   の2つが同時に起きていた。★**専用の月移動 UI は足さない**——
   *   棚卸と同じこのピッカーが既に「月を選ぶ」入力で、押した先も同じ `load()`。
   */
  const monthly = isMonthlyTab(tab);
  const picker = el("label", { className: "field inline" });
  /*
   * ★設定タブは「営業日」ではない。**その日に有効な値を引くための基準日**。
   *   同じ入力欄でも意味が違うので、ラベルを流用しない。
   *   「営業日」のまま未来日を選ばせると、日次入力の日付を変えたと誤解される。
   */
  picker.append(
    el("span", { text: monthly ? "対象月" : tab === "settings" ? "基準日" : "営業日" }),
  );
  const dateInput = el("input");
  dateInput.type = monthly ? "month" : "date";
  dateInput.value = monthly ? date.slice(0, 7) : date;
  dateInput.addEventListener("change", () => {
    if (!confirmLeave()) {
      dateInput.value = monthly ? date.slice(0, 7) : date;
      return;
    }
    void load(me, monthly ? `${dateInput.value}-01` : dateInput.value, tab);
  });
  picker.append(dateInput);
  /*
   * ★**日付の行は1行にまとめる**（2026-09-20・依頼者判断「上部を詰める」）。
   *   ピッカーと前後移動を別の段にすると、375px で本文が 44px ぶん下がる。
   *   ★実測: 詰める前は最初の入力欄が上から 578px（画面の7割が操作の外）だった。
   */
  const dateRow = el("div", { className: "date-row" });
  dateRow.append(picker);
  root.append(dateRow);

  /*
   * ★**日次のタブだけ、営業日を前後に動かす近道を置く**（引き継ぎ §8.1）。
   *   ピッカーは既にあるが、閉店後に「前の営業日」を出すのに日付を打ち直させていた。
   * ★独自カレンダーは作らない。日付そのものの選択は上の native な入力のまま。
   * ★**月のタブ・設定タブには出さない**——あちらの値は「営業日」ではない
   *   （対象月／基準日。`date` の二役に3つ目を足さない）。
   */
  if (!monthly && tab !== "settings") {
    const moves = el("div", { className: "date-moves" });
    const go = (next: string): void => {
      if (next === date) return;
      if (!confirmLeave()) return;
      void load(me, next, tab);
    };
    /*
     * ★★**「前の営業日」は営業カレンダーに聞く。前日で代用しない**（§8.1 の3）。
     *   休業日を挟むと前日は営業日でなく、その日には売上も仕入も無い。
     *   ★カレンダーが未整備の月では**押せなくする**——前日を「前の営業日」と
     *   言い張ると、利用者は休業日に数字を入れたことに気づけない。
     */
    const previous = el("button", { text: "前の営業日" });
    previous.type = "button";
    previous.addEventListener("click", () => {
      previous.disabled = true;
      void findPreviousBusinessDay(me.storeId, date)
        .then((found) => {
          if (found === null) {
            root?.append(
              notice("この月の営業日カレンダーが未登録のため、前の営業日を出せません。日付を選んでください。", "info"),
            );
            return;
          }
          go(found);
        })
        .catch(() => {
          root?.append(notice("営業日カレンダーを取得できませんでした。日付を選んでください。", "info"));
        })
        .finally(() => {
          previous.disabled = false;
        });
    });
    // ★「今日」は**日本時間の暦日**（`todayJst`）。端末の時計の日付ではない
    const today = el("button", { text: "今日" });
    today.type = "button";
    today.addEventListener("click", () => {
      go(todayJst());
    });
    moves.append(previous, today);
    dateRow.append(moves);
  }

  const locked = closing === "CLOSED" || (closing === "SUBMITTED" && me.role !== "owner");
  /*
   * ★★**締め状態は「入力の邪魔になるとき」だけ画面上部に出す**
   *   （2026-09-20・依頼者判断「上部を詰める」）。
   *
   * ★**隠してよいのは `OPEN` だけ。** 引き継ぎ §3.1 は
   *   「登録を妨げる締め状態や必須警告は、画面圧縮のために隠さない」と書いている——
   *   ★`OPEN` は何も妨げないので、この禁止に当たらない。
   *   申請中・締め済み・差戻し・再開はそのまま出す（`locked` でなくても出す）。
   * ★月の状態そのものは「締め」タブが正面から扱う。ここは**日次入力の邪魔の予告**。
   */
  if (closing !== "OPEN") {
    root.append(
      el("p", {
        text: `この月の状態：${CLOSING_LABEL[closing]}`,
        className: locked ? "notice info" : "muted",
      }),
    );
  }

  /*
   * ★タブの出し分けは見た目の都合。権限マトリクス §3.1 の担保はサーバ側にある。
   *   ここで出してしまっても、叩けば 403 が返る（絶対ルール1）。
   */
  /*
   * ★現在地を読み上げにも伝える（2026-08-20）。
   *   2026-08-19 まで**色と太字だけ**で示しており、読み上げではどこにいるか分からなかった
   *   （WCAG SC 1.4.1「色だけで伝えない」）。
   *
   * ★**`role="tablist"` を名乗らない。** 一度は名乗ったが外した——
   *   ARIA のタブは `role="tabpanel"` ／ `aria-controls` ／ 矢印キーでの移動 ／
   *   非選択タブの `tabindex="-1"` まで揃って初めて期待どおりに動く。
   *   この画面は押すたびに**全体を描き直す**（タブパネルという入れ物が存在しない）ので、
   *   名乗ると「矢印キーで動くはず」という期待だけを作って裏切る。
   *   実態は**画面の切り替えナビゲーション**なので `aria-current="page"` で表す
   *   （2026-08-20・code-reviewer の指摘）。
   *
   * ★見た目の現在地も同じ属性から引く（CSS 側）。クラスと属性で二重に持たない。
   */
  // ★`main-tabs`：900px 未満では画面下に固定する（依頼者判断 2026-09-21・`docs/10` §5）
  const tabs = el("nav", { className: "tabs main-tabs" });
  tabs.setAttribute("aria-label", "画面の切り替え");
  /*
   * ★★**シフトタブは、気づきが指す月へ着地させる**（R0102 B-8 / AC-3・AC-4 の決定）。
   *
   *   サーバは `GET /api/shifts` を返した時点で、**開いた月を**既読にする（0058）。
   *   ★着地先が今月に固定されていたときは、**帯が「2026-10」と言っているのに
   *   9月の画面が開き、その一回で印だけ消えていた**（B-8 の症状3／security の PROBE2）。
   *
   * ★**穴は二重に塞いである。** 既読が月ごとになった（0058）ので別の月を開いても
   *   印は消えないが、それでも**帯が言った月へ降りる**——
   *   ★でないと、帯を見た人は「開いたのに何も無い」画面に着き、
   *   自分で月を戻す必要があると気づけない（AC-3）。
   */
  const noticeMonth =
    shiftNotice !== undefined && shiftNotice !== "unavailable"
      ? (shiftNotice.notice?.month ?? null)
      : null;
  /**
   * 押した先のタブへ渡す日付。
   *
   * ★★**月のタブから日次のタブへ、`YYYY-MM-01` を持ち出さない**
   *   （2026-09-03・spec の差し戻し。R0102 B-8 周回2）。
   *
   *   `date` は**1本しかないのに2つの意味を担っている**——日次入力の「営業日」と、
   *   月のタブの「対象月」。月のタブは選んだ月を `${値}-01` の形で `date` へ押し込むので、
   *   ★**そのままタブを移ると、日次入力の営業日が黙って「その月の1日」になる。**
   *
   *   ★**実測で壊れた**：シフトタブで10月を見た `staff` がロスタブへ移って ¥500 を記録すると、
   *   **今日（9/3）より先の 2026-10-01 に着地した**（spec が実機で再現）。
   *   ★絶対ルール5 が名指しで避けている「静かなデータの取り違え」そのもので、
   *   旧Excelの13年ぶんの損傷が発覚しなかった根因と同じ形。
   *
   * ★**棚卸タブも同じ穴を持っていた**（`monthly` だったのは元々棚卸だけ）。
   *   ★**タブの切替はここ1箇所に集まっている**ので門も1つで足りる——
   *   `t.key === "shift"` で特別扱いすると、棚卸だけ壊れたまま残る。
   *
   * ★**天井**：日次のタブへ戻ると営業日は**アプリの初期値（前営業日）に戻り、
   *   直前に見ていた日は覚えていない。** 覚えるにはモジュールの状態が要り、
   *   それはログアウトをまたいで残る（`shownFullWageBanner` と同じ形の罠）。
   *   ★**日付欄は画面に出ているので戻ったことは見える。** 黙って未来へ飛ぶほうが害が大きい。
   */
  const tabDate = (to: Tab): string => {
    // ★気づきが指す月へ着地させる（AC-3）。これは月のタブ側の話なので先に見る
    if (to === "shift" && noticeMonth !== null) return `${noticeMonth}-01`;
    if (monthly && !isMonthlyTab(to)) return yesterday();
    return date;
  };
  const available = tabsFor(me.role, me.attendanceInKintai === true);
  const tabButton = (t: (typeof TABS)[number]): HTMLButtonElement => {
    const b = el("button", { text: t.label, className: "tab" });
    b.type = "button";
    if (t.key === tab) b.setAttribute("aria-current", "page");
    b.addEventListener("click", () => {
      if (!confirmLeave()) return;
      void load(me, tabDate(t.key), t.key);
    });
    return b;
  };

  /*
   * ★★**主ナビは束（`GROUPS`）、副ナビはその中身**（`docs/10` §5・依頼者判断 2026-09-20）。
   *   13本を一度に並べていたときは 375px で `nav.tabs` が3段 149px を占めていた。
   *
   * ★**いま開いている束だけを2段目に開く。** 開閉の状態は持たない——
   *   畳んだ管理系タブと同じ考え方で、**現在地から決まる**（描き直しても迷子にならない）。
   * ★束が1本しか持たないときは2段目を出さない（同じ語が縦に2つ並ぶだけになる）。
   */
  const inGroup = (g: TabGroup): (typeof TABS)[number][] => available.filter((t) => t.group === g);
  /*
   * ★**アカウントメニューへ入れるか。** 例外はここ1箇所（`GROUPS` の説明を参照）——
   *   `staff` の主作業は打刻なので、「シフト・出退勤」だけ主ナビに残す。
   */
  const inAccount = (g: (typeof GROUPS)[number]): boolean =>
    g.account === true && !(g.key === "attendance" && me.role === "staff");
  const currentGroup = available.find((t) => t.key === tab)?.group ?? null;

  for (const g of GROUPS) {
    if (inAccount(g)) continue;
    const items = inGroup(g.key);
    if (items.length === 0) continue;
    const b = el("button", { text: g.label, className: "tab" });
    b.type = "button";
    b.prepend(navIcon(g.key));
    if (g.key === currentGroup) b.setAttribute("aria-current", "page");
    b.addEventListener("click", () => {
      if (!confirmLeave()) return;
      const first = items[0]!;
      void load(me, tabDate(first.key), first.key);
    });
    tabs.append(b);
  }
  root.append(tabs);

  /*
   * ★アカウントメニューの中の束（「シフト・出退勤」「管理」）。
   *   ★**中身の名前を文字で出す**（畳んだ管理系タブのときの決まりをそのまま引き継ぐ）。
   *   ★`account` は上で組み立ててヘッダへ入れてあるが、押した先の日付（`tabDate`）が
   *     ここまで来ないと決まらないので、中身だけ後から足す。
   */
  for (const g of GROUPS) {
    if (!inAccount(g)) continue;
    const items = inGroup(g.key);
    if (items.length === 0) continue;
    const box = el("div", { className: "account-group" });
    box.append(el("p", { text: g.label, className: "muted" }));
    box.append(...items.map(tabButton));
    account.append(box);
  }
  account.append(accountFooter);
  // ★管理系の画面では主ナビに現在地が無いので、入口のほうに印を付ける（AC-3）
  if (GROUPS.some((g) => g.key === currentGroup && inAccount(g))) account.classList.add("here");

  if (currentGroup !== null) {
    const siblings = inGroup(currentGroup);
    if (siblings.length > 1) {
      const sub = el("nav", { className: "tabs subtabs" });
      sub.setAttribute(
        "aria-label",
        `${GROUPS.find((g) => g.key === currentGroup)?.label ?? ""}の中の切り替え`,
      );
      sub.append(...siblings.map(tabButton));
      root.append(sub);
    }
  }

  /*
   * ★M5b：全タブ共通の警告。タブより下＝どのタブの中身よりも先に読まれる位置。
   *
   * ★M5e：ここに `if` を書かない。`undefined`（出さない相手）と `"unavailable"`
   *   （取れなかった）の取り違えが M5b の差し戻しそのもので、
   *   その分岐は `wageAlertBlocks` 側で検査されている。
   */
  const blocks = wageAlertBlocks(wageAlert, { shownFull: shownFullWageBanner, tab });
  root.append(...minimumWageBanner(me, blocks));
  if (showedFullWageBanner(blocks)) shownFullWageBanner = true;

  /*
   * ★シフトの変更・公開への気づき（R0102 B-7 / 依頼者判断 Q-S6）。
   *   最低賃金の警告と**同じ位置**——タブより下＝どのタブの中身よりも先に読まれる。
   *
   * ★**どの月かを出す。** 帯だけでは「いつの話か」が分からない。
   * ★★**この月は、上のタブの着地先と同じもの**（R0102 B-8）——
   *   帯が「2026-10」と書いたら、シフトタブを押すと 2026-10 が開く。
   *   ★B-8 まで、帯は月を書けても**開く先は今月に固定**で、
   *   その一回で印だけが消えていた（症状3）。
   *
   * ★**取れなかったことは黙らない**（M5b の差し戻しと同じ形。`undefined` と
   *   `"unavailable"` を取り違えない）。
   */
  if (shiftNotice === "unavailable") {
    root.append(
      notice("シフトの変更をお知らせできませんでした。シフトの画面で確認してください。", "info"),
    );
  } else if (shiftNotice?.notice !== undefined && shiftNotice.notice !== null) {
    /*
     * ★**取消は帯に書く**（2026-09-03・周回3 / spec の差し戻し2）。
     *   一覧は `ACTIVE` しか出さないので、取り消された予定は開いても画面に現れない——
     *   ★帯に書かないと、本人には「開いたが何も無かった」としか見えない。
     *   ★何から何へ変わったかは書かない（`docs/05` §3.7 で対象外）。**消えた事実の存在だけ。**
     */
    root.append(
      notice(
        `シフトに変更があります（${shiftNotice.notice.month}）。` +
          (shiftNotice.notice.canceled ? "取り消された予定があります。" : "") +
          "「シフト」で確認してください。",
        "info",
      ),
    );
    /*
     * ★★**帯から直接シフトへ行けるようにする**（2026-09-20・ナビの3グループ化）。
     *   それまで帯は「「シフト」タブで確認してください」とだけ書いており、
     *   ★タブが主ナビに常時出ていることに寄りかかっていた——
     *   束にまとめた後、`manager` / `owner` ではシフトは**アカウントメニューの中**にあり、
     *   帯を見た人は指す先を自力で探すことになる。
     * ★最低賃金の帯の「判定を開く」と同じ形。日付は `tabDate("shift")`＝帯が言った月。
     */
    const openShift = el("button", { text: "シフトを開く" });
    openShift.type = "button";
    openShift.addEventListener("click", () => {
      if (!confirmLeave()) return;
      void load(me, tabDate("shift"), "shift");
    });
    root.append(openShift);
  }

  return { root, me, date, closing, locked };
}

/**
 * いま画面が求めている「どの日の・どのタブ」か。★`load()` が入口で書き換える。
 *
 * ★★**遅れて返ってきた古い応答に、新しい日付の画面を上書きさせない**
 *   （引き継ぎ §8.1 の5「遅い取得応答の順序逆転」）。
 *   日付を変えた直後は**2本の `load()` が同時に走る**——先に投げたほうが遅れて返ると、
 *   利用者が見ている新しい日の画面が、古い日のデータで丸ごと描き直される。
 *   ★描き直したあとの画面には**新しい日付がラベルとして出ている**ので、
 *   中身が別の日だと気づけない。
 *
 * ★**判定を `renderShell` の1箇所に置く**のは、そこが全タブの必ず通る唯一の場所だから。
 *   タブごとの分岐に書くと、足し忘れたタブだけが黙って古い値を映す。
 */
let latestRequest: { date: string; tab: Tab } | null = null;

/**
 * 次の描画で目印を付ける登録先の行（引き継ぎ §8.4）。★1回だけ効いて消える。
 *
 * ★**一覧の先頭を開くだけで済ませない。** 証憑から移った人は「さっき登録した行」を
 *   直したいのであって、その日の一覧を見たいのではない。
 * ★色だけで示さない（枠と文字を添える。docs/10・WCAG SC 1.4.1）。
 */
let highlightRecord: { target: string; id: string } | null = null;

/**
 * 登録先の一覧の行に目印を付け、画面内へ送る。
 *
 * ★`li` を組み立てる場所ごとに書かず、**一覧を作った直後に1回**呼ぶ。
 */
function markHighlighted(list: HTMLElement, target: string): void {
  if (highlightRecord === null || highlightRecord.target !== target) return;
  const found = list.querySelector<HTMLElement>(`[data-record-id="${CSS.escape(highlightRecord.id)}"]`);
  highlightRecord = null;
  if (found === null) return;
  found.classList.add("highlighted");
  found.append(el("small", { text: "この伝票から登録した行です", className: "muted" }));
  // ★`?.` は jsdom 対策ではなく**この関数の役目の切り分け**——目印を付けることが本体で、
  //   画面を送るのは補助。環境が持っていなければ目印だけ残す（検査の環境は持っていない）
  found.scrollIntoView?.({ block: "center" });
}

/**
 * `date` より前で、いちばん近い**営業日**（`YYYY-MM-DD`）。
 *
 * ★カレンダーに登録が無ければ `null`。★**前日で代用しない**（引き継ぎ §8.1 の3）。
 *   休業日を挟むと前日は営業日ではなく、そこに数字は無い。
 * ★またぐのは**前の月まで**。それ以上さかのぼるなら日付を選んでもらう
 *   （何か月も前へ勝手に飛ぶと、どこへ動いたのか分からなくなる）。
 */
export async function findPreviousBusinessDay(
  storeId: string,
  date: string,
  getCalendar: (storeId: string, month: string) => Promise<{ calendar: CalendarDraft }> = api.getCalendar,
): Promise<string | null> {
  const thisMonth = date.slice(0, 7);
  const prevMonth = previousDay(`${thisMonth}-01`).slice(0, 7);
  for (const month of [thisMonth, prevMonth]) {
    const { calendar } = await getCalendar(storeId, month);
    const open = calendar.days
      .filter((day) => day.registered && day.isOpen && day.businessDate < date)
      .map((day) => day.businessDate)
      .sort();
    const latest = open[open.length - 1];
    if (latest !== undefined) return latest;
  }
  return null;
}

function renderSalesTab(
  ctx: ScreenContext,
  sales: DailySales | null,
  /** ★読み込みに失敗した経路では `null`。そのとき `maxlength` は付かない（サーバは断り続ける） */
  ceilings: InputCeilings | null,
  message?: Notice,
  /** 記録の無い営業日（§2.1）。`null`＝カレンダー未整備、`undefined`＝読み込めていない（何も出さない） */
  missingDates?: string[] | null,
): void {
  const { me, date, locked } = ctx;
  const section = el("section");
  section.append(el("h2", { text: "売上" })); // 他のタブと同じく h2 を持つ（docs/10 §5・見出しの抜け）
  if (message !== undefined) section.append(noticeOf(message));
  // ★0円で記録済みの日と見分ける（§2.1・案C）。読み込みに失敗したときは言わない（記録が無いとは限らない）
  if (sales === null && missingDates !== undefined) {
    section.append(el("p", { className: "muted", text: "この日の売上は、まだ入力されていません" }));
  }

  const form = el("form");
  const yenCap = (label: string, name: string, value: string): HTMLLabelElement =>
    capped(field(label, name, value), ceilings?.amount);
  form.append(yenCap("売上（税込）", "salesInclTaxDinner", sales?.salesInclTaxDinner ?? ""));
  form.append(capped(field("客数", "guestsDinner", String(sales?.guestsDinner ?? "")), ceilings?.guests));
  form.append(
    capped(field("組数", "groupsDinner", String(sales?.groupsDinner ?? "")), ceilings?.groups),
  );
  form.append(yenCap("現金", "cashAmount", sales?.cashAmount ?? ""));
  form.append(yenCap("カード", "cardAmount", sales?.cardAmount ?? ""));
  form.append(yenCap("その他", "otherAmount", sales?.otherAmount ?? ""));

  const submit = el("button", { text: locked ? "変更できません" : "保存する" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  onSubmit(
    form,
    submit,
    (data) =>
      api.putSales(
        me.storeId,
        date,
        Object.fromEntries(
          [
            "salesInclTaxDinner",
            "guestsDinner",
            "groupsDinner",
            "cashAmount",
            "cardAmount",
            "otherAmount",
          ].map((k) => [k, Number(data.get(k) ?? 0)]),
        ),
      ),
    (m) => void load(me, date, "sales", m),
    "保存しました",
  );
  section.append(form);

  // 記録の無い営業日（§2.1）。日付を押すとその日へ移る。★未整備は 0件と区別して言う
  if (missingDates === null) {
    section.append(
      el("p", { className: "muted", text: "営業日カレンダーが揃っていないため、未入力の日を一覧にできません" }),
    );
  } else if (missingDates !== undefined && missingDates.length > 0) {
    section.append(el("h3", { text: `売上が未入力の営業日（${String(missingDates.length)}日）` }));
    const list = el("ul", { className: "missing-dates" });
    for (const d of missingDates) {
      const item = el("li");
      const jump = el("button", { text: d });
      jump.type = "button";
      jump.addEventListener("click", () => void load(me, d, "sales"));
      item.append(jump);
      list.append(item);
    }
    section.append(list);
  }

  if (sales !== null) {
    const dl = el("dl", { className: "status" });
    for (const [label, value] of [
      ["売上（税込）", yen(sales.salesInclTaxDinner)],
      ["客数", `${sales.guestsDinner}人`],
      [
        "客単価（税込）",
        sales.guestsDinner > 0
          ? yen(String(Math.round(Number(sales.salesInclTaxDinner) / sales.guestsDinner)))
          : "—",
      ],
      // ★税抜はここに出さない。計算は `findSalesSummary` の1経路で、集計タブが表示する
    ] as const) {
      dl.append(el("dt", { text: label }), el("dd", { text: value }));
    }
    section.append(dl);
  }
  ctx.root.append(section);
}

// ---------------------------------------------------------------------
// 仕入
// ---------------------------------------------------------------------

const CATEGORY_LABEL = { FOOD: "フード", DRINK: "ドリンク", OTHER: "その他" } as const;

/**
 * 選択欄。★**ラベルを必ず受け取る。**
 *
 * 2026-08-20 まで `<label class="field">` の中に `<span>` を入れておらず、
 * **12個の選択欄がすべて無名だった**（`field()` は入れている）。
 * 業者・科目・入出金・支払方法・ロス種別・雇用形態——**どれも取り違えが
 * そのまま数字の誤りになる欄**で、とくに現金／クレジットの取り違えは D-13 の要。
 * ★引数を省略できる形にしない。省略できると、次に足す人がまた無名で作る。
 */
/**
 * ★`selected` を渡すと、いま選んでいる値を復元する。
 *
 *   渡さないと**絞り込んだ直後に選択が「すべて」へ戻って見える**（状態は効いているのに
 *   画面が違うことを言う）。入力フォームの `select` は新規入力用なので渡さないままでよい。
 */
export function select(
  label: string,
  name: string,
  options: { value: string; label: string }[],
  selected?: string,
): HTMLLabelElement {
  const wrap = el("label", { className: "field" });
  wrap.append(el("span", { text: label }));
  const node = document.createElement("select");
  node.name = name;
  for (const o of options) {
    const opt = document.createElement("option");
    opt.value = o.value;
    opt.textContent = o.label;
    node.append(opt);
  }
  if (selected !== undefined) node.value = selected;
  wrap.append(node);
  return wrap;
}

/**
 * マスタ（業者・小口現金の科目）を足す入口。★**1件目を入れた後も足せること**（MASTER-ADD-AFTER-FIRST）——
 *   登録フォームが「0件のとき」しか出ず、業者は約20件要るのに2件目から画面で足せなかった。
 *   毎日の入力の邪魔をしないよう、既に1件以上あるときは畳んでタブの末尾に置く（素の `<details>`）。
 */
function masterAddFold(summary: string, form: HTMLFormElement): HTMLElement {
  const fold = el("details", { className: "master-add" });
  fold.append(el("summary", { text: summary }), form);
  return fold;
}

function vendorForm(ctx: ScreenContext): HTMLFormElement {
  const { me, date } = ctx;
  const vf = el("form");
  vf.append(field("業者コード", "vendorCode", "", "text"));
  vf.append(field("業者名", "name", "", "text"));
  vf.append(select("区分", "category", optionsOf(CATEGORY_LABEL)));
  const add = el("button", { text: "業者を登録" });
  add.type = "submit";
  vf.append(add);
  onSubmit(
    vf,
    add,
    (d) =>
      api.addVendor(me.storeId, {
        vendorCode: String(d.get("vendorCode") ?? ""),
        name: String(d.get("name") ?? ""),
        category: String(d.get("category") ?? "FOOD"),
      }),
    (m) => void load(me, date, "purchases", m),
    "業者を登録しました",
  );
  return vf;
}

function pettyAccountForm(ctx: ScreenContext): HTMLFormElement {
  const { me, date } = ctx;
  const af = el("form");
  af.append(field("科目コード", "accountCode", "", "text"));
  af.append(field("科目名", "name", "", "text"));
  af.append(
    select("科目の種類", "accountType", [
      { value: "OTHER", label: "その他" },
      { value: "PURCHASE", label: "仕入" },
      { value: "SUPPLIES", label: "消耗品費" },
      { value: "MISC", label: "雑費" },
      { value: "TRANSPORT", label: "交通費" },
    ]),
  );
  const add = el("button", { text: "科目を登録" });
  add.type = "submit";
  af.append(add);
  onSubmit(
    af,
    add,
    (d) =>
      api.addPettyCashAccount(me.storeId, {
        accountCode: String(d.get("accountCode") ?? ""),
        name: String(d.get("name") ?? ""),
        accountType: String(d.get("accountType") ?? "OTHER"),
      }),
    (m) => void load(me, date, "petty", m),
    "科目を登録しました",
  );
  return af;
}

function renderPurchases(
  ctx: ScreenContext,
  purchases: Purchase[],
  vendors: Vendor[],
  /** ★読み込みに失敗した経路では `undefined`。そのとき `maxlength` は付かない（サーバは断り続ける） */
  ceilings: PurchaseCeilings | undefined,
  message?: Notice,
): void {
  const { me, date, locked } = ctx;
  const section = el("section");
  section.append(el("h2", { text: "仕入" })); // 他のタブと同じく h2 を持つ（docs/10 §5・見出しの抜け）
  if (message !== undefined) section.append(noticeOf(message));

  const canAddVendor = me.role === "manager" || me.role === "owner";
  if (vendors.length === 0) {
    section.append(
      notice(
        canAddVendor
          ? "仕入を入れる前に、業者を登録してください。"
          : "業者がまだ登録されていません。店長にご連絡ください。",
        "info",
      ),
    );
    if (canAddVendor) section.append(vendorForm(ctx));
    ctx.root.append(section);
    return;
  }

  const form = el("form");
  form.append(
    select(
      "仕入業者",
      "vendorId",
      vendors.map((v) => ({ value: v.id, label: `${v.name}（${CATEGORY_LABEL[v.category]}）` })),
    ),
  );
  form.append(select("区分", "category", optionsOf(CATEGORY_LABEL)));
  form.append(capped(field("金額", "amount", "0"), ceilings?.amount));
  const submit = el("button", { text: locked ? "変更できません" : "追加する" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  onSubmit(
    form,
    submit,
    (d) =>
      api.addPurchase(me.storeId, date, {
        vendorId: String(d.get("vendorId") ?? ""),
        category: String(d.get("category") ?? "FOOD"),
        amount: Number(d.get("amount") ?? 0),
      }),
    (m) => void load(me, date, "purchases", m),
    "追加しました",
  );
  section.append(form);

  if (purchases.length > 0) {
    const list = el("ul", { className: "list" });
    for (const p of purchases) {
      const li = el("li");
      li.dataset.recordId = p.id;
      li.append(
        el("span", { text: p.vendorName }),
        el("span", { text: yen(p.amount), className: "amount" }),
        // ★承認するまで原価に入らない。状態を隠さない
        el("small", {
          text: p.isConfirmed ? "承認済み" : "未承認（原価に入りません）",
          className: p.isConfirmed ? "muted" : "warn",
        }),
      );
      if (!p.isConfirmed && (me.role === "manager" || me.role === "owner") && !locked) {
        const ok = el("button", { text: "承認", className: "link" });
        ok.type = "button";
        ok.addEventListener("click", () => {
          void api
            .confirmPurchase(me.storeId, p.id)
            .then(() => void load(me, date, "purchases", infoNotice("承認しました")))
            .catch((err: unknown) =>
              void load(me, date, "purchases", errorNotice(err instanceof ApiError ? err.message : "承認できませんでした")),
            );
        });
        li.append(ok);
      }
      list.append(li);
    }
    section.append(list);
    markHighlighted(list, "purchases");

    const { confirmed, pending } = purchaseTotals(purchases);
    const dl = el("dl", { className: "status" });
    dl.append(el("dt", { text: "承認済みの合計" }), el("dd", { text: yen(String(confirmed)) }));
    if (pending > 0) {
      dl.append(el("dt", { text: "未承認（原価に入りません）" }), el("dd", { text: yen(String(pending)) }));
    }
    section.append(dl);
  }
  if (canAddVendor) section.append(masterAddFold("業者を追加する", vendorForm(ctx)));
  ctx.root.append(section);
}

// ---------------------------------------------------------------------
// 小口現金（★D-13 の再発防止：現金とクレジットを分ける）
// ---------------------------------------------------------------------

const METHOD_LABEL = { CASH: "現金", CREDIT: "クレジット", OTHER: "その他" } as const;

function renderPettyCash(
  ctx: ScreenContext,
  data: {
    entries: PettyCashEntry[];
    accounts: PettyCashAccount[];
    balance: CashBalance;
    cashCount: CashCount | null;
    ceilings?: PettyCashCeilings;
  },
  message?: Notice,
): void {
  const { me, date, locked } = ctx;
  const section = el("section");
  if (message !== undefined) section.append(noticeOf(message));

  /* ★科目を書けるのは owner だけ（`docs/05` §3.3）。manager に出すと、押すたびに必ず 403 になる */
  const canAddAccount = me.role === "owner";

  if (data.accounts.length === 0) {
    section.append(
      notice(
        canAddAccount
          ? "小口現金を入れる前に、科目を登録してください。数え始めの現金は「pc_opening」で記録します。"
          : "科目がまだ登録されていません。オーナーにご連絡ください。",
        "info",
      ),
    );
    if (canAddAccount) section.append(pettyAccountForm(ctx));
    ctx.root.append(section);
    return;
  }

  /*
   * ★繰越が未記録なら、残高を「手元現金」として見せない。
   *   数字だけ出すと、正しい残高だと読まれる。
   */
  if (!data.balance.hasOpening) {
    section.append(
      notice(
        "数え始めの現金（繰越）がまだ記録されていません。" +
          "いまの残高は「記録を始めてからの増減」であって、手元の現金ではありません。",
        "info",
      ),
    );
  }

  const form = el("form");
  form.append(
    select(
      "科目",
      "accountId",
      data.accounts.map((a) => ({ value: a.id, label: a.name })),
    ),
  );
  const directionField = select("入金・出金", "direction", [
    { value: "OUT", label: "出金" },
    { value: "IN", label: "入金" },
  ]);
  form.append(directionField);
  // ★ここが D-13 の要。現金とクレジットを必ず分けて記録させる
  form.append(select("支払方法", "paymentMethod", optionsOf(METHOD_LABEL)));
  /*
   * ★★**入金と出金で上限が違う**（0069）。欄は1つなので、区分を変えたら桁数も張り替える。
   *   ★出金 50,000（5桁）に合わせたまま入金 300,000 を打てないのは誤りで、
   *   入金 300,000（6桁）に合わせたままだと**出金の桁止めが画面から消える。**
   *   ★これは入力を助けるだけの飾りで、拒否そのものはサーバが行う。
   */
  const amountField = field("金額", "amount", "0");
  const applyCeiling = (): void => {
    const direction = directionField.querySelector("select")?.value;
    capped(amountField, direction === "IN" ? data.ceilings?.inAmount : data.ceilings?.amount);
  };
  applyCeiling();
  directionField.addEventListener("change", applyCeiling);
  form.append(amountField);
  form.append(field("内容（任意）", "description", "", "text"));
  const submit = el("button", { text: locked ? "変更できません" : "追加する" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  onSubmit(
    form,
    submit,
    (d) =>
      api.addPettyCash(me.storeId, date, {
        accountId: String(d.get("accountId") ?? ""),
        direction: String(d.get("direction") ?? "OUT"),
        paymentMethod: String(d.get("paymentMethod") ?? "CASH"),
        amount: Number(d.get("amount") ?? 0),
        description: String(d.get("description") ?? "") || null,
      }),
    (m) => void load(me, date, "petty", m),
    "追加しました",
  );
  section.append(form);

  if (data.entries.length > 0) {
    const list = el("ul", { className: "list" });
    for (const e of data.entries) {
      const li = el("li");
      li.dataset.recordId = e.id;
      li.append(el("span", { text: `${e.direction === "IN" ? "入" : "出"} ${e.accountName}` }));
      li.append(el("span", { text: yen(e.amount), className: "amount" }));
      li.append(
        el("small", {
          text: METHOD_LABEL[e.paymentMethod],
          // ★クレジットは現金残高に影響しない。見た目でも区別する
          className: e.paymentMethod === "CASH" ? "muted" : "warn",
        }),
      );
      list.append(li);
    }
    section.append(list);
    markHighlighted(list, "petty_cash_entries");
  }

  const dl = el("dl", { className: "status" });
  dl.append(
    el("dt", { text: data.balance.hasOpening ? "現金残高（帳簿）" : "記録開始からの増減" }),
    el("dd", { text: yen(String(data.balance.cashBalance)) }),
  );
  if (data.balance.creditTotal > 0) {
    // ★現行Excelはここを現金出金に混ぜて約30万円ずれていた（D-13）
    dl.append(
      el("dt", { text: "クレジット払い（現金残高に含めない）" }),
      el("dd", { text: yen(String(data.balance.creditTotal)) }),
    );
  }
  section.append(dl);

  // ---- レジ金の実査（C-07） ----
  const countForm = el("form");
  countForm.append(el("h2", { text: "レジ金の実査" }));
  countForm.append(
    el("p", {
      text: "数えた金額を記録します。帳簿の残高は明細から自動で計算するので、入力は不要です。",
      className: "muted",
    }),
  );
  countForm.append(
    capped(
      field("実査額", "countedAmount", data.cashCount?.countedAmount ?? "0"),
      data.ceilings?.countedAmount,
    ),
  );
  countForm.append(field("差額の理由（任意）", "reason", data.cashCount?.reason ?? "", "text"));
  const countSubmit = el("button", { text: locked ? "変更できません" : "実査を記録" });
  countSubmit.type = "submit";
  countSubmit.disabled = locked;
  countForm.append(countSubmit);

  onSubmit(
    countForm,
    countSubmit,
    (d) =>
      api.recordCashCount(me.storeId, date, {
        countedAmount: Number(d.get("countedAmount") ?? 0),
        reason: String(d.get("reason") ?? "") || null,
      }),
    (m) => void load(me, date, "petty", m),
    "実査を記録しました",
  );
  section.append(countForm);

  if (data.cashCount !== null) {
    const cdl = el("dl", { className: "status" });
    const diff = Number(data.cashCount.difference);
    cdl.append(el("dt", { text: "実査額" }), el("dd", { text: yen(data.cashCount.countedAmount) }));
    cdl.append(el("dt", { text: "帳簿残高" }), el("dd", { text: yen(data.cashCount.bookAmount) }));
    cdl.append(
      el("dt", { text: "差額" }),
      el("dd", { text: diff === 0 ? "一致" : yen(String(diff)) }),
    );
    section.append(cdl);
  }

  if (canAddAccount) section.append(masterAddFold("科目を追加する", pettyAccountForm(ctx)));
  ctx.root.append(section);
}

// ---------------------------------------------------------------------
// ロス記録（★staff も書ける唯一のもの）
// ---------------------------------------------------------------------

const LOSS_LABEL = { WASTE: "廃棄", STAFF_MEAL: "まかない", SERVICE: "サービス" } as const;

function renderLoss(ctx: ScreenContext, records: LossRecord[] | null, message?: Notice): void {
  const { me, date, locked } = ctx;
  const section = el("section");
  section.append(el("h2", { text: "ロス" })); // 他のタブと同じく h2 を持つ（docs/10 §5・見出しの抜け）
  if (message !== undefined) section.append(noticeOf(message));

  const form = el("form");
  form.append(select("ロスの種別", "lossType", optionsOf(LOSS_LABEL)));
  form.append(field("金額", "amount", "0"));
  form.append(field("内容（任意）", "note", "", "text"));
  const submit = el("button", { text: locked ? "変更できません" : "記録する" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  onSubmit(
    form,
    submit,
    (d) =>
      api.addLoss(me.storeId, date, {
        lossType: String(d.get("lossType") ?? "WASTE"),
        amount: Number(d.get("amount") ?? 0),
        note: String(d.get("note") ?? "") || null,
      }),
    (m) => void load(me, date, "loss", m),
    "記録しました",
  );
  section.append(form);

  if (records === null) {
    // ★staff は書けるが読めない（権限マトリクス §3.1 の注記）。隠すのではなく理由を出す
    section.append(
      notice("記録した内容の一覧は、社員以上の方が確認します。入力だけお願いします。", "info"),
    );
  } else if (records.length > 0) {
    const list = el("ul", { className: "list" });
    for (const r of records) {
      const li = el("li");
      li.append(el("span", { text: LOSS_LABEL[r.lossType] }));
      li.append(el("span", { text: yen(r.amount), className: "amount" }));
      if (r.note !== null && r.note !== "") li.append(el("small", { text: r.note, className: "muted" }));
      list.append(li);
    }
    section.append(list);

    // ★まかないは原価から除外しない。参考値として出すだけ（絶対ルール10）
    const meal = records.filter((r) => r.lossType === "STAFF_MEAL").reduce((a, r) => a + Number(r.amount), 0);
    const dl = el("dl", { className: "status" });
    dl.append(
      el("dt", { text: "ロス合計" }),
      el("dd", { text: yen(String(records.reduce((a, r) => a + Number(r.amount), 0))) }),
    );
    if (meal > 0) {
      dl.append(el("dt", { text: "うちまかない（参考値）" }), el("dd", { text: yen(String(meal)) }));
    }
    section.append(dl);
  }
  ctx.root.append(section);
}

// ---------------------------------------------------------------------
// 打刻（§3.6：全ロール W(個)。R0102 A-4）
// ---------------------------------------------------------------------

/**
 * 出勤・休憩・退勤のボタンと、自分の直近の記録・未退勤検出・訂正を出す（A-4 AC-1〜3・A-5）。
 *
 * ★次に押せるボタンは `nextPunchOptions`（`src/ui/punch-view.ts`）が
 *   `events` の `businessDate`（サーバが焼き付けた値）だけから決める。
 *   クライアント時計を読まない（`.loop/issues/R0102-A4.md` 実装方針）。
 * ★氏名は扱わない（絶対ルール1）。`events` は `scope=own` でサーバが自分の分だけに絞る
 *   （manager / owner も同じ。全員分から自分のボタン状態を計算しない——A-5 AC-8）。
 * ★`staff` は manager / owner のときだけ渡る（従業員選択の訂正ビュー・A-5 AC-2）。
 *   渡さない出し分けは見た目の都合で、担保はサーバの 403。
 */
export function renderPunch(
  ctx: ScreenContext,
  log: PunchLogResult,
  anomalies: AttendanceAnomaly[],
  staff: Staff[] | null,
  message?: Notice,
): void {
  const { me } = ctx;
  const events = log.events;
  const section = el("section");
  section.append(el("h2", { text: "打刻" }));
  if (message !== undefined) section.append(noticeOf(message));

  const state = nextPunchOptions(events);
  const buttons = el("div", { className: "punch-buttons" });
  for (const type of PUNCH_TYPES) {
    const b = el("button", { text: PUNCH_LABEL[type] });
    b.type = "button";
    b.disabled = !state.enabled.has(type);
    b.addEventListener("click", () => {
      b.disabled = true;
      // ★サーバ拒否時は理由をそのまま表示する（AC-2）。担保はサーバ側（絶対ルール1と同じ考え方）
      void api
        .recordPunch(me.storeId, type)
        .then(() => load(me, ctx.date, "punch", infoNotice(`${PUNCH_LABEL[type]}を記録しました`)))
        .catch((e: unknown) => {
          void load(me, ctx.date, "punch", errorNotice(e instanceof ApiError ? e.message : "記録できませんでした"));
        });
    });
    buttons.append(b);
  }
  section.append(buttons);

  /*
   * ★docs/02 §4.3「検出して出す」の本人向け宛先（権限マトリクス §3.6・AC-11）。
   *   A-3 では manager/owner の「従業員」タブにしか導線が無く、本人には0本だった
   *   （`.loop/rounds/R0102-A3.md` B6）。ここがその宛先。判断は `attendance-anomaly-view.ts`。
   */
  const anomalyLines = attendanceAnomalyLines(anomalies);
  if (anomalyLines.length > 0) {
    const box = notice(attendanceAnomalyHeading(anomalies), "error");
    const list = el("ul", { className: "list" });
    for (const line of anomalyLines) list.append(el("li", { text: line }));
    box.append(list);
    section.append(box);
  }

  // ★自分の直近の勤務の記録（AC-3）と、その訂正（A-5 AC-1。理由必須はサーバが最後の砦）
  section.append(el("h3", { text: "直近の記録" }));
  const ownDone = (msg?: Notice): void => {
    void load(me, ctx.date, "punch", msg);
  };
  section.append(...punchLogBlocks(me, log.events, log.corrections, ownDone));

  /*
   * ★従業員の打刻の訂正（§3.6「他人の分」= manager 以上・A-5 AC-2）。
   *   選択後の再取得はこのセクション内だけを描き直す（全面 `load()` だと選択が消える）。
   */
  if (staff !== null) section.append(staffPunchSection(me, staff));

  /*
   * ★勤怠データのCSV出力（§3.6：owner のみ・R0102 A-4）。タブは分けない
   *   （出し分けは見た目の都合。担保はサーバの `export` / `read`＝owner のみ）。
   */
  if (me.role === "owner") section.append(renderAttendanceExportForm(me), renderDataExport(me));

  ctx.root.append(section);
}

/**
 * 打刻イベントの一覧（訂正ボタンつき）と訂正履歴（A-5 AC-1・AC-4）。
 * 自分のビューと従業員選択ビューの両方が使う。★判断（行の文言・選べる種別・時刻の整形）は
 * `src/ui/punch-view.ts`。ここは DOM に写すだけ。
 */
function punchLogBlocks(
  me: Me,
  events: PunchEvent[],
  corrections: PunchCorrection[],
  after: (message?: Notice) => void,
): HTMLElement[] {
  const blocks: HTMLElement[] = [];
  if (events.length === 0) {
    blocks.push(notice("まだ打刻がありません", "info"));
  } else {
    const list = el("ul", { className: "list" });
    for (const ev of events) {
      const li = el("li");
      li.append(el("span", { text: PUNCH_LABEL[ev.punchType] }));
      li.append(
        el("span", { text: `${ev.businessDate} ${ev.occurredAt.slice(11, 16)}`, className: "muted" }),
      );
      const toggle = el("button", { text: "訂正" });
      toggle.type = "button";
      toggle.addEventListener("click", () => {
        const open = li.querySelector("form");
        if (open !== null) {
          open.remove();
          return;
        }
        li.append(punchCorrectionForm(me, ev, after));
      });
      li.append(toggle);
      list.append(li);
    }
    blocks.push(list);
  }

  // ★履歴の閲覧は「打刻イベントの閲覧」と同じ行・同じ権限（§3.6）。0件なら見出しごと出さない
  if (corrections.length > 0) {
    const heading = el("h4", { text: "訂正履歴" });
    const list = el("ul", { className: "list" });
    // ★`correctionLine` は reason（利用者の自由文）を含む。textContent 経由（`el`）で入れる
    for (const c of corrections) list.append(el("li", { text: correctionLine(c) }));
    blocks.push(heading, list);
  }
  return blocks;
}

/** 打刻1件の訂正フォーム（A-5 AC-1。項目は 種別 / 時刻、理由必須） */
function punchCorrectionForm(me: Me, ev: PunchEvent, after: (message?: Notice) => void): HTMLFormElement {
  const form = el("form", { className: "punch-correction" });

  const fieldChoice = select("訂正する項目", "field", [
    { value: "punch_type", label: "種別" },
    { value: "occurred_at", label: "時刻" },
  ]);
  const fieldSelect = fieldChoice.querySelector("select") as HTMLSelectElement;
  form.append(fieldChoice);

  const typeChoice = select(
    "訂正後の種別",
    "newType",
    correctionTypeChoices(ev.punchType).map((t) => ({ value: t, label: PUNCH_LABEL[t] })),
  );
  form.append(typeChoice);

  const timeField = field("訂正後の時刻", "newTime", ev.occurredAt, "datetime-local");
  const timeInput = timeField.querySelector("input") as HTMLInputElement;
  timeInput.step = "1"; // ★秒まで持つ（サーバの受理形式が YYYY-MM-DDTHH:MM:SS のため）
  form.append(timeField);

  const sync = (): void => {
    typeChoice.hidden = fieldSelect.value !== "punch_type";
    timeField.hidden = fieldSelect.value !== "occurred_at";
  };
  fieldSelect.addEventListener("change", sync);
  sync();

  const reasonField = field("理由（必須）", "reason", "", "text");
  const reasonInput = reasonField.querySelector("input") as HTMLInputElement;
  reasonInput.required = true; // ★最後の砦はサーバ（空・200字超は 400 が返り、そのまま表示される）
  reasonInput.maxLength = 200;
  form.append(reasonField);

  const submit = el("button", { text: "訂正を記録する" });
  submit.type = "submit";
  form.append(submit);

  onSubmit(
    form,
    submit,
    (data) => {
      const chosen = String(data.get("field") ?? "");
      const isType = chosen === "punch_type";
      return api.correctPunch(
        me.storeId,
        ev.id,
        isType ? "punch_type" : "occurred_at",
        isType
          ? String(data.get("newType") ?? "")
          : normalizeOccurredAtInput(String(data.get("newTime") ?? "")),
        String(data.get("reason") ?? ""),
      );
    },
    after,
    "打刻を訂正しました",
  );
  return form;
}

/** manager / owner の従業員選択ビュー（A-5 AC-2・AC-4。担保はサーバの §3.6） */
function staffPunchSection(me: Me, staff: Staff[]): HTMLElement {
  const wrap = el("section", { className: "staff-punch" });
  wrap.append(el("h3", { text: "従業員の打刻の訂正" }));
  if (staff.length === 0) {
    wrap.append(notice("従業員がまだ登録されていません", "info"));
    return wrap;
  }

  const picker = select("従業員", "staffPunchTarget", [
    { value: "", label: "選択してください" },
    // ★氏名が返っていない＝読む権限が無い（renderStaff と同じ扱い）
    ...staff.map((s) => ({ value: s.id, label: `${s.staffCode} ${s.displayName ?? "—"}` })),
  ]);
  const pickerSelect = picker.querySelector("select") as HTMLSelectElement;
  wrap.append(picker);

  const box = el("div");
  wrap.append(box);

  const show = (staffId: string, message?: Notice): void => {
    // ★取りに行く月は自分のビューと同じ「当月」。ボタン状態の判定には使わないので足りる
    void api
      .getPunches(me.storeId, punchMonth(), { staffId })
      .then((log) => {
        const blocks = punchLogBlocks(me, log.events, log.corrections, (msg) => show(staffId, msg));
        box.replaceChildren(...(message !== undefined ? [noticeOf(message)] : []), ...blocks);
      })
      .catch((e: unknown) => {
        box.replaceChildren(notice(e instanceof ApiError ? e.message : "読み込めませんでした"));
      });
  };
  pickerSelect.addEventListener("change", () => {
    if (pickerSelect.value === "") {
      box.replaceChildren();
      return;
    }
    show(pickerSelect.value);
  });
  return wrap;
}

// ---------------------------------------------------------------------
// シフト（予定）（§3.7 / R0102 B-2）
// ---------------------------------------------------------------------

/**
 * 確定シフト表（**店舗全体・氏名つき**）と、作成フォーム（manager 以上）を出す。
 *
 * ★★**全ロールが同じ表を読む**（`docs/05` §3.7＝依頼者判断 Q-S1・R0102 B-5）。
 *   氏名と時刻が20名ぶん並ぶ。**時給・個人別人件費は1マスも出ない。**
 * ★**氏名を画面で合成しない。** サーバが返した `displayName` をそのまま写すだけにする——
 *   名簿と突き合わせて作ると、可視性の境界が画面側へ逃げる（絶対ルール1）。
 * ★**「自分の分だけ」は表示の絞り込み**であって可視性の境界ではない。
 *   絞っても絞らなくても、サーバが返している行は同じ。
 * ★時刻は 24時超え表現（`docs/02` §4.4）。`<input type="time">` では表せないので
 *   `HH:MM` の文字列で受け、`parseHhmm` が分へ直す。
 *
 * ★**下書き（B-6・Q-S12）は `manager` 以上の手元にしか来ない。**
 *   サーバが返す行が既に絞られている（`shiftDraftAccess`）。**画面で隠しているのではない。**
 *   ★だから「下書き」の印は、**見えている＝組む権限がある人にだけ**意味を持つ。
 *
 * @param staff `manager` / `owner` のときだけ渡る（作成フォームの選択肢）。担保はサーバの 403
 * @param month 公開の操作の範囲。★**表示している月と同じもの**を渡す（2箇所で決めない）
 */
export function renderShifts(
  ctx: ScreenContext,
  result: ShiftsResult,
  requests: ShiftRequestsResult,
  changeRequests: ShiftChangeRequestsResult,
  staff: Staff[] | null,
  month: string,
  message?: Notice,
): void {
  const { me, date, locked } = ctx;
  const section = el("section");
  if (message !== undefined) section.append(noticeOf(message));

  /*
   * ★★**承認待ちを、確定シフト表より先に出す**（AC-8）。
   *   組む人が最初に見るのは「返事をしていない申請」であって、既に組んだ表ではない。
   *   ★件数が0のときは何も出さない——常設すると「0件」が背景になって、
   *   0でない日に気づかない（記憶「承認待ちが0でないときそれが見える」）。
   */
  if (staff !== null) section.append(pendingRequests(me, date, locked, requests));
  /*
   * ★修正申請の承認待ち（§3.7：同じく `manager` 以上・R0102 B-4）。
   * ★**希望の承認待ちのすぐ下に置く**——組む人が最初に見る「返事をしていない申請」は
   *   希望・修正の両方であり、片方だけ目立つ場所にしない。
   */
  if (staff !== null) {
    section.append(pendingChangeRequests(me, date, locked, changeRequests, result.shifts));
  }

  if (staff !== null) {
    section.append(renderShiftForm(me, date, locked, staff));
    section.append(publishControls(me, date, month, locked, result));
  }

  /*
   * ★★**確定シフトが0件でも、ここで打ち切らない**（2026-09-02・B-3 の画面確認で判明）。
   *
   *   B-2 はここで `return` していた。**希望を出すのはシフトが組まれる前**なので、
   *   打ち切ると**いちばん要る場面（まだ何も組まれていない月）で申請フォームが出ない。**
   *   ★案内は出す。**出さないのは確定シフトの表だけ。**
   */
  const hasShifts = result.shifts.length > 0;
  // ★**どの月の話かを書く**（R0102 B-8）。月を移動できるようになった以上、
  //   「今月」と書くと、10月を開いているのに9月の話に読める
  if (!hasShifts) {
    section.append(notice(`${formatShiftMonth(month)}の確定シフトはまだありません。`, "info"));
  }

  /*
   * ★**再描画はここだけで完結させる**（サーバへ取りに行かない）。
   *   絞り込みは表示の都合であり、押すたびに GET し直すと
   *   「絞り込みが権限であるかのような形」になる。
   */
  const listHost = el("div");
  const paint = (onlyMine: boolean): void => {
    listHost.replaceChildren(renderShiftList(ctx, result, staff, onlyMine, changeRequests, month));
  };

  /*
   * ★★**既定は「自分の分だけ」**（2026-09-01・spec の差し戻し）。
   *
   *   B-2 では `staff` / `leader` に自分の分だけが返っていたので、
   *   **タブを開く＝自分の予定を見る**が操作0回で成立していた。
   *   B-5 で店舗全体を返すようにした結果、既定を「全員」にすると
   *   **主な利用者（アルバイト16名）が毎回チェックを付け直す**ことになる。
   *   知りたいのは多くの場合「自分の次の出勤」なので、そちらを既定にする。
   * ★**紐付けられていないアカウントには絞り込みようが無い**ので、そのときだけ全体を出す。
   */
  const linked = result.myStaffIds.length > 0;
  if (!hasShifts) {
    // ★表が無いなら絞り込みも出さない（押せるのに何も変わらないものを置かない）
  } else if (linked) {
    section.append(mineToggle(paint));
  } else {
    // ★黙って隠さない。絞り込みが出せない理由と、次にすることまで書く
    section.append(
      notice(
        "このアカウントは従業員と紐付けられていないため、「自分の分だけ」で絞り込めません。" +
          "オーナーに紐付けを依頼してください。表は店舗全体です。",
        "info",
      ),
    );
  }
  if (hasShifts) {
    paint(linked);
    section.append(listHost);
  }
  /*
   * ★自分の希望（全ロール）。**却下理由が本人の目に触れる唯一の経路**（§3.7）。
   * ★申請フォームは**紐付けられているときだけ**——`(個)` の申請なので、
   *   自分の従業員レコードが無ければ出しようがない（サーバも 403 で拒む）。
   */
  if (result.myStaffIds.length > 0) {
    section.append(requestForm(me, date, locked, result.myStaffIds[0]!, requests.window));
  }
  section.append(myRequests(me, date, locked, requests, result.myStaffIds));
  /*
   * ★自分の修正申請（全ロール）。★**`myStaffIds` で絞る**——「自分の分」を数える場所を
   *   `myRequests` と別に増やさない（記憶「自分の分を数える場所を増やさない」）。
   */
  section.append(myChangeRequests(me, date, locked, changeRequests, result.myStaffIds));
  ctx.root.append(section);
}

/**
 * 「自分の分だけ」の絞り込み。★状態は `checked` そのもの（別に持たない）。
 *
 * ★**`className` は `checkbox`**（2026-09-01・code と tester の差し戻し）。
 *   `field` は**テキスト入力用の縦積み**で、汎用の `input { width: 100%; min-height: 44px;
 *   border; padding }` がチェックボックスにもそのまま掛かる。
 *   `.checkbox input { width: auto; min-height: auto }` を浴びないと、
 *   **全幅44pxの白い箱がラベルの上に独立して出る**（実測：375/768/1280 の全てで再現）。
 *   ★このファイルの他の2つのチェックボックスも `checkbox` を使っている。**揃えること。**
 */
function mineToggle(paint: (onlyMine: boolean) => void): HTMLLabelElement {
  const wrap = el("label", { className: "checkbox" });
  const box = el("input");
  box.type = "checkbox";
  box.name = "onlyMine";
  box.checked = true;
  box.addEventListener("change", () => {
    paint(box.checked);
  });
  wrap.append(box, el("span", { text: "自分の分だけ" }));
  return wrap;
}

/**
 * 営業日ごとの表（B-5 AC-8）。★**日付を見出しにして、その下に氏名と時刻を並べる。**
 * 1行ずつ日付を繰り返すと 390px で読めない。
 */
function renderShiftList(
  ctx: ScreenContext,
  result: ShiftsResult,
  staff: Staff[] | null,
  onlyMine: boolean,
  changeRequests: ShiftChangeRequestsResult,
  /** ★表示中の月。**ここで `date` から作り直さない**——月の決定点を2つにしない */
  month: string,
): HTMLElement {
  const { me, date, locked } = ctx;
  /*
   * ★★**申請中の行には印を出し、2件目を出させない**（2026-09-02・spec の差し戻しの根）。
   *   承認待ちが無いように見える行から2件目を出せてしまい、サーバの 409 で初めて気づく形だった。
   *   ★「自分の分」の判定は増やさない——`myStaffIds`（上）で絞った行にだけ付く印。
   */
  const pendingByPlan = new Set(
    changeRequests.requests.filter((r) => r.status === "SUBMITTED" && r.shiftPlanId !== null).map((r) => r.shiftPlanId),
  );
  const mine = new Set(result.myStaffIds);
  const shifts = onlyMine ? result.shifts.filter((s) => mine.has(s.staffId)) : result.shifts;
  if (shifts.length === 0) {
    /*
     * ★**「今月」と書かない**（R0102 B-8）。月を移動できるので、
     *   10月を開いている人に「今月ありません」と言うと9月の話に読める。
     *   ★兄弟（店舗全体が0件のときの案内）は既に月を書いている。**片方だけ直さない。**
     */
    return notice(
      `${formatShiftMonth(month)}に自分の確定シフトはありません。`,
      "info",
    );
  }

  const list = el("ul", { className: "list" });
  for (const day of groupByDate(shifts)) {
    const dayItem = el("li");
    dayItem.append(el("strong", { text: formatShiftDate(day.businessDate) }));
    // ★`shift-rows` は検査が行を名指しするための印（日付の見出し行と混ざらないように）
    const rows = el("ul", { className: "list shift-rows" });
    for (const shift of day.shifts) {
      const li = el("li");
      if (mine.has(shift.staffId)) li.dataset["mine"] = "true";
      /*
       * ★`textContent` で入れる（`el` の `text`）。将来ここへ自由文（申請の理由・B-3）が
       *   混ざったときに `innerHTML` へ変えないための形をいまから固定する。
       * ★氏名はサーバが返したものをそのまま写す（画面で作らない）。
       */
      li.append(el("span", { text: shift.displayName }));
      li.append(el("span", { text: shiftSummary(shift) }));
      /*
       * ★下書きの印（B-6）。**この行が来ている時点で読み手は `manager` 以上**
       *   （サーバが `staff` / `leader` へ下書きを返さない）。
       * ★色だけで伝えない——`.badge` は記号と文字も出す（WCAG SC 1.4.1・`docs/10`）。
       */
      if (shift.publishedAt === null) {
        li.append(el("span", { text: "下書き", className: "badge draft" }));
      }
      if (staff !== null) {
        li.append(editShiftButton(me, date, shift, locked, li));
        li.append(cancelShiftButton(me, date, shift.id, locked));
      }
      /*
       * ★修正申請（§3.7：全ロール `W(個)`・要理由・R0102 B-4）。
       * ★**自分の・公開済みの行にだけ出す**——他人の行や下書きへは出せない
       *   （組む前のものは組む人が直せばよく、他人の分は §3.7 の別の行＝manager 以上の作成・変更）。
       */
      if (mine.has(shift.staffId) && shift.publishedAt !== null) {
        if (pendingByPlan.has(shift.id)) {
          li.append(el("span", { text: "修正申請中", className: "badge pending" }));
        } else {
          li.append(changeRequestButton(me, date, shift, locked, li));
        }
      }
      rows.append(li);
    }
    dayItem.append(rows);
    list.append(dayItem);
  }
  return list;
}

/**
 * フォームの `HH:MM` を分へ直す。**作成フォームと変更フォームで同じものを使う**
 * （2箇所に持つと、片方だけ 24時超えを取りこぼす日が来る）。
 * 読めなければ `ApiError` を**返す**（投げない。呼び出し側が `Promise.reject` に載せる）。
 */
function shiftTimesOf(
  d: FormData,
): { startMinutes: number; endMinutes: number; breaks: { startMinutes: number; endMinutes: number }[] } | ApiError {
  const startMinutes = parseHhmm(String(d.get("startAt") ?? ""));
  const endMinutes = parseHhmm(String(d.get("endAt") ?? ""));
  if (startMinutes === null || endMinutes === null) {
    return new ApiError(400, "invalid_input", "開始・終了は 17:00 の形で入力してください");
  }
  const breakStart = parseHhmm(String(d.get("breakStartAt") ?? ""));
  const breakEnd = parseHhmm(String(d.get("breakEndAt") ?? ""));
  if ((breakStart === null) !== (breakEnd === null)) {
    return new ApiError(400, "invalid_input", "休憩は開始と終了の両方を入力してください");
  }
  return {
    startMinutes,
    endMinutes,
    breaks:
      breakStart === null || breakEnd === null ? [] : [{ startMinutes: breakStart, endMinutes: breakEnd }],
  };
}

/**
 * 変更ボタン。押すと**いまの値を入れた**フォームを行の下に出す（§3.7「作成・変更・取消」）。
 *
 * ★★**取消→再作成では代わりにならない**（2026-09-01・spec の差し戻し）。
 *   打ち間違いのたびに全項目を記憶で打ち直すことになり、修正時に新しい誤入力を誘発する。
 *   ★加えて、取消→再作成は**新しい行**を作るので `updated_at` が動かず、
 *   B-4 が「変更あり」の材料に使う予定の列（`docs/02` §4.4）が実運用でほぼ発火しなくなる。
 * ★`businessDate` / `staffId` は変えられない（サーバ側も動かさない）。日付を移すなら取消して置き直す。
 */
function editShiftButton(
  me: Me,
  date: string,
  shift: Shift,
  locked: boolean,
  row: HTMLLIElement,
): HTMLButtonElement {
  const button = el("button", { text: "変更", className: "link" });
  button.type = "button";
  button.disabled = locked;
  button.addEventListener("click", () => {
    if (row.querySelector("form") !== null) return; // ★2回押しても2つ出さない

    const form = el("form");
    form.append(field("開始（例 17:00）", "startAt", formatHhmm(shift.startMinutes), "text"));
    form.append(field("終了（例 26:00 ＝ 深夜2時）", "endAt", formatHhmm(shift.endMinutes), "text"));
    // ★休憩は1組ぶんだけ入れ直せる（作成フォームと同じ制限）
    const first = shift.breaks[0];
    form.append(
      field("休憩開始（任意）", "breakStartAt", first === undefined ? "" : formatHhmm(first.startMinutes), "text"),
    );
    form.append(
      field("休憩終了（任意）", "breakEndAt", first === undefined ? "" : formatHhmm(first.endMinutes), "text"),
    );
    const submit = el("button", { text: "保存する" });
    submit.type = "submit";
    form.append(submit);

    onSubmit(
      form,
      submit,
      (d) => {
        const times = shiftTimesOf(d);
        return times instanceof ApiError
          ? Promise.reject(times)
          : api.updateShift(me.storeId, shift.id, times);
      },
      (m) => void load(me, date, "shift", m),
      "変更しました",
    );
    row.append(form);
  });
  return button;
}

/**
 * 理由を書く欄。★`.field` と同じ縦積みで揃える（`field()` は `<input>` 専用なので別に作る）。
 * `textarea` にも `.field` 配下の `input, select` と同じ寸法を掛ける（`styles.css`）。
 */
function textareaField(label: string, name: string): HTMLLabelElement {
  const wrap = el("label", { className: "field" });
  wrap.append(el("span", { text: label }));
  const textarea = document.createElement("textarea");
  textarea.name = name;
  wrap.append(textarea);
  return wrap;
}

/**
 * 確定シフトの修正を申請するボタン（§3.7：全ロール `W(個)`・要理由。R0102 B-4）。
 *
 * ★**`shiftPlanId` は対象の行の id をそのまま使う。** 画面で選ばせない
 *   （`requestForm` が `staffId` を選ばせないのと同じ理由）。
 * ★**理由が空なら送らない。** サーバも DB の CHECK も拒むが、押してから怒られるのは手戻り
 *   （`pendingRequests` の却下理由と同じ考え方）。
 */
function changeRequestButton(
  me: Me,
  date: string,
  shift: Shift,
  locked: boolean,
  row: HTMLLIElement,
): HTMLButtonElement {
  const button = el("button", { text: "修正を申請", className: "link" });
  button.type = "button";
  button.disabled = locked;
  button.addEventListener("click", () => {
    if (row.querySelector("form.change-request-form") !== null) return; // ★2回押しても2つ出さない

    const form = el("form", { className: "change-request-form" });
    form.append(field("開始（例 17:00）", "startAt", formatHhmm(shift.startMinutes), "text"));
    form.append(field("終了（例 26:00 ＝ 深夜2時）", "endAt", formatHhmm(shift.endMinutes), "text"));
    form.append(textareaField("修正の理由（必須）", "reason"));
    const submit = el("button", { text: "申請する" });
    submit.type = "submit";
    // ★閉じる手段（2026-09-02・spec の advisory）。開いたら再読込しか無かった
    const close = el("button", { text: "閉じる", className: "link" });
    close.type = "button";
    close.addEventListener("click", () => form.remove());
    form.append(submit, close);

    // ★`onSubmit` を通らない保存フォーム。未保存の見張りへは手で登録する（`guardForm` の説明）
    guardForm(form);
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const d = new FormData(form);
      const startMinutes = parseHhmm(String(d.get("startAt") ?? ""));
      const endMinutes = parseHhmm(String(d.get("endAt") ?? ""));
      if (startMinutes === null || endMinutes === null) {
        failAtButton(submit, "開始・終了は 17:00 の形で入力してください");
        return;
      }
      const reason = String(d.get("reason") ?? "").trim();
      if (reason === "") {
        failAtButton(submit, "修正の理由を入力してください");
        return;
      }
      submit.disabled = true;
      void api
        .createShiftChangeRequest(me.storeId, { shiftPlanId: shift.id, startMinutes, endMinutes, reason })
        .then(() => load(me, date, "shift", infoNotice("修正を申請しました")))
        .catch((err: unknown) => {
          failAtButton(submit, err);
        });
    });
    row.append(form);
  });
  return button;
}

/**
 * 希望を出すフォーム（§3.7：全ロール `W(個)`。R0102 B-3 の AC-7）。
 *
 * ★**`staffId` は自分の紐付けから取る。** 画面で選ばせない——
 *   選べる形にすると、他人の分を出す経路が画面にできる（サーバは `assertOwnStaff` で
 *   拒むが、**押せてしまう画面を作らない**）。
 * ★時刻は 24時超え表現（`docs/02` §4.4）。`<input type="time">` は 23:59 までしか表せない。
 * ★**締切はサーバが判定する。** 画面で締切を計算しない——
 *   営業日の切り替わり（`business_day_cutoff`）を2箇所で解くことになる。
 */
function requestForm(
  me: Me,
  date: string,
  locked: boolean,
  staffId: string,
  window: ShiftRequestsResult["window"],
): HTMLFormElement {
  const form = el("form");
  form.append(el("h2", { text: "希望を出す" }));
  /*
   * ★★**締切を押す前に出す**（2026-09-02・周回4 / spec の差し戻し）。
   *   周回3 まで、締切は**送信してから 400 で初めて分かる**形だった。
   *   しかも初期値は画面上部の「営業日」（既定は今日）で、
   *   **いちばん自然な操作が必ず締切超過で弾かれていた。**
   *   締切を読める場所は設定タブ（`manager` 以上）だけで、**申請する側は到達できない。**
   * ★**窓が引けないときは、出せないことを言う。** 空欄のフォームだけ置かない。
   */
  if (window === null) {
    form.append(
      notice(
        "いま出せる希望はありません。募集の期間と締切が設定されていない可能性があります。" +
          "店長・オーナーに設定を依頼してください。",
        "info",
      ),
    );
    return form;
  }
  /*
   * ★★**「上の対象月とは別」と書く**（R0102 B-8）。
   *   募集の窓は**今日から**サーバが1つに決めるもので、上のピッカーで選んだ月には追随しない
   *   （`listShiftRequestsBody` は `month` ではなく `findResolvedBusinessDate` から出している）。
   * ★B-8 でシフトタブが今月から解放されるまで、**この食い違いは画面に出せなかった**——
   *   表示月＝今月だったので、窓と見出しがいつも一致していた。
   *   ★10月の見出しの下に「2026-09-16 以降」とだけ書くと、10月の募集の話に読める。
   *   ★**窓そのものは動かさない。** 出せる期間は月の眺め方で変わらない。
   */
  form.append(
    notice(
      `${window.from} 以降の希望を受け付けています（締切：${window.deadline} まで）。` +
        `上の「対象月」に関わらず、いま出せるのはこの期間だけです。`,
      "info",
    ),
  );
  // ★初期値は**いま出せる最短の営業日**。画面上部の「営業日」を写さない（それは締切超過になる）
  form.append(field("営業日", "requestDate", window.from, "date"));
  form.append(field("開始（例 17:00）", "requestStart", "", "text"));
  form.append(field("終了（例 26:00 ＝ 深夜2時）", "requestEnd", "", "text"));
  const submit = el("button", { text: "希望を出す" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  // ★`onSubmit` を通らない保存フォーム。未保存の見張りへは手で登録する（`guardForm` の説明）
  guardForm(form);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const d = new FormData(form);
    const startMinutes = parseHhmm(String(d.get("requestStart") ?? ""));
    const endMinutes = parseHhmm(String(d.get("requestEnd") ?? ""));
    if (startMinutes === null || endMinutes === null) {
      failAtButton(submit, "開始・終了は 17:00 の形で入力してください");
      return;
    }
    submit.disabled = true;
    void api
      .createShiftRequest(me.storeId, {
        staffId,
        businessDate: String(d.get("requestDate") ?? ""),
        startMinutes,
        endMinutes,
      })
      .then(() => load(me, date, "shift", infoNotice("希望を出しました")))
      .catch((err: unknown) => {
        failAtButton(submit, err);
      });
  });
  return form;
}

/**
 * 承認待ちの申請（§3.7：`manager` 以上。R0102 B-3 の AC-8）。
 *
 * ★**0件のときは何も出さない。** 常設すると「0件」が背景になり、
 *   0でない日に気づかない（それが「見える」の中身）。
 * ★**却下は理由必須**（原典）。理由が空のまま押せない形にする——
 *   サーバも DB も拒むが、**押してから怒られるのは手戻り。**
 */
function pendingRequests(
  me: Me,
  date: string,
  locked: boolean,
  result: ShiftRequestsResult,
): HTMLElement {
  const pending = result.requests.filter((r) => r.status === "SUBMITTED");
  const host = el("section");
  if (pending.length === 0) return host;

  host.append(el("h2", { text: `承認待ちの希望 ${pending.length} 件` }));
  const list = el("ul", { className: "list request-rows" });
  for (const r of pending) {
    const li = el("li");
    // ★氏名はサーバが載せたときだけ出す（`can_view_payroll` のロール）。画面で合成しない
    li.append(el("span", { text: `${formatShiftDate(r.businessDate)} ${r.displayName ?? ""}`.trim() }));
    li.append(el("span", { text: `${formatHhmm(r.startMinutes)}〜${formatHhmm(r.endMinutes)}` }));

    const approve = el("button", { text: "承認" });
    approve.type = "button";
    approve.disabled = locked;
    approve.addEventListener("click", () => {
      approve.disabled = true;
      void api
        .approveShiftRequest(me.storeId, r.id)
        .then(() => load(me, date, "shift", infoNotice("承認しました。確定シフトに下書きとして入りました")))
        .catch((e: unknown) => {
          approve.disabled = false;
          void load(me, date, "shift", errorNotice(e instanceof ApiError ? e.message : "承認できませんでした"));
        });
    });

    const reasonWrap = field("却下の理由（必須）", `rejectReason-${r.id}`, "", "text");
    const reason = reasonWrap.querySelector("input") as HTMLInputElement;
    const reject = el("button", { text: "却下", className: "link danger" });
    reject.type = "button";
    reject.disabled = locked;
    reject.addEventListener("click", () => {
      /*
       * ★**押す前に止める。** 空の理由はサーバ（400）と DB の CHECK が拒むが、
       *   そこまで往復させると「押したのに何も起きない」に見える。
       */
      if (reason.value.trim() === "") {
        failAtButton(reject, "却下の理由を入力してください");
        return;
      }
      reject.disabled = true;
      void api
        .rejectShiftRequest(me.storeId, r.id, reason.value)
        .then(() => load(me, date, "shift", infoNotice("却下しました")))
        .catch((e: unknown) => {
          failAtButton(reject, e);
        });
    });

    li.append(approve, reasonWrap, reject);
    list.append(li);
  }
  host.append(list);
  return host;
}

/**
 * 承認待ちの修正申請（§3.7：`manager` 以上。R0102 B-4）。
 *
 * ★**現在の時刻は `result.shifts` を `shiftPlanId` で引く**——申請の応答に
 *   変更前の時刻は載らない（確定シフトを読む経路をもう1本増やさないため。課題の設計）。
 *   見つからなければ、取り消し済み・別の修正で既に動いたということ。**「見つからない」と言う。**
 * ★理由は自由文。`textContent` で描く（3経路のうちの1本）。
 */
function pendingChangeRequests(
  me: Me,
  date: string,
  locked: boolean,
  result: ShiftChangeRequestsResult,
  shifts: readonly Shift[],
): HTMLElement {
  const pending = result.requests.filter((r) => r.status === "SUBMITTED");
  const host = el("section");
  if (pending.length === 0) return host;

  const byId = new Map(shifts.map((s) => [s.id, s] as const));
  host.append(el("h2", { text: `承認待ちの修正申請 ${pending.length} 件` }));
  const list = el("ul", { className: "list change-request-rows" });
  for (const r of pending) {
    const li = el("li");
    li.append(el("span", { text: `${formatShiftDate(r.businessDate)} ${r.displayName ?? ""}`.trim() }));
    const current = r.shiftPlanId === null ? undefined : byId.get(r.shiftPlanId);
    li.append(
      el("span", {
        text:
          current === undefined
            ? "（確定シフトが見つかりません）"
            : `${formatHhmm(current.startMinutes)}〜${formatHhmm(current.endMinutes)} → ` +
              `${formatHhmm(r.startMinutes)}〜${formatHhmm(r.endMinutes)}`,
      }),
    );
    // ★申請の理由。自由文。`el` は `textContent` で入れる（`innerHTML` にしない）
    if (r.reason !== null) li.append(el("span", { text: `理由：${r.reason}` }));
    /*
     * ★承認の副作用を承認前に見せる（2026-09-02・spec の advisory）。
     *   `migrations/0056`：新しい区間の外へ出る休憩だけが承認時に消える。休憩が無ければ黙る。
     */
    if (current !== undefined && current.breaks.length > 0) {
      const breaks = current.breaks.map((b) => `${formatHhmm(b.startMinutes)}〜${formatHhmm(b.endMinutes)}`).join(" / ");
      li.append(el("span", { text: `休憩 ${breaks}：新しい区間の外へ出る分だけ承認時に消えます` }));
    }

    const approve = el("button", { text: "承認" });
    approve.type = "button";
    approve.disabled = locked;
    approve.addEventListener("click", () => {
      approve.disabled = true;
      void api
        .approveShiftChangeRequest(me.storeId, r.id)
        .then(() => load(me, date, "shift", infoNotice("承認しました。確定シフトを更新しました")))
        .catch((e: unknown) => {
          approve.disabled = false;
          void load(me, date, "shift", errorNotice(e instanceof ApiError ? e.message : "承認できませんでした"));
        });
    });

    const reasonWrap = field("却下の理由（必須）", `changeRejectReason-${r.id}`, "", "text");
    const reason = reasonWrap.querySelector("input") as HTMLInputElement;
    const reject = el("button", { text: "却下", className: "link danger" });
    reject.type = "button";
    reject.disabled = locked;
    reject.addEventListener("click", () => {
      if (reason.value.trim() === "") {
        failAtButton(reject, "却下の理由を入力してください");
        return;
      }
      reject.disabled = true;
      void api
        .rejectShiftChangeRequest(me.storeId, r.id, reason.value)
        .then(() => load(me, date, "shift", infoNotice("却下しました")))
        .catch((e: unknown) => {
          failAtButton(reject, e);
        });
    });

    li.append(approve, reasonWrap, reject);
    list.append(li);
  }
  host.append(list);
  return host;
}

/**
 * 自分の希望（§3.7：全ロール）。
 *
 * ## ★★「自分の分」は**サーバが返した `myStaffIds`** で決める（2026-09-02・周回3 / tester の差し戻し）
 *
 * ★**サーバの応答をそのまま「自分の」と呼ばない。**
 *   §3.7 の閲覧行は `staff` / `leader` が `(個)`、`manager` / `owner` は**店舗全体**。
 *   周回2 までは受け取った配列を無条件に描いていたので、
 *   **`manager` の「自分の希望」に他人の申請が並び、承認待ちの行に「取り下げ」まで出ていた**
 *   （サーバは 404 で拒むので実害は無いが、押せる形を画面に作っていた）。
 *
 * ★★**同じ絞り込みは、すぐ下の `renderShiftList` が既にやっている**（`mine = new Set(myStaffIds)`）。
 *   ★**答えは画面に届いていた。使い忘れていただけ。**
 *   「自分の分」を**サーバの読みスコープ・サーバの書きスコープ・画面**の3箇所で別々に決めており、
 *   この課題ではその3つが**3周とも**食い違った（周回1：読みと書き／周回2：読みと書き／周回3：画面）。
 *   ★**`myStaffIds` が唯一の答え。** 新しく「自分の分」を数える場所を増やさないこと。
 *
 * ★★**却下理由が本人の目に触れる唯一の経路**（§3.7：監査ログの閲覧は `manager` 以上なので、
 *   本人への説明責任を監査ログに預けない）。**この描画を消すと、本人は理由を読めなくなる。**
 * ★**却下理由は利用者の自由文。`textContent` で入れる**——`innerHTML` にすると
 *   却下理由が描画経路の注入口になる（`docs/02` §4.4 の明文）。
 */
function myRequests(
  me: Me,
  date: string,
  locked: boolean,
  result: ShiftRequestsResult,
  myStaffIds: readonly string[],
): HTMLElement {
  const host = el("section");
  host.append(el("h2", { text: "自分の希望" }));
  /*
   * ★★**未紐付けなら「黙って空」を返さない**（`docs/05` §2.1 の明文・2026-09-02 / spec の差し戻し）。
   *   周回3 までは、紐付けが無くても「まだ希望を出していません。」と出していた——
   *   **「出せるが、まだ出していない」と読める。** 実際は**出しようがない。**
   *   ★同じ画面の確定シフト一覧は既に説明を出しており、**この欄だけ黙っていた。**
   *   `GET /work-records` / `POST /punches` も同じ形（`message`）を持っている。
   */
  if (myStaffIds.length === 0) {
    host.append(
      notice(
        result.message ??
          "このアカウントは従業員と紐付けられていないため、希望を出せません。オーナーに紐付けを依頼してください。",
        "info",
      ),
    );
    return host;
  }
  const mine = new Set(myStaffIds);
  const rows = result.requests.filter((r) => mine.has(r.staffId));
  if (rows.length === 0) {
    host.append(notice("まだ希望を出していません。", "info"));
    return host;
  }
  const list = el("ul", { className: "list my-request-rows" });
  for (const r of rows) {
    const li = el("li");
    li.dataset["status"] = r.status;
    li.append(el("span", { text: formatShiftDate(r.businessDate) }));
    li.append(el("span", { text: `${formatHhmm(r.startMinutes)}〜${formatHhmm(r.endMinutes)}` }));
    li.append(el("span", { text: requestStatusLabel(r.status), className: statusClass(r.status) }));
    if (r.rejectReason !== null) {
      // ★自由文。**`el` は `textContent` で入れる**（`innerHTML` にしない）
      li.append(el("span", { text: `理由：${r.rejectReason}` }));
    }
    if (r.status === "SUBMITTED") li.append(withdrawButton(me, date, r, locked));
    list.append(li);
  }
  host.append(list);
  return host;
}

/** ★色だけで伝えない。`.badge` は記号と文字も出す（WCAG SC 1.4.1・`docs/10`） */
function statusClass(status: ShiftRequest["status"]): string {
  if (status === "APPROVED") return "badge passed";
  if (status === "REJECTED") return "badge failed";
  if (status === "SUBMITTED") return "badge draft";
  return "badge skipped";
}

/** ★取り下げは**締切後も通る**（原典）。画面でも締切で無効化しない */
function withdrawButton(me: Me, date: string, r: ShiftRequest, locked: boolean): HTMLButtonElement {
  const button = el("button", { text: "取り下げ", className: "link danger" });
  button.type = "button";
  button.disabled = locked;
  button.addEventListener("click", () => {
    button.disabled = true;
    void api
      .withdrawShiftRequest(me.storeId, r.id)
      .then(() => load(me, date, "shift", infoNotice("取り下げました")))
      .catch((e: unknown) => {
        button.disabled = false;
        void load(me, date, "shift", errorNotice(e instanceof ApiError ? e.message : "取り下げられませんでした"));
      });
  });
  return button;
}

/**
 * 自分の修正申請（§3.7：全ロール。R0102 B-4）。
 *
 * ★**`myRequests` と同じ形**（希望の側と2箇所に増やさない）。`myStaffIds` で絞る——
 *   「自分の分」を数える場所を増やさない（記憶「自分の分を数える場所を増やさない」）。
 * ★未紐付けなら黙って空を返さない（§2.1 の明文）。却下理由・申請理由の両方を `textContent` で描く。
 */
function myChangeRequests(
  me: Me,
  date: string,
  locked: boolean,
  result: ShiftChangeRequestsResult,
  myStaffIds: readonly string[],
): HTMLElement {
  const host = el("section");
  host.append(el("h2", { text: "自分の修正申請" }));
  if (myStaffIds.length === 0) {
    host.append(
      notice(
        result.message ??
          "このアカウントは従業員と紐付けられていないため、修正を申請できません。オーナーに紐付けを依頼してください。",
        "info",
      ),
    );
    return host;
  }
  const mine = new Set(myStaffIds);
  const rows = result.requests.filter((r) => mine.has(r.staffId));
  if (rows.length === 0) {
    host.append(notice("まだ修正を申請していません。", "info"));
    return host;
  }
  const list = el("ul", { className: "list my-change-request-rows" });
  for (const r of rows) {
    const li = el("li");
    li.dataset["status"] = r.status;
    li.append(el("span", { text: formatShiftDate(r.businessDate) }));
    li.append(el("span", { text: `${formatHhmm(r.startMinutes)}〜${formatHhmm(r.endMinutes)}` }));
    li.append(el("span", { text: requestStatusLabel(r.status), className: statusClass(r.status) }));
    // ★申請の理由。自由文。`el` は `textContent` で入れる
    if (r.reason !== null) li.append(el("span", { text: `理由：${r.reason}` }));
    if (r.rejectReason !== null) {
      li.append(el("span", { text: `却下理由：${r.rejectReason}` }));
    }
    if (r.status === "SUBMITTED") li.append(withdrawChangeButton(me, date, r, locked));
    list.append(li);
  }
  host.append(list);
  return host;
}

/** ★取り下げは締切の概念が無いのでいつでも通る（原典：修正申請は確定後にしか出せない） */
function withdrawChangeButton(me: Me, date: string, r: ShiftRequest, locked: boolean): HTMLButtonElement {
  const button = el("button", { text: "取り下げ", className: "link danger" });
  button.type = "button";
  button.disabled = locked;
  button.addEventListener("click", () => {
    button.disabled = true;
    void api
      .withdrawShiftChangeRequest(me.storeId, r.id)
      .then(() => load(me, date, "shift", infoNotice("取り下げました")))
      .catch((e: unknown) => {
        button.disabled = false;
        void load(me, date, "shift", errorNotice(e instanceof ApiError ? e.message : "取り下げられませんでした"));
      });
  });
  return button;
}

/** その月の初日・末日（`YYYY-MM-DD`）。★暦の境目であって営業日数ではない */
function monthEdges(month: string): { first: string; last: string } {
  const [y, m] = month.split("-").map(Number) as [number, number];
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { first: `${month}-01`, last: `${month}-${String(lastDay).padStart(2, "0")}` };
}

/**
 * 公開・公開取消（§3.7 の「公開」の行：`manager` 以上。R0102 B-6 / 依頼者判断 Q-S12）。
 *
 * ★★**営業日の範囲を選べる**（2026-09-02・周回2 / spec の差し戻し）。
 *   周回1 は月ごとに公開していたが、**20名×1か月を段階的に組む実務では、
 *   1〜15日を組み終えた時点で 16日以降が仮置きのまま残っている。**
 *   月ごとに公開すると、その仮置きも一緒に全20名へ出る——
 *   **この課題が防ごうとしている事象そのもの。** 取消側も同じで、
 *   後半の公開ミスを戻すと既に周知した前半まで消える。
 *   ★**既定は月の初日〜末日**なので、範囲を触らなければ周回1 と同じ操作になる。
 *
 * ★**押す前に、何件が対象かを出す。** 押した後に件数を知る形だと、
 *   「20名×1か月ぶんを公開した」ことに気づけない（戻す操作はあるが、その間は全員が見ている）。
 * ★**件数はサーバへ取りに行かず、いま画面が持っている行から数える**——
 *   範囲を触るたびに GET し直すと、絞り込みが権限であるかのような形になる（B-5 の注記と同じ）。
 * ★対象が0件でも**操作を消さない**。「いつ押せるか」が状況で変わると押し方を覚えられない。
 *   **無効化して、件数を出したままにする。**
 */
function publishControls(
  me: Me,
  date: string,
  month: string,
  locked: boolean,
  result: ShiftsResult,
): HTMLElement {
  const { first, last } = monthEdges(month);
  const host = el("div");

  const range = el("div", { className: "punch-buttons" });
  const dateInput = (label: string, name: string, value: string): HTMLLabelElement => {
    const wrap = field(label, name, value, "date");
    const input = wrap.querySelector("input") as HTMLInputElement;
    // ★画面が読み込んでいない月へはみ出させない（件数と実態がずれる）
    input.min = first;
    input.max = last;
    return wrap;
  };
  const fromField = dateInput("公開する範囲（開始）", "publishFrom", first);
  const toField = dateInput("公開する範囲（終了）", "publishTo", last);
  const valueOf = (wrap: HTMLLabelElement): string =>
    (wrap.querySelector("input") as HTMLInputElement).value;

  const buttons = el("div", { className: "punch-buttons" });
  const publish = el("button", { text: "" });
  const unpublish = el("button", { text: "", className: "link danger" });
  const summary = notice("", "info");

  const paint = (): void => {
    /*
     * ★**空欄を月の端として読む**（2026-09-02・周回2 / code の指摘）。
     *   `<input type="date">` は手で消せる。`"" <= 日付` は文字列比較で**常に真**なので、
     *   空欄のままだと**送れる範囲より多い件数をプレビューする**（送信は 400 で止まるが、
     *   利用者は押す前に嘘の数を見ることになる）。
     */
    const from = valueOf(fromField) || first;
    const to = valueOf(toField) || last;
    const inRange = result.shifts.filter((s) => s.businessDate >= from && s.businessDate <= to);
    const draftRows = inRange.filter((s) => s.publishedAt === null);
    const published = inRange.length - draftRows.length;
    publish.textContent = `この範囲を公開（下書き ${draftRows.length} 件）`;
    unpublish.textContent = `公開を取り消す（公開済み ${published} 件）`;
    publish.disabled = locked || draftRows.length === 0;
    unpublish.disabled = locked || published === 0;
    /*
     * ★★**件数だけでなく、どの営業日が対象かを出す**（2026-09-02・周回2 / spec の指摘）。
     *   一覧側は範囲を知らないので、範囲を狭めても行の見た目は変わらない。
     *   ★**件数だけだと、店長は暗算で「意図した対象と一致しているか」を判断することになる**——
     *   半月ずつ組む運用では「70件のはずが73件」に気づけない。**日付なら目で照合できる。**
     */
    const days = [...new Set(draftRows.map((s) => formatShiftDate(s.businessDate)))];
    summary.textContent =
      days.length > 0
        ? `この範囲に下書きが ${draftRows.length} 件あります（${days.join("・")}）。` +
          `公開するまで、店長・オーナー以外には表示されません。`
        : `この範囲に下書きはありません。`;
  };

  for (const wrap of [fromField, toField]) {
    wrap.querySelector("input")?.addEventListener("change", paint);
  }

  const run = (
    button: HTMLButtonElement,
    call: (from: string, to: string) => Promise<unknown>,
    done: string,
  ): void => {
    button.type = "button";
    button.addEventListener("click", () => {
      button.disabled = true;
      void call(valueOf(fromField), valueOf(toField))
        .then(() => load(me, date, "shift", infoNotice(done)))
        .catch((e: unknown) => {
          failAtButton(button, e);
        });
    });
  };
  run(
    publish,
    (from, to) => api.publishShifts(me.storeId, from, to),
    "公開しました",
  );
  run(
    unpublish,
    (from, to) => api.unpublishShifts(me.storeId, from, to),
    "公開を取り消しました",
  );

  paint();
  range.append(fromField, toField);
  buttons.append(publish, unpublish);
  host.append(range, buttons, summary);
  return host;
}

/** 取消ボタン。★物理削除ではない（サーバは `CANCELED` にするだけ） */
function cancelShiftButton(
  me: Me,
  date: string,
  shiftId: string,
  locked: boolean,
): HTMLButtonElement {
  const button = el("button", { text: "取消", className: "link danger" });
  button.type = "button";
  button.disabled = locked;
  button.addEventListener("click", () => {
    button.disabled = true;
    void api
      .cancelShift(me.storeId, shiftId)
      .then(() => load(me, date, "shift", infoNotice("取り消しました")))
      .catch((e: unknown) => {
        button.disabled = false;
        void load(me, date, "shift", errorNotice(e instanceof ApiError ? e.message : "取り消せませんでした"));
      });
  });
  return button;
}

/**
 * 確定シフトの作成フォーム（§3.7：manager 以上・Q-S2）。
 *
 * ★休憩は**1組だけ**入力できる（空欄なら休憩なし）。複数組は API とテーブルが既に持っている
 *   （`docs/02` §4.4 の対の集合）が、入力欄は B-2 では1組に留める。
 * ★時刻が読めない場合はサーバへ送らずここで止める（`NaN` を送ると 400 の理由が曖昧になる）。
 */
function renderShiftForm(me: Me, date: string, locked: boolean, staff: Staff[]): HTMLFormElement {
  const form = el("form");
  form.append(
    select(
      "従業員",
      "staffId",
      staff
        .filter((s) => s.isActive)
        // ★氏名が無ければ従業員コードで出す（`displayName` はサーバが伏せることがある）
        .map((s) => ({ value: s.id, label: s.displayName ?? s.staffCode })),
    ),
  );
  form.append(field("営業日", "businessDate", date, "date"));
  // ★`type="time"` を使わない（23:59 までしか表せず、26:00 のような深夜勤務が入らない）
  form.append(field("開始（例 17:00）", "startAt", "", "text"));
  form.append(field("終了（例 26:00 ＝ 深夜2時）", "endAt", "", "text"));
  form.append(field("休憩開始（任意）", "breakStartAt", "", "text"));
  form.append(field("休憩終了（任意）", "breakEndAt", "", "text"));

  const submit = el("button", { text: locked ? "変更できません" : "置く" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  onSubmit(
    form,
    submit,
    (d) => {
      const times = shiftTimesOf(d);
      if (times instanceof ApiError) return Promise.reject(times);
      return api.createShift(me.storeId, {
        staffId: String(d.get("staffId") ?? ""),
        businessDate: String(d.get("businessDate") ?? date),
        ...times,
      });
    },
    (m) => void load(me, date, "shift", m),
    "置きました",
  );
  return form;
}

/**
 * 全データのエクスポート（F-18・§3.2：owner のみ）。★開いたときにだけ一覧を取りに行く（毎回の描画を重くしない）。
 * 出し分けは見た目の都合。担保はサーバの `export` / `read`＝owner のみ。1表ずつ出力し、1回ごとに監査ログへ残る
 */
/** 監査の対象外で `AUDIT_TABLE_LABEL` に載せられない表（あちらは監査対象の表だけ、と検査が固定している） */
const UNAUDITED_TABLE_LABEL: Record<string, string> = {
  audit_logs: "監査ログ",
  shift_notice_seen: "シフトのお知らせの既読",
};

function renderDataExport(me: Me): HTMLDetailsElement {
  const details = el("details");
  details.append(el("summary", { text: "全データのエクスポート（表ごとのCSV）" }));
  details.append(
    el("p", {
      className: "muted",
      text: "従業員の氏名と時給を含みます。出力するたびに、誰がどの表を出したかが監査ログに残ります。",
    }),
  );
  const list = el("ul");
  details.append(list);
  let loaded = false;
  details.addEventListener("toggle", () => {
    if (!details.open || loaded) return;
    loaded = true;
    api.getExportTables(me.storeId).then(
      ({ tables }) => {
        for (const table of tables) {
          const item = el("li");
          const link = el("a", { text: `${UNAUDITED_TABLE_LABEL[table] ?? auditTableLabel(table)}（${table}.csv）` });
          link.href = api.dataExportUrl(me.storeId, table);
          link.target = "_blank";
          link.rel = "noopener";
          item.append(link);
          list.append(item);
        }
      },
      () => {
        loaded = false;
        list.replaceChildren(el("li", { text: "一覧を取得できませんでした。閉じてから、もう一度開いてください" }));
      },
    );
  });
  return details;
}

/** ★JSON API ではないのでダウンロードは新しいタブへ任せる（現在の SPA を壊さない） */
function renderAttendanceExportForm(me: Me): HTMLFormElement {
  const form = el("form");
  form.append(el("h3", { text: "勤怠データのCSV出力" }));
  form.append(field("開始日", "from", "", "date"));
  form.append(field("終了日", "to", "", "date"));
  const submit = el("button", { text: "CSVを出力する" });
  submit.type = "submit";
  form.append(submit);
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const data = new FormData(form);
    const from = String(data.get("from") ?? "");
    const to = String(data.get("to") ?? "");
    if (from === "" || to === "") return;
    window.open(api.attendanceExportUrl(me.storeId, from, to), "_blank");
  });
  return form;
}

// ---------------------------------------------------------------------
// 棚卸金額（★manager 以上。月単位）
// ---------------------------------------------------------------------

/**
 * 棚卸は月に属する。営業日の入力欄はそのまま使い、**その日の属する月**を対象にする。
 *
 * ★期首は表示だけ。入力欄を作らない（計算定義書 §3.2「手入力させない」）。
 * ★未入力は「—」。0 と区別する。0 を入れると「棚卸したが在庫が無かった」になり、
 *   月間原価がそのぶん過大に出る。**期首（前月の繰越）にも同じ区別を出す。**
 */
/**
 * 目標達成率の棒（R-03 / R03-C-4）。★**ライブラリを足さない**（epic 問い6）。
 *
 * ★**数値と必ず併記する**（`docs/10` §4・WCAG 1.4.1）。棒は隣の `dd` の数値の言い換えでしかないので
 *   `aria-hidden` で読み上げから外す——同じことを2回言わせない。
 * ★**動きを足さない。** `transition` を書かなければ `prefers-reduced-motion` で止めるものが無い。
 * ★色は `docs/10` §1 のトークンだけ（`--accent` / `--positive`）。数値をここに書かない。
 */
function achievementBar(ratio: number): HTMLElement {
  const g = barGeometry(ratio);
  const track = el("div", { className: g.over ? "bar over" : "bar" });
  track.setAttribute("aria-hidden", "true");
  const fill = el("span", { className: "bar-fill" });
  fill.style.width = `${g.fill}%`;
  track.append(fill);
  /*
   * ★目標の印は**超えたときだけ**出す。未達なら目標は右端＝枠そのものなので、
   *   同じ位置に線を重ねても意味が増えない（枠は `--line-strong` で 3:1 を満たす）。
   * ★色は `--surface`。★**`--fg` では超過色（`--positive`）に対して 3:1 に届かない**
   *   （ライト 2.87 / ダーク 2.64。2026-09-04 に `docs/10` §1 の値から算出）。
   */
  if (g.over) {
    const mark = el("span", { className: "bar-target" });
    // ★枠の内側に残す（`overflow: hidden` の中で右端に置くと消える）
    mark.style.left = `calc(${g.target}% - 1px)`;
    track.append(mark);
  }
  return track;
}

/**
 * 当日・当月の売上要約のカード（R-03 / R03-C-3・`docs/02` §2.3）。
 *
 * ★集計は画面でしない。サーバの `findSalesSummary`（単一実装点）が返した値を写すだけ。
 * ★判断（`null` の言い分け・書式・棒の幾何）は `sales-summary-view.ts`。
 *   ここは**それを画面へ写す最後の1行**（`.loop/memory.md`「判断を切り出して検査したら、
 *   それを画面へ写す最後の1行が無検査で残る」）。
 * ★C-4 で「売上要約」タブ（全ロール）へ移した。締めタブからは外してある——
 *   同じ意味の表示を2箇所に持つと、見え方の違いだけで別の意味に読まれる。
 * ★**`export` しているのは検査から呼ぶため。**
 *
 * @param summary 応答。読めなかったときは理由の文字列（黙って消さない）
 */
export function salesSummaryCard(summary: SalesSummary | string): HTMLElement {
  const card = el("section", { className: "card" });
  card.append(el("h2", { text: "売上要約" }));
  if (typeof summary === "string") {
    card.append(notice(summary));
    return card;
  }
  const view = summaryView(summary);
  const block = (heading: string, lines: readonly SummaryLine[]): void => {
    card.append(el("h3", { text: heading }));
    const dl = el("dl", { className: "status" });
    for (const line of lines) {
      dl.append(el("dt", { text: line.label }));
      const dd = el("dd", { text: line.value });
      // ★棒は**数値の代わりではなく添え物**。`ratio` が無い行（未入力・未設定・目標0）には出さない
      if (line.ratio !== undefined) dd.append(achievementBar(line.ratio));
      dl.append(dd);
    }
    card.append(dl);
  };
  block(view.dayHeading, view.day);
  block(view.monthHeading, view.month);
  return card;
}

/**
 * 損益のカード（#12・§3.2：manager 以上）。売上要約タブの下に出す。
 * ★判断は `profit-loss-view.ts`。ここは画面へ写すだけ。`export` は検査から呼ぶため。
 *
 * @param anomalies 異常検知（F-16）。渡さなければ欄ごと出さない
 *   （宛先は同じ §3.2 のカード。`docs/05` §3.2「異常検知アラート」の注記）。
 */
export function profitLossCard(profitLoss: ProfitLoss | string, anomalies?: DailyAnomalies): HTMLElement {
  const card = el("section", { className: "card" });
  card.append(el("h2", { text: "損益（店長・オーナーのみ）" }));
  if (typeof profitLoss === "string") {
    card.append(notice(profitLoss));
    return card;
  }
  const view = profitLossView(profitLoss);
  // ★除いたものの知らせは誤りではない。赤（error）で出さない
  for (const warning of view.warnings) card.append(notice(warning, "info"));
  /*
   * ★正常も1行で言う（`anomaly-view.ts`）ので、判定が返れば欄は必ず出る。
   *   「警告」は `.warn`（`--caution`。誤りではないが気づいてほしい）、
   *   「判定不能」は理由だけ `.muted` で小さく出す（人件費率の判定不能は毎日出うるので、
   *   目立たせすぎない）。
   */
  if (anomalies !== undefined) {
    const lines = anomalyView(anomalies).lines;
    if (lines.length > 0) {
      card.append(el("h3", { text: "異常検知" }));
      const list = el("ul", { className: "list" });
      for (const line of lines) {
        const li = el("li");
        li.append(
          el(line.kind === "indeterminate" ? "small" : "p", {
            text: line.text,
            className: { anomaly: "warn", normal: "anomaly-ok", indeterminate: "muted" }[line.kind],
          }),
        );
        list.append(li);
      }
      card.append(list);
    }
  }
  for (const block of view.blocks) {
    card.append(el("h3", { text: block.heading }));
    if (block.note !== undefined) card.append(el("p", { text: block.note, className: "muted" }));
    const dl = el("dl", { className: "status" });
    for (const line of block.lines) {
      dl.append(el("dt", { text: line.label }), el("dd", { text: line.value }));
    }
    card.append(dl);
  }
  if (profitLoss.trend.length > 0) card.append(...trendSection(profitLoss.trend));
  return card;
}

/**
 * 推移の2枚（`docs/02` §7.1）。★判断は `trendCharts`。ここは棒を置くだけ。
 * ★ライブラリも SVG も足さない（R-03 の目標の棒と同じ）。高さは `style.height` の%だけ。
 * ★各棒は `aria-label` と `title` で日付と金額を言い、下に表も置く（色・形だけで伝えない）。
 */
function trendSection(trend: ProfitLoss["trend"]): HTMLElement[] {
  const first = trend[0]!.date;
  const last = trend[trend.length - 1]!.date;
  const md = (d: string): string => `${Number(d.slice(5, 7))}/${Number(d.slice(8, 10))}`;
  const nodes: HTMLElement[] = [el("h3", { text: `推移（${md(first)}〜${md(last)}）` })];
  const charts = trendCharts(trend);
  for (const chart of charts) {
    if (chart.empty !== null) {
      nodes.push(el("p", { text: chart.empty, className: "muted" }));
      continue;
    }
    nodes.push(el("p", { text: chart.title, className: "trend-title" }));
    const plot = el("div", { className: chart.hasNegative ? "trend has-negative" : "trend" });
    plot.setAttribute("role", "list");
    plot.setAttribute("aria-label", chart.title);
    for (const bar of chart.bars) {
      // ★未入力は棒を描かず、基線に印だけ置く（0 円＝定休日と見分ける）
      const col = el("span", { className: bar.ratio === null ? "trend-col missing" : "trend-col" });
      col.setAttribute("role", "listitem");
      col.setAttribute("aria-label", `${bar.day} ${bar.value}`);
      col.title = `${bar.day} ${bar.value}`;
      if (bar.ratio !== null) {
        const fill = el("span", { className: bar.negative ? "trend-bar negative" : "trend-bar" });
        // ★基線が真ん中のときは半分の高さが最大
        fill.style.height = `${String(bar.ratio * (chart.hasNegative ? 50 : 100))}%`;
        col.append(fill);
      }
      plot.append(col);
    }
    const axis = el("div", { className: "trend-axis muted" });
    axis.append(el("small", { text: md(first) }), el("small", { text: md(last) }));
    nodes.push(plot, axis);
  }
  const details = el("details");
  details.append(el("summary", { text: "数値で見る" }));
  const table = el("table", { className: "trend-table" });
  const head = el("tr");
  for (const h of ["日付", "売上（税抜）", "営業利益"]) head.append(el("th", { text: h }));
  table.append(head);
  const [sales, profit] = charts;
  sales!.bars.forEach((b, i) => {
    const row = el("tr");
    row.append(el("td", { text: b.day }), el("td", { text: b.value }), el("td", { text: profit!.bars[i]!.value }));
    table.append(row);
  });
  details.append(table);
  nodes.push(details);
  return nodes;
}

/**
 * 月次締めの画面（M9c）。自動検算の14件と、締め申請。
 *
 * ★**「締められません」で終わらせない。** 何がまだ検査されていないかを14件ぶん名指しで出す。
 *   並べ替え・件数・押せるかどうか・文言の判断は `closing-view.ts` が持つ。
 *
 * ★申請の前に確認の一段を挟む（GOV.UK の Check answers）。
 *   月次締めは**不可逆**（解除は owner の操作と理由が要る）ので、押し間違いを1回で通さない。
 *
 * ★**`export` しているのは検査から呼ぶため**（`renderAudit` と同じ理由）。
 *   判断は `closing-view.ts` に切り出してあるが、**それを画面へ写す最後の1行**は
 *   ここにしか無く、消しても結合の検査は緑のままになる
 *   （`.loop/memory.md`「判断を切り出して検査したら、それを画面へ写す最後の1行が無検査で残る」）。
 */
export function renderClosing(ctx: ScreenContext, data: ClosingChecks, message?: Notice): void {
  const { me, date } = ctx;
  const month = data.month;
  const section = el("section");
  section.append(el("h2", { text: `${month} の締め` }));
  if (message !== undefined) section.append(noticeOf(message));

  const summary = summarizeChecks(data.checks, data.closingStatus);
  const reasonNote = closingReasonNote(data.closingStatus, data);
  if (reasonNote !== null) section.append(notice(reasonNote, "info"));

  /*
   * ★状態はシェル（全タブ共通）が既に出している。**ここで二度言わない**——
   *   同じ意味の表示が2種類あると、見え方の違いだけで別の意味に読まれる
   *   （2026-08-20・spec-reviewer が実機で指摘）。
   *   この画面が足すのは「申請という操作から見た状態」で、下の `reason` がそれを言う。
   */

  /*
   * ★件数を必ず出す。「問題なし」だけだと、**1件も検査していない状態**と見分けが付かない
   *   （`.loop/memory.md`「走った件数と飛ばした件数を分けて聞く」）。
   */
  const tally = el("p", { className: "num" });
  tally.textContent =
    `${String(summary.counts.total)}件中 ／ ` +
    `一致 ${String(summary.counts.passed)} ／ ` +
    `不一致 ${String(summary.counts.failed)} ／ ` +
    `未検証 ${String(summary.counts.skipped)}`;
  section.append(tally);

  const actions = ownerActions(me.role, data.closingStatus, summary.counts);
  const ownerReason = ownerSubmittedReason(actions);
  if (ownerReason !== null) {
    section.append(notice(ownerReason, actions.confirm ? "info" : "error"));
  } else if (summary.reason !== null) {
    // ★止まっている理由は alert で読み上げる。ボタンを押せなくするだけにしない
    section.append(notice(summary.reason, "error"));
  }

  const list = el("ul", { className: "list" });
  for (const check of summary.ordered) {
    const li = el("li");
    const badge = el("span", { text: STATUS_LABEL[check.status], className: `badge ${check.status}` });
    li.append(badge);
    li.append(el("span", { text: `${check.code} ${check.name}` }));
    li.append(el("small", { text: describeCheck(check), className: "muted" }));
    /*
     * ★検算が拾ったのに判定を変えないもの（休業日の売上・承認待ちの仕入）を出す。
     *   ★**`muted` にしない。** 「確認しました」の下に薄字で置くと、
     *   いちばん気づいてほしいものがいちばん読まれない場所へ落ちる。
     */
    for (const note of notesFor(check)) {
      li.append(el("small", { text: `${note}`, className: "warn" }));
    }
    list.append(li);
  }
  section.append(list);

  /*
   * ★申請できないときはボタンを**出さない**（押せないボタンを置かない）。
   *   理由は上の notice に出ている。押せない理由を押してから知らせない。
   */
  if (summary.canSubmit) {
    const form = el("form");
    form.append(
      el("p", {
        text: `${month} の自動検算はすべて通っています。締め申請すると、この月は店舗側から変更できなくなります。`,
      }),
    );
    const submit = el("button", { text: "締め申請する" });
    submit.type = "submit";
    form.append(submit);
    onSubmit(form, submit, async () => {
      await api.submitClosing(me.storeId, month);
      return `${month} を締め申請しました`;
    }, (msg) => void load(me, date, "closing", msg));
    section.append(form);
  }

  /*
   * ★owner だけの操作（M10）。確定は不可逆（以後は解除と理由が要る）ので、何が起きるかをボタンの上で言う。
   *   差戻し・解除は理由が必須（サーバと DB も同じ門を持つ）。
   */
  const decide = (
    action: "confirm" | "reject" | "reopen",
    lead: string,
    label: string,
    done: string,
    reasonLabel?: string,
  ): void => {
    const form = el("form");
    form.append(el("p", { text: lead }));
    if (reasonLabel !== undefined) {
      const reason = textareaField(reasonLabel, "reason");
      reason.querySelector("textarea")?.setAttribute("required", "");
      form.append(reason);
    }
    const button = el("button", { text: label, ...(action === "confirm" ? {} : { className: "danger" }) });
    button.type = "submit";
    form.append(button);
    onSubmit(
      form,
      button,
      (fd) => api.decideClosing(me.storeId, month, action, reasonLabel === undefined ? undefined : String(fd.get("reason") ?? "")),
      (msg) => void load(me, date, "closing", msg),
      done,
    );
    section.append(form);
  };
  if (actions.confirm) {
    decide("confirm", `${month} を確定すると、この月は誰も変更できなくなります（直すには締めの解除と理由が要ります）。`, "締めを確定する", `${month} を確定しました`);
  }
  if (actions.reject) {
    decide("reject", "店舗へ差し戻すと、この月はもう一度入力できるようになります。", "差し戻す", `${month} を差し戻しました`, "差し戻す理由");
  }
  if (actions.reopen) {
    decide("reopen", "締めを解除すると、この月はもう一度入力できるようになります。解除は理由つきで監査ログに残ります。", "締めを解除する", `${month} の締めを解除しました`, "解除する理由");
  }

  ctx.root.append(section);
}

/**
 * 監査ログの画面（F-22a / 絶対ルール8）。
 *
 * ★**行全体を出さない。** サーバが「変わった列だけ」を返す。
 *   `users` はログイン失敗のたびに全列が乗るので、そのまま並べるとノイズに埋もれる。
 *
 * ★**`(マスク済み)` を `true` / `false` と同じ見た目にしない。**
 *   伏せてあるのであって、その値だったわけではない。
 */
export function renderAudit(ctx: ScreenContext, page: AuditPage, message?: Notice): void {
  const { me, date } = ctx;
  const section = el("section");
  section.append(el("h2", { text: "監査ログ" }));
  if (message !== undefined) section.append(noticeOf(message));

  const params = new URLSearchParams(window.location.search);
  const current = auditFilter;

  /* 絞り込み。★選択肢は「実際に記録がある表」だけ（空振りする選択肢を作らない） */
  const filter = el("form");
  filter.append(
    select(
      "対象",
      "table",
      [
        { value: "", label: `すべて（${String(page.total)}件）` },
        ...page.tables.map((t) => ({
          value: t.tableName,
          label: `${auditTableLabel(t.tableName)}（${String(t.count)}件）`,
        })),
      ],
      current.table,
    ),
  );
  /*
   * ★**操作の種類。** `パスワード変更の失敗` は `users` の記録として入るので、
   *   アカウント作成・再発行と同居する。対象だけでは抜けない（B2f の A3）。
   */
  filter.append(
    select(
      "操作の種類",
      "action",
      [
        { value: "", label: "すべて" },
        ...page.actions.map((a) => ({
          value: a.action,
          label: `${auditActionLabel(a.action)}（${String(a.count)}件）`,
        })),
      ],
      current.action,
    ),
  );
  filter.append(
    select(
      "操作した人",
      "actor",
      [
        { value: "", label: "すべて" },
        ...page.actors.map((a) => ({
          value: a.actor,
          label: `${a.actor}（${String(a.count)}件）`,
        })),
      ],
      current.actor,
    ),
  );
  /*
   * ★**接続元（A6-2b）。記録が1件も無ければ、選択肢そのものを出さない。**
   *   開発では常に空になる（A6-1）——空の選択肢を置くと
   *   「選べるのに絞れない」口が1つ増える（`page.tables` と同じ約束）。
   */
  if (page.sources.length > 0 || current.ip !== "") {
    filter.append(
      select(
        "接続元",
        "ip",
        [
          { value: "", label: "すべて" },
          ...page.sources.map((s) => ({
            value: s.ip,
            label: `${s.ip}（${String(s.count)}件）`,
          })),
        ],
        current.ip,
      ),
    );
  }
  filter.append(field("この日から（任意）", "from", current.from, "date"));
  filter.append(field("この日まで（任意）", "to", current.to, "date"));

  /*
   * ★**畳んでいることを隠さない。** ログインの記録は毎リクエスト更新されるので
   *   既定では外しているが、**何件あるかを書いたうえで**1タップで含められるようにする。
   */
  /*
   * ★対象を指定しているときは畳んでいないので、この操作を出さない
   *   （出すと「押しても変わらない」＝畳んでいないのに畳んだと言うことになる）。
   */
  if (
    current.table === "" &&
    current.action === "" &&
    current.actor === "" &&
    current.ip === "" &&
    (page.hiddenSessions > 0 || current.sessions)
  ) {
    const wrap = el("label", { className: "checkbox" });
    const box = el("input");
    box.type = "checkbox";
    box.name = "sessions";
    box.checked = current.sessions;
    wrap.append(box);
    wrap.append(
      el("span", {
        text: current.sessions
          ? "ログインの記録を含めている"
          : `ログインの記録 ${String(page.hiddenSessions)}件 を含める`,
      }),
    );
    wrap.append(
      el("small", { text: "画面を開くたびに増える記録です。既定では外しています" }),
    );
    filter.append(wrap);
  }
  const apply = el("button", { text: "絞り込む" });
  apply.type = "submit";
  filter.append(apply);
  filter.addEventListener("submit", (e) => {
    e.preventDefault();
    const data = new FormData(filter);
    auditFilter = {
      table: String(data.get("table") ?? ""),
      action: String(data.get("action") ?? ""),
      from: String(data.get("from") ?? ""),
      to: String(data.get("to") ?? ""),
      actor: String(data.get("actor") ?? ""),
      /* ★選択肢を出していない周回では `null` になる。空文字＝すべて */
      ip: String(data.get("ip") ?? ""),
      offset: 0,
      sessions: data.get("sessions") !== null,
    };
    void load(me, date, "audit");
  });
  section.append(filter);
  void params;

  /*
   * ★件数を必ず出す。「0件」と「絞り込みが効いていない」を区別できるようにする。
   */
  const shown = page.entries.length;
  const tally = el("p", { className: "num" });
  tally.textContent =
    shown === 0
      ? `該当する記録は 0件です（絞り込みを外すと ${String(page.total)}件）`
      : `${String(page.total)}件中 ${String(page.offset + 1)}〜${String(page.offset + shown)}件目`;
  section.append(tally);

  const list = el("ul", { className: "list" });
  for (const entry of page.entries) {
    const li = el("li");
    li.append(el("span", { text: entry.occurredAt, className: "num" }));
    li.append(
      el("span", {
        text: `${auditTableLabel(entry.tableName)} を ${auditActionLabel(entry.action)}`,
      }),
    );
    li.append(
      el("span", {
        // ★操作者が分からない行を「システム」と言い換えない。分からないと書く
        text: entry.actor ?? "操作者の記録なし",
        className: "amount",
      }),
    );
    if (!entry.storeScoped) {
      li.append(el("small", { text: "店舗の指定なし", className: "muted" }));
    }
    /*
     * ★接続元（A6-2）。**必ず1行を占める。値が無い行にも「記録なし」と書く。**
     *
     *   出さないと、読む人には**記録が無いのか画面が出していないのか**が分からない。
     *   `ip` が入るのは本番だけ（A6-1）で、A6 より前の行は両方 `null`
     *   ——★**過去分が全部「記録なし」になるのは正しい状態。**
     *
     * ★端末は最大 512 文字（`MAX_USER_AGENT_LENGTH`）あるので `<details>` で畳む。
     *   ★1店舗・共有端末（権限マトリクス §5.4）では**接続元が全員同じになりうる**ので、
     *     アドレスだけでは撃ち分けられない。畳んでも、たどれば読める所に置く。
     * ★`el()` は `textContent` で入れる。**申告された文字列を `innerHTML` にしない。**
     */
    const source = el("details", { className: "muted" });
    source.append(el("summary", { text: `接続元：${entry.ip ?? "記録なし"}` }));
    source.append(el("small", { text: `端末：${entry.userAgent ?? "記録なし"}` }));
    li.append(source);
    for (const c of entry.changes) {
      const line = el("small", { className: "muted" });
      const before = c.before ?? "（なし）";
      const after = c.after ?? "（なし）";
      line.textContent =
        entry.action === "UPDATE"
          ? `${c.field}：${before} → ${after}`
          : `${c.field}：${entry.action === "INSERT" ? after : before}`;
      // ★伏せられた値は、伏せてあると分かる形で
      if (isMaskedValue(c.before) || isMaskedValue(c.after)) line.className = "warn";
      li.append(line);
    }
    if (entry.changes.length === 0) {
      li.append(el("small", { text: "見られる変更はありません", className: "muted" }));
    }
    list.append(li);
  }
  section.append(list);

  /* ページ送り。★「次」だけ。戻る導線はブラウザの戻るではなく明示的に置く */
  const nav = el("form");
  if (page.offset > 0) {
    const prev = el("button", { text: "前の50件" });
    prev.type = "button";
    prev.addEventListener("click", () => {
      auditFilter = { ...auditFilter, offset: Math.max(0, page.offset - page.pageSize) };
      void load(me, date, "audit");
    });
    nav.append(prev);
  }
  if (page.offset + shown < page.total) {
    const next = el("button", { text: "次の50件" });
    next.type = "button";
    next.addEventListener("click", () => {
      auditFilter = { ...auditFilter, offset: page.offset + page.pageSize };
      void load(me, date, "audit");
    });
    nav.append(next);
  }
  if (nav.childElementCount > 0) section.append(nav);

  ctx.root.append(section);
}

/**
 * システム状態の自己診断（F-19）。
 *
 * ★**値を出さない。** パラメータの実際の金額は1マスも渡ってこない（サーバ側・絶対ルール1）。
 *   ここは「切れた／切れかける」という事実と、キー名・表示名・期限日だけを並べる。
 */
export function renderSelfDiagnosis(
  ctx: ScreenContext,
  data: SelfDiagnosisResult,
  message?: Notice,
): void {
  const section = el("section");
  section.append(el("h2", { text: "システム状態の自己診断" }));
  if (message !== undefined) section.append(noticeOf(message));

  /*
   * ★件数を必ず出す。「問題なし」だけだと、1件も検査していない状態と見分けが付かない
   *   （`.loop/memory.md`「走った件数と飛ばした件数を分けて聞く」）。
   */
  if (hasNoIssues(data)) {
    section.append(notice("放置している項目はありません（問題なし）", "info"));
  } else {
    if (data.expiringParameters.length > 0) {
      section.append(notice(`${String(data.expiringParameters.length)}件のパラメータが期限切れ／切れかけです`, "error"));
    }
    if (data.pendingDocuments.count > 0) {
      section.append(notice(`確認待ち・未登録の証憑が ${String(data.pendingDocuments.count)}件あります`, "error"));
    }
    if (data.unclosedMonths.length > 0) {
      section.append(notice(`当月より前で締めていない月が ${String(data.unclosedMonths.length)}件あります`, "error"));
    }
    const kintaiNotice = describeKintaiIngest(data.kintaiIngest);
    if (kintaiNotice !== null) {
      section.append(notice(kintaiNotice, "error"));
    }
  }

  const paramCard = el("section", { className: "card" });
  paramCard.append(el("h3", { text: "パラメータの有効期限" }));
  if (data.expiringParameters.length === 0) {
    paramCard.append(el("p", { text: "問題ありません。" }));
  } else {
    const list = el("ul", { className: "list" });
    for (const p of data.expiringParameters) {
      const li = el("li");
      li.append(el("span", { text: p.name }));
      li.append(el("small", { text: describeExpiry(p), className: p.daysRemaining <= 0 ? "warn" : "muted" }));
      list.append(li);
    }
    paramCard.append(list);
  }
  section.append(paramCard);

  const documentCard = el("section", { className: "card" });
  documentCard.append(el("h3", { text: "確認待ち・未登録の証憑" }));
  if (data.pendingDocuments.count === 0) {
    documentCard.append(el("p", { text: "問題ありません。" }));
  } else {
    documentCard.append(
      el("p", {
        text: `${String(data.pendingDocuments.count)}件が確認・登録待ちです（最も古いのは ${data.pendingDocuments.oldestBusinessDate ?? "不明"}）`,
      }),
    );
  }
  section.append(documentCard);

  const closingCard = el("section", { className: "card" });
  closingCard.append(el("h3", { text: "未締めの月" }));
  if (data.unclosedMonths.length === 0) {
    closingCard.append(el("p", { text: "問題ありません。" }));
  } else {
    const list = el("ul", { className: "list" });
    for (const m of data.unclosedMonths) {
      const li = el("li");
      li.append(el("span", { text: m.yearMonth }));
      li.append(
        el("small", {
          text: CLOSING_LABEL[m.status as ClosingStatus] ?? m.status,
          className: "muted",
        }),
      );
      list.append(li);
    }
    closingCard.append(list);
  }
  section.append(closingCard);

  const kintaiCard = el("section", { className: "card" });
  kintaiCard.append(el("h3", { text: "勤怠（kintai）からの取込" }));
  if (!data.kintaiIngest.enabled) {
    kintaiCard.append(el("p", { text: "受け取りは無効です（連携の設定がされていません）。", className: "muted" }));
  } else {
    const kintaiIsIssue = data.kintaiIngest.warning || data.kintaiIngest.heldSessionCount > 0;
    kintaiCard.append(
      el("p", {
        text: `最終受信：${data.kintaiIngest.lastReceivedAt ?? "まだありません"}／保留中のセッション：${String(data.kintaiIngest.heldSessionCount)}件`,
        ...(kintaiIsIssue ? { className: "warn" } : {}),
      }),
    );
    /*
     * ★B3（spec）：件数だけでなく、理由コード×年月ごとの内訳と次の行動を並べる。
     *   氏名・金額は `describeKintaiHeldReason` にも渡していない（渡せる材料がそもそも無い）。
     */
    if (data.kintaiIngest.heldBreakdown.length > 0) {
      const list = el("ul", { className: "list" });
      for (const item of data.kintaiIngest.heldBreakdown) {
        const li = el("li");
        li.append(el("small", { text: describeKintaiHeldReason(item), className: "warn" }));
        list.append(li);
      }
      kintaiCard.append(list);
    }
    // ★氏名は kintai が正（NAME-FROM-KINTAI）。照合をやめた代わりの見張り＝件数だけ出す（氏名は出さない）
    kintaiCard.append(
      el("p", {
        text: `直近30日に kintai から氏名が変わった従業員：${String(data.kintaiIngest.nameChangesLast30Days)}名（初回は仮の氏名から置き換わるので数えられます。心当たりが無い変更は、監査ログの「従業員」で確かめてください）`,
        className: "muted",
      }),
    );
  }
  section.append(kintaiCard);

  /*
   * ★バックアップ状態（要件定義 F-19）はアプリから観測する手段が無いため対象外（TODO ではない。
   *   `src/infra/selfDiagnosis.ts` の注記）。「対象外」を隠さず、理由つきで出す。
   */
  section.append(
    el("p", {
      text: "バックアップ状態：対象外（このシステムからは観測できません。運用側の記録を確認してください）",
      className: "muted",
    }),
  );

  ctx.root.append(section);
}

function renderInventory(
  ctx: ScreenContext,
  inventories: Inventory[],
  ceilings: InventoryCeilings | undefined,
  message?: Notice,
): void {
  const { me, date, locked } = ctx;
  const month = date.slice(0, 7);
  const section = el("section");
  section.append(el("h2", { text: `${month} の棚卸` }));
  if (message !== undefined) section.append(noticeOf(message));

  const list = el("ul", { className: "list" });
  for (const inv of inventories) {
    const li = el("li");
    li.append(el("span", { text: CATEGORY_LABEL[inv.category] }));
    li.append(
      el("span", {
        text: inv.closingAmount === null ? "期末 —（未入力）" : `期末 ${yen(inv.closingAmount)}`,
        className: "amount",
      }),
    );
    li.append(
      el("small", {
        // ★「前月が未入力」と「前月が実額0円」を混ぜない
        text:
          inv.openingAmount === null
            ? "期首 —（前月の期末が未登録）"
            : `期首（前月の期末を繰越）${yen(inv.openingAmount)}`,
        className: "muted",
      }),
    );
    list.append(li);
  }
  section.append(list);

  const form = el("form");
  form.append(
    select(
      "区分",
      "category",
      inventories.map((i) => ({ value: i.category, label: CATEGORY_LABEL[i.category] })),
    ),
  );
  form.append(capped(field("期末棚卸金額", "closingAmount", "0"), ceilings?.closingAmount));

  const submit = el("button", { text: locked ? "変更できません" : "保存する" });
  submit.type = "submit";
  submit.disabled = locked;
  form.append(submit);

  onSubmit(
    form,
    submit,
    (d) =>
      api.putInventory(me.storeId, month, {
        category: String(d.get("category") ?? "FOOD"),
        closingAmount: Number(d.get("closingAmount") ?? 0),
      }),
    (m) => void load(me, date, "inventory", m),
    "保存しました",
  );
  section.append(form);

  /*
   * ★締めを止めるかどうかを**ここで決めない。**
   *   計算定義書 §3.2 と C-10、権限マトリクス §4 が「期末棚卸は月次締めの必須入力」と
   *   すでに決めている。検査の実装は M9 なので、いまは「必須である」ことだけ伝える。
   *   ★以前ここに「未入力のままでも締め処理は止めません」と書いていた。
   *     Q-A（＝期末棚卸の**実額**。移行時のD-08確定用）を「棚卸を実施しているか」と
   *     読み違えて、原典で決着済みの要件を画面文言で覆していた。
   */
  if (inventories.some((i) => i.closingAmount === null)) {
    section.append(
      notice(
        "期末棚卸が未入力の区分があります。月間原価はこの金額で決まります。" +
          "期末棚卸は月次締めの必須入力です（締め時の検査は今後の実装）。",
        "info",
      ),
    );
  }
  ctx.root.append(section);
}

// ---------------------------------------------------------------------
// パラメータ設定（§3.4：manager 以上）

const CHECK_CLASS: Record<MinimumWageCheck["status"], "error" | "info"> = {
  failed: "error",
  // ★skipped を info にしているのは「問題なし」の意味ではない。文言のほうで区別する
  skipped: "info",
  passed: "info",
};

/**
 * 最低賃金の判定（C-14）を描く。
 *
 * ★**`skipped` を「問題ありません」と書かない。** 判定できなかったという意味しかない。
 *   ここを「0件でした」と書くと、時給が1件も入っていない状態が
 *   合格に見える。現行Excelが2019年の実査記録で C-07 を通していたのと同じ形。
 */
/**
 * C-14 のカードから直す手立て（M5c）。**渡さなければカードは読むだけになる。**
 *
 * ★`exclusions` は `check` から引かれていない。判定は判定のまま（AC-16）。
 */
export interface WageFixContext {
  me: Me;
  date: string;
  tab: Tab;
  /**
   * ★その最低賃金が有効になった日。**適用開始日ではない**（適用開始日は `date`）。
   * 判定日より前から上がっていることを利用者に知らせ、そこへ移る導線を出すために持つ。
   */
  minimumWageFrom: string | null;
  exclusions: WageExclusion[];
  /**
   * ★**誰のことかを画面に出すために要る**（AC-8）。
   *
   * C-14 の判定はサーバが従業員IDしか返さない（絶対ルール1。氏名を判定の経路に載せない）。
   * だが「10名ぶんの時給をこれから動かす」確認で、内部連番しか出さないのは
   * **確認できないものを確認させている**のと同じ。
   *
   * ★突き合わせは画面でやる。氏名がそもそも届いているかどうかはサーバが決めており
   *   （`GET /api/staff` の `redactPayroll`）、読めない相手には `displayName` が来ない。
   *   フロントで隠しているのではない（絶対ルール1）。
   */
  staff: Staff[];
}

/*
 * ★2026-08-21（R-05）に `manager` を外した。原典 §3.3「時給マスタ」の write は owner のみ。
 *   ★**読み取りは manager 以上のまま。** この関数は書き込みの出し分けだけに使う。
 *   禁止しているのはサーバ（`staff_wage.write`）と DB（`migrations/0037`）で、ここではない。
 */
const canWriteWage = (me: Me): boolean => me.role === "owner";

/**
 * 従業員IDを、その人だと分かる文字列にする。
 *
 * ★氏名が届いていなければ従業員コードだけ。どちらも無ければ `従業員ID n` に戻す。
 *   **空文字で埋めない**（届いていないことが分からなくなる）。
 */
const staffLabelOf = (staff: Staff[], staffId: number): string => {
  const found = staff.find((s) => s.id === String(staffId));
  if (found === undefined) return `従業員ID ${staffId}`;
  return `${found.staffCode} ${found.displayName ?? "—"}`.trim();
};

/**
 * 「この人は一括改定から外す」の理由入力（M5c AC-14）。**理由は必須。**
 *
 * ★押した行の中で開く。別画面へ飛ばさない——外す相手と不足額を見ながら
 *   理由を書けないと、「とりあえず外す」が起きる。
 */
export function exclusionEditor(fix: WageFixContext, staffId: number): HTMLElement {
  const wrap = el("div", { className: "field" });
  const input = el("input");
  input.type = "text";
  input.placeholder = "外す理由（必須）";
  input.maxLength = 200;
  input.setAttribute(
    "aria-label",
    `${staffLabelOf(fix.staff, staffId)} を一括改定から外す理由`,
  );
  const save = el("button", { text: "外す", className: "danger" });
  save.type = "button";
  const error = notice("", "error");
  error.hidden = true;

  save.addEventListener("click", () => {
    const reason = input.value.trim();
    if (reason === "") {
      error.textContent = "外す理由を入力してください";
      error.hidden = false;
      return;
    }
    save.disabled = true;
    void api
      .setWageExclusion(fix.me.storeId, staffId, reason)
      .then(() => load(fix.me, fix.date, fix.tab, infoNotice("一括改定から外しました")))
      .catch((e: unknown) => {
        save.disabled = false;
        error.textContent = e instanceof ApiError ? e.message : "外せませんでした";
        error.hidden = false;
      });
  });

  /*
   * ★理由に氏名を書かせない（絶対ルール1。`reason` は列名で判定できないので
   *   マスクで落とせず、監査ログにそのまま残る）。
   *
   * ★**placeholder に書いていたが 375px で読めなかった。** 入力欄は 199px しか
   *   幅が無く「外す理由（必須・氏名は」で切れる。しかも placeholder は
   *   1文字打った時点で消えるので、**書いている最中こそ見えない。**
   *   常時見える注記へ移した（2026-08-25 / モバイル検証）。
   */
  const caution = el("p", {
    className: "muted",
    text: "氏名は書かないでください。この理由は監査ログにそのまま残ります。",
  });

  wrap.append(input, caution, save, error);
  return wrap;
}

/**
 * 一括改定の確認（M5c AC-8）。**押した瞬間には実行しない。**
 *
 * 対象人数・改定後の金額・適用開始日を出してから確かめる。
 * 10人ぶんの時給がひと押しで動くので、押し間違いを取り返す手段が要る。
 */
function bulkRaisePanel(
  fix: WageFixContext,
  check: Extract<MinimumWageCheck, { status: "failed" }>,
): HTMLElement {
  const wrap = el("div");
  const excluded = new Set(fix.exclusions.map((e) => e.staffId));
  const targets = check.violations.filter((v) => !excluded.has(v.staffId));
  const skipped = check.violations.length - targets.length;

  if (targets.length === 0) {
    wrap.append(
      el("small", {
        text: "下回っている全員が一括改定から外れています。判定は「下回っている」のままです。",
        className: "muted",
      }),
    );
    return wrap;
  }

  const open = el("button", { text: "最低賃金に合わせて一括改定する" });
  open.type = "button";
  wrap.append(open);

  const confirm = el("div", { className: "card" });
  confirm.hidden = true;
  confirm.append(
    notice(
      `${targets.length}名を ${check.minimumWage.toLocaleString()}円 へ改定します` +
        `${skipped > 0 ? `（外してある ${skipped}名 は改定しません）` : ""}`,
      "info",
    ),
  );
  /*
   * ★**誰を動かすのかを、その人だと分かる形で出す**（AC-8）。
   *   従業員IDだけだと、確認する人が対象を認識できない——
   *   人数と金額しか見ずに押す運用になり、確認が形だけになる。
   */
  const ids = el("ul", { className: "list" });
  for (const t of targets) {
    const row = el("li");
    row.append(el("span", { text: staffLabelOf(fix.staff, t.staffId) }));
    row.append(
      el("span", {
        text: `${t.baseHourlyWage.toLocaleString()}円 → ${check.minimumWage.toLocaleString()}円`,
        className: "amount",
      }),
    );
    ids.append(row);
  }
  confirm.append(ids);

  /*
   * ★適用開始日は**判定に使った日そのもの**（AC-9）。ここで編集させない。
   *
   *   かつては編集できる日付欄を置いていた。日付を動かすと、
   *   **上の一覧（判定日の判定）と、実際に書かれる相手（動かした日の判定）が割れた。**
   *   さらに、判定日の最低賃金が「当時それが最低賃金でなかった期間」へ書かれ、
   *   当時は合法だった時給まで改定された（絶対ルール6違反・security 担当が実測）。
   *
   *   別の日で直すなら、**画面の営業日そのものを動かす**（下の導線）。
   *   日付が1つしか無ければ、判定・確認・書き込みが割れようがない。
   */
  confirm.append(
    el("small", {
      text:
        `適用開始日：${fix.date}（画面上部の営業日）。` +
        "上書きではなく、前日で古い時給を閉じて履歴を積みます。" +
        "1件でも保存できなければ、1件も改定しません。",
      className: "muted",
    }),
  );

  /*
   * ★最低賃金が上がった日より前から直したいなら、その日へ**移ってから**押す。
   *
   * ★「そのあいだは下回ったまま」とは書かない。**その期間を判定していない。**
   *   別の日の判定結果を、確かめずに言い切らない。
   */
  if (fix.minimumWageFrom !== null && fix.minimumWageFrom < fix.date) {
    confirm.append(
      notice(
        `最低賃金 ${check.minimumWage.toLocaleString()}円 は ${fix.minimumWageFrom} から有効です。` +
          `${fix.date} から改定すると、${fix.minimumWageFrom} 〜 ${fix.date} の時給は変わりません。`,
        "info",
      ),
    );
    const jump = el("button", { text: `${fix.minimumWageFrom} で判定し直す` });
    jump.type = "button";
    jump.addEventListener("click", () => {
      void load(fix.me, fix.minimumWageFrom as string, fix.tab);
    });
    confirm.append(jump);
  }

  const error = notice("", "error");
  error.hidden = true;
  const run = el("button", { text: "実行する", className: "primary" });
  run.type = "button";
  const cancel = el("button", { text: "やめる" });
  cancel.type = "button";

  open.addEventListener("click", () => {
    confirm.hidden = false;
    open.hidden = true;
  });
  cancel.addEventListener("click", () => {
    confirm.hidden = true;
    open.hidden = false;
  });
  run.addEventListener("click", () => {
    run.disabled = true;
    void api
      .bulkRaiseWagesToMinimum(fix.me.storeId, fix.date)
      .then((r) =>
        load(
          fix.me,
          fix.date,
          fix.tab,
          infoNotice(
            `${r.result.raisedStaffIds.length}名の時給を ${r.result.validFrom} から ` +
              `${r.result.minimumWage.toLocaleString()}円 へ改定しました`,
          ),
        ),
      )
      .catch((e: unknown) => {
        run.disabled = false;
        error.textContent = e instanceof ApiError ? e.message : "一括改定できませんでした";
        error.hidden = false;
      });
  });

  confirm.append(run, cancel, error);
  wrap.append(confirm);
  return wrap;
}

/**
 * ★**時給を直せない相手に、その場で「誰なら直せるか」を出す。**
 *
 *   ★2026-08-21 の spec 確認担当が見つけた行き止まり。R-05 で時給の write を
 *   `owner` のみへ戻した結果、**最低賃金割れの一覧を見た `manager` が
 *   16名の氏名と不足額を見せられた直後、何もできないまま画面が終わっていた。**
 *   設定タブには汎用の注記が出るが、C-14 のカードから3ブロック離れた場所にあり、
 *   従業員タブには**注記そのものが無かった。**
 *
 *   ★`src/ui/wage-alert-view.ts` の intent は「気づく仕組みが壊れたことにも気づけること」。
 *   気づいた人が行き止まりで終わるなら、その intent は半分しか届いていない。
 *   **警告を出す相手と、直せる相手が違うなら、その差をその場で書く。**
 *
 *   ★氏名は載せない（絶対ルール1）。役職だけで書く。
 */
function ownerOnlyWageNote(next: "伝える" | "登録を依頼"): HTMLElement {
  return el("small", {
    text:
      "時給の登録・改定と最低賃金の変更ができるのはオーナーのみです。" +
      (next === "伝える"
        ? "この一覧をオーナーへ伝えてください。"
        : "オーナーへ時給の登録を依頼してください。"),
    className: "muted",
  });
}

function minimumWageCard(check: MinimumWageCheck, fix?: WageFixContext): HTMLElement {
  const box = el("section", { className: "card" });
  box.append(el("h3", { text: "最低賃金の判定" }));

  if (check.status === "skipped") {
    box.append(notice(`判定できません — ${check.reason}`, CHECK_CLASS.skipped));
    box.append(
      el("small", {
        text: "「問題なし」ではありません。判定の材料が揃っていない状態です。",
        className: "muted",
      }),
    );
    /*
     * ★次に何をすればよいかを書く。**「判定できません」で止めない。**
     *   材料が揃っていないと分かっても、揃え方が分からなければ画面は行き止まりになる。
     *   （タブを開かずに気づく経路＝push 型の警告は M5b。これはもう開いている人の話）
     */
    if (fix !== undefined && canWriteWage(fix.me)) {
      box.append(
        el("small", {
          text:
            check.minimumWage === null
              ? "設定タブで最低賃金を登録すると判定できます。"
              : "下の一覧から時給を登録すると判定できます。",
          className: "muted",
        }),
      );
    } else {
      box.append(ownerOnlyWageNote("登録を依頼"));
    }
    return box;
  }

  const min = `${check.minimumWage.toLocaleString()}円`;
  if (check.status === "passed") {
    box.append(
      notice(`${check.checked}件の時給すべてが最低賃金 ${min} 以上です`, CHECK_CLASS.passed),
    );
    return box;
  }

  box.append(
    notice(
      `${check.checked}件のうち ${check.violations.length}件 が最低賃金 ${min} を下回ります`,
      CHECK_CLASS.failed,
    ),
  );

  const writable = fix !== undefined && canWriteWage(fix.me);
  const excludedBy = new Map((fix?.exclusions ?? []).map((e) => [e.staffId, e]));

  const list = el("ul", { className: "list" });
  for (const v of check.violations) {
    const li = el("li");
    /*
     * ★サーバは従業員IDしか返していない（絶対ルール1。判定の経路に氏名を載せない）。
     *   ここで従業員一覧と突き合わせて、**誰のことかが分かる形**にする。
     *   氏名が届いていない相手には従業員コードだけが出る。
     */
    li.append(el("span", { text: staffLabelOf(fix?.staff ?? [], v.staffId) }));
    li.append(el("span", { text: `${v.baseHourlyWage.toLocaleString()}円`, className: "amount" }));
    li.append(el("small", { text: `あと ${v.shortfall.toLocaleString()}円`, className: "muted" }));

    const excluded = excludedBy.get(v.staffId);
    if (excluded !== undefined) {
      /*
       * ★外してあることと、その理由・外した日を出す（AC-15）。
       *   **判定からは外れていない**ので、この行は上の failed の件数に入ったまま。
       */
      li.append(
        el("small", {
          text: `一括改定から除外中 — ${excluded.reason}（${excluded.excludedAt}）`,
          className: "muted",
        }),
      );
      if (writable && fix !== undefined) {
        const undo = el("button", { text: "除外を取り消す", className: "danger" });
        undo.type = "button";
        undo.addEventListener("click", () => {
          undo.disabled = true;
          void api
            .setWageExclusion(fix.me.storeId, v.staffId, null)
            .then(() => load(fix.me, fix.date, fix.tab, infoNotice("除外を取り消しました")))
            .catch((e: unknown) => {
              undo.disabled = false;
              void load(fix.me, fix.date, fix.tab, errorNotice(e instanceof ApiError ? e.message : "除外を取り消せませんでした"));
            });
        });
        li.append(undo);
      }
    } else if (writable && fix !== undefined) {
      li.append(exclusionEditor(fix, v.staffId));
    }
    list.append(li);
  }
  box.append(list);

  if (writable && fix !== undefined) {
    box.append(bulkRaisePanel(fix, check));
    box.append(
      el("small", {
        text:
          "外せるのは「今回の一括改定の対象」からだけです。" +
          "最低賃金の判定からは外れません（下回っている件数は変わりません）。",
        className: "muted",
      }),
    );
  } else {
    box.append(ownerOnlyWageNote("伝える"));
  }
  return box;
}

// 従業員・時給（§3.3：read は manager 以上／write は氏名も時給も owner のみ。★R-05）

const EMPLOYMENT_LABEL: Record<Staff["employmentType"], string> = {
  PART_TIME: "アルバイト",
  FULL_TIME: "社員",
};

/**
 * 紐付けの履歴（折りたたみ・§3.6・R0102 A-2 周回2 advisory）。
 *
 * ★**開いてから読み込む。** 20名分を先読みすると、設定タブを開くだけで
 *   従業員の人数ぶん `getStaffLinks` を呼ぶことになる（`<details>` の `toggle` で遅延）。
 * ★取り消し（void）は owner のみ。manager には履歴の閲覧だけを出す
 *   （ボタンを出さない。禁止そのものはサーバ側・`ROUTE_PERMISSIONS` の `staff_link.write`）。
 */
function staffLinkHistoryFold(me: Me, date: string, staffId: string, canVoid: boolean): HTMLElement {
  const fold = el("details", { className: "muted" });
  fold.append(el("summary", { text: "紐付けの履歴" }));
  const body = el("div");
  fold.append(body);

  let loaded = false;
  fold.addEventListener("toggle", () => {
    if (!fold.open || loaded) return;
    loaded = true;
    void api
      .getStaffLinks(me.storeId, staffId)
      .then(({ links }) => body.replaceChildren(...staffLinkHistoryRows(me, date, links, canVoid)))
      .catch((error: unknown) => {
        loaded = false;
        body.replaceChildren(notice(error instanceof ApiError ? error.message : "読み込めませんでした"));
      });
  });

  return fold;
}

function staffLinkHistoryRows(
  me: Me,
  date: string,
  links: StaffAccountLink[],
  canVoid: boolean,
): HTMLElement[] {
  if (links.length === 0) return [el("small", { text: "履歴はまだありません", className: "muted" })];

  const list = el("ul", { className: "list" });
  for (const link of links) {
    const row = el("li");
    row.append(
      el("small", {
        text:
          `${link.loginId}：${link.linkedAt.slice(0, 10)} 〜 ` +
          `${link.unlinkedAt?.slice(0, 10) ?? "（紐付け中）"}` +
          (link.voided ? "（取り消し済み）" : ""),
        className: "muted",
      }),
    );
    // ★取り消せるのは「解除済みで・まだ取り消していない」窓だけ（先に解除。migrations/0047 の CHECK）
    if (canVoid && link.unlinkedAt !== null && !link.voided) {
      row.append(voidLinkForm(me, date, link.id));
    }
    list.append(row);
  }
  return [list];
}

/** 誤った紐付けの訂正（紐付けの取り消し）の理由入力（§2.1「窓の取り消し」）。**理由は必須**（AC-13） */
function voidLinkForm(me: Me, date: string, linkId: string): HTMLFormElement {
  const form = el("form", { className: "field" });
  const input = el("input");
  input.type = "text";
  input.name = "reason";
  input.placeholder = "取り消す理由（必須）";
  input.maxLength = 200;
  form.append(input);
  const submit = el("button", { text: "取り消す", className: "danger" });
  submit.type = "submit";
  form.append(submit);
  onSubmit(
    form,
    submit,
    (d) => api.voidStaffLink(me.storeId, linkId, String(d.get("reason") ?? "").trim()),
    (m) => void load(me, date, "staff", m),
    "紐付けを取り消しました",
  );
  return form;
}

/**
 * 従業員と時給。
 *
 * ★**氏名（従業員マスタ）も時給も `owner` だけが書ける**
 *   （権限マトリクス §3.3。2026-08-18 に時給だけ広げ、2026-08-21 の R-05 で戻した）。
 *   ★**§3.3 の別の行なので、いま同じ広さでも画面では別の変数で判定する。**
 *   隠すのは使いやすさのためで、担保はサーバ側。
 *
 * ★氏名は `canViewPayroll` の相手にしか**届いていない**。
 *   届いていないものを画面で埋めない（`—` と出す）。
 */
function renderStaff(
  ctx: ScreenContext,
  staff: Staff[],
  wages: WagesResult,
  accounts: Account[],
  anomalies: AttendanceAnomaly[],
  message?: Notice,
): void {
  const { me, date } = ctx;
  /** ★氏名（§3.3「従業員マスタ」）は owner のみ。時給の広さと混ぜない */
  const canWriteStaff = me.role === "owner";
  const canWriteWages = canWriteWage(me);
  const section = el("section");
  section.append(el("h2", { text: `${date} 時点の従業員` }));
  if (message !== undefined) section.append(noticeOf(message));

  /*
   * ★docs/02 §4.3「検出して出す」の宛先（権限マトリクス §3.6・R0102 A-3）。
   *   manager 以上はここで店舗全員分を見る（`(個)` の絞り込みはサーバ側）。
   *   **判断（出す／出さない・文言）は `src/ui/attendance-anomaly-view.ts` に置く。**
   *   0件なら何も足さない（沈黙ではなく、上の判定関数が空配列を返す設計）。
   */
  const anomalyLines = attendanceAnomalyLines(anomalies);
  if (anomalyLines.length > 0) {
    const box = notice(attendanceAnomalyHeading(anomalies), "error");
    const list = el("ul", { className: "list" });
    for (const line of anomalyLines) list.append(el("li", { text: line }));
    box.append(list);
    section.append(box);
  }

  // ★C-14。時給が0件なら「判定不能」と出る（`minimumWageCard` が文言で区別する）
  section.append(
    minimumWageCard(wages.minimumWage, {
      me,
      date,
      tab: "staff",
      minimumWageFrom: wages.minimumWageFrom,
      exclusions: wages.exclusions,
      staff,
    }),
  );

  if (staff.length === 0) {
    section.append(
      notice(
        canWriteStaff
          ? "従業員がまだ登録されていません。下のフォームから登録してください。"
          : "従業員がまだ登録されていません。登録できるのはオーナーのみです。",
        "info",
      ),
    );
  }

  const effective = new Map(wages.wages.map((w) => [String(w.staffId), w.baseHourlyWage]));
  const historyOf = (id: string): typeof wages.history =>
    wages.history.filter((h) => String(h.staffId) === id);

  const list = el("ul", { className: "list" });
  for (const s of staff) {
    const li = el("li");
    li.append(
      el("span", {
        // ★氏名が返っていない＝読む権限が無い。空文字で埋めず、届いていないことを出す
        text: `${s.staffCode} ${s.displayName ?? "—"}`,
      }),
    );
    const wage = effective.get(s.id);
    li.append(
      el("span", {
        text: wage === undefined ? "時給 未登録" : `${wage.toLocaleString()}円`,
        className: "amount",
      }),
    );
    const past = historyOf(s.id);
    li.append(
      el("small", {
        text:
          `${EMPLOYMENT_LABEL[s.employmentType]}` +
          `${s.isActive ? "" : ` ／ 退職 ${s.retiredOn ?? ""}`}` +
          `${past.length > 0 ? ` ／ 履歴 ${past.length}件` : ""}`,
        className: "muted",
      }),
    );

    // 履歴。★上書きしていないことを、画面で確かめられるようにする
    if (past.length > 0) {
      const hist = el("ul", { className: "list" });
      for (const h of past) {
        const row = el("li");
        row.append(
          el("small", {
            text: `${h.validFrom} 〜 ${h.validTo ?? "（次の改定まで）"}`,
            className: "muted",
          }),
        );
        row.append(
          el("span", {
            text: h.baseHourlyWage === undefined ? "—" : `${h.baseHourlyWage.toLocaleString()}円`,
            className: "amount",
          }),
        );
        if (h.reason !== null) row.append(el("small", { text: h.reason, className: "muted" }));
        hist.append(row);
      }
      li.append(hist);
    }

    // ★退職日は従業員マスタ（氏名の行）。owner のみ
    if (canWriteStaff) {
      // ★退職にする＝マイナスの操作（docs/10 §4・依頼者 2026-09-15）。在籍に戻すは安全側なので付けない
      const toggle = el("button", {
        text: s.isActive ? "退職にする" : "在籍に戻す",
        ...(s.isActive ? { className: "danger" } : {}),
      });
      toggle.type = "button";
      toggle.addEventListener("click", () => {
        toggle.disabled = true;
        void api
          .setStaffRetirement(me.storeId, s.id, s.isActive ? date : null)
          .then(() => load(me, date, "staff", infoNotice(s.isActive ? "退職日を登録しました" : "在籍に戻しました")))
          .catch((error: unknown) => {
            toggle.disabled = false;
            void load(me, date, "staff", errorNotice(error instanceof ApiError ? error.message : "変更できませんでした"));
          });
      });
      li.append(toggle);
    }

    // ---- 紐付け（§3.6「従業員とアカウントの紐付け」・R0102 A-2） ----
    /** ★氏名・時給は含まない。この店舗の login_id と、誰に紐付け済みかだけ */
    const linkedAccount = accounts.find((a) => a.staffId === s.id) ?? null;
    if (canWriteStaff) {
      // ★選べるのは「未紐付けのアカウント」＋「いまこの従業員に紐付けられているアカウント」
      const choices = accounts.filter((a) => a.staffId === null || a.staffId === s.id);
      const linkForm = el("form");
      linkForm.append(
        select(
          "紐付けるアカウント",
          "userId",
          [
            { value: "", label: "（未紐付けにする）" },
            /*
             * ★退職済み（`isActive: false`）のアカウントにも印を付ける（advisory）。
             *   選んでも `setStaffAccountLink` が拒む——ここは「いま紐付けられている相手が
             *   実は退職済み」を見た目で分かるようにするだけ
             */
            ...choices.map((a) => ({
              value: a.id,
              label: a.isActive ? a.loginId : `${a.loginId}（退職）`,
            })),
          ],
          linkedAccount?.id ?? "",
        ),
      );
      const saveLink = el("button", { text: "紐付けを保存" });
      saveLink.type = "submit";
      linkForm.append(saveLink);
      onSubmit(
        linkForm,
        saveLink,
        (d) => api.setStaffLink(me.storeId, s.id, String(d.get("userId") ?? "").trim() || null),
        (m) => void load(me, date, "staff", m),
        "紐付けを保存しました",
      );
      li.append(linkForm);
    } else if (me.role === "manager") {
      // ★manager は状態の閲覧のみ（操作は owner のみ。権限マトリクス §3.6）
      li.append(
        el("small", {
          text: linkedAccount === null ? "紐付け: 未紐付け" : `紐付け: ${linkedAccount.loginId}`,
          className: "muted",
        }),
      );
    }
    // ★履歴の閲覧は manager 以上（§3.6「紐付けの履歴」）。取り消しは owner のみ（周回2 AC-13）
    li.append(staffLinkHistoryFold(me, date, s.id, canWriteStaff));

    list.append(li);
  }
  section.append(list);

  if (!canWriteStaff) {
    section.append(
      el("small", {
        text:
          "従業員の登録と退職（氏名）を扱えるのはオーナーのみです。" +
          (canWriteWages ? "時給の登録・改定は店長以上が行えます。" : ""),
        className: "muted",
      }),
    );
  } else {
  // ---- 従業員を登録する ----

  const staffForm = el("form");
  staffForm.append(el("h3", { text: "従業員を登録する" }));
  staffForm.append(field("従業員コード", "staffCode", "", "text"));
  staffForm.append(field("氏名", "displayName", "", "text"));
  // ★氏名は本人が kintai で決め、勤務と一緒に届いた時点で置き換わる（NAME-FROM-KINTAI・docs/18 §2）
  staffForm.append(
    el("p", { className: "muted", text: "仮の氏名で構いません。本人が勤怠（kintai）で決めた氏名が、最初の勤務が届いた時点で置き換わります" }),
  );
  staffForm.append(select("雇用形態", "employmentType", optionsOf(EMPLOYMENT_LABEL)));
  staffForm.append(field("入社日（任意）", "hiredOn", "", "date"));
  const addStaff = el("button", { text: "従業員を登録する" });
  addStaff.type = "submit";
  staffForm.append(addStaff);
  onSubmit(
    staffForm,
    addStaff,
    (d) =>
      api.addStaff(me.storeId, {
        staffCode: String(d.get("staffCode") ?? "").trim(),
        displayName: String(d.get("displayName") ?? "").trim(),
        employmentType: String(d.get("employmentType") ?? ""),
        hiredOn: String(d.get("hiredOn") ?? "") || null,
      }),
    (m) => void load(me, date, "staff", m),
    "従業員を登録しました",
  );
  section.append(staffForm);
  }

  // ---- 時給を改定する（★owner のみ。氏名の登録とは別の行・R-05） ----

  if (canWriteWages && staff.length > 0) {
    const wageForm = el("form");
    wageForm.append(el("h3", { text: "時給を登録・改定する" }));
    wageForm.append(
      select(
        "従業員",
        "staffId",
        staff.map((s) => ({ value: s.id, label: `${s.staffCode} ${s.displayName ?? ""}`.trim() })),
      ),
    );
    wageForm.append(capped(field("時給（円）", "baseHourlyWage"), wages.maxHourlyWage));
    wageForm.append(field("適用開始日", "validFrom", date, "date"));
    wageForm.append(field("理由（任意）", "reason", "", "text"));
    const saveWage = el("button", { text: "時給を保存する" });
    saveWage.type = "submit";
    wageForm.append(saveWage);
    wageForm.append(
      el("small", {
        text:
          "上書きではありません。適用開始日の前日で古い時給を閉じ、履歴として積みます。" +
          "締め済みの月に有効な時給が変わる登録は保存できません。",
        className: "muted",
      }),
    );
    onSubmit(
      wageForm,
      saveWage,
      (d) =>
        api.reviseWage(me.storeId, {
          staffId: Number(d.get("staffId")),
          baseHourlyWage: Number(d.get("baseHourlyWage")),
          validFrom: String(d.get("validFrom") ?? ""),
          reason: String(d.get("reason") ?? "").trim() || null,
        }),
      (m) => void load(me, date, "staff", m),
      "時給を保存しました",
    );
    section.append(wageForm);
  }

  ctx.root.append(section);
}

/**
 * ログインアカウントの棚卸（F-21 / 権限マトリクス §3.5）。
 *
 * ★**出す／出さないの判断はここに書かない。** `src/ui/account-review-view.ts` が決め、
 *   ここはその結果を DOM へ写すだけ。判断を両方に置くと、置いた側が無検査になる
 *   （`.loop/memory.md`「判定を各所に書くと、書き忘れた場所が『制限なし』になる」）。
 */
export function accountReviewCard(ctx: ScreenContext, accounts: Account[]): HTMLElement {
  const { me, date } = ctx;
  /** ★止められるのは owner だけ（§3.5）。担保はサーバ側で、ここは見た目の都合 */
  const canWrite = me.role === "owner";
  /*
   * ★ロックの解除は原典 §3.5 の**別の行**（「ユーザー・権限管理」= owner のみ）。
   *   今日は `canWrite` と同じ値になるが、**同じ変数を使い回さない。**
   *   `account_review` は manager に `R` があり、片方だけが動く日が来る
   *   （`.loop/memory.md`「原典の複数の行を、1つのリソースに束ねない」）。
   */
  const canUnlock = me.role === "owner";
  /*
   * ★**権限ではない**（S5 周回3）。保守契約の当事者か＝**連絡先を知っているか。**
   *   店長は保守の担当者を知る手段がシステム内に無いので、伝える先が変わる（原典 §3.5）。
   *   ★今日は上の2つと同じ値になるが、**性質が違うので束ねない。**
   */
  const isOwner = me.role === "owner";
  const card = el("section", { className: "card" });
  card.append(el("h3", { text: "ログインアカウントの棚卸" }));

  const showTemporaryPassword = async (
    temporaryPassword: string,
    initialResult: HTMLParagraphElement,
  ): Promise<void> => {
    initialResult.className = "notice info";
    initialResult.setAttribute("role", "status");
    initialResult.textContent =
      `一時パスワード：${temporaryPassword}（この表示は一度だけです）`;
    initialResult.hidden = false;

    try {
      const { accounts: latest } = await api.getAccounts(me.storeId);
      const refreshed = accountReviewCard(ctx, latest);
      // ★受け渡し前に別操作で平文表示を消さない。明示的に消す操作だけを残す
      for (const control of refreshed.querySelectorAll<HTMLInputElement | HTMLButtonElement | HTMLSelectElement>(
        "input, button, select",
      )) {
        control.disabled = true;
      }
      const holder = el("div", { className: "card" });
      const secret = notice(
        `一時パスワード：${temporaryPassword}（この表示は一度だけです）`,
        "info",
      );
      const clear = el("button", { text: "一時パスワードの表示を消す" });
      clear.type = "button";
      clear.disabled = false;
      clear.addEventListener("click", () => {
        // ★共有端末のDOMへ平文を残さない。消した値は再取得できない
        secret.textContent = "";
        holder.remove();
        void load(me, date, "settings", infoNotice("一時パスワードの表示を消しました"));
      });
      holder.append(
        secret,
        el("small", {
          text: "本人へ安全に渡したら表示を消してください。消した後は再表示できません。",
          className: "muted",
        }),
        clear,
      );
      refreshed.prepend(holder);
      card.replaceWith(refreshed);
    } catch {
      // ★発行済みの平文を消さない。一覧更新だけ失敗したことを同じ場で伝える
      initialResult.textContent += " 一覧の更新に失敗しました。受け渡し後に画面を読み直してください。";
    }
  };

  const banner = accountReviewBanner(accounts, canWrite);
  card.append(notice(banner.text, banner.kind === "clear" ? "info" : "error"));
  /*
   * ★**旗の有無に関わらず出す**（S5 周回2 / spec blocking）。
   *   「0件」を「壊れていない」と読ませない。いちばん危ないのは
   *   **壊れているのに旗が立たない**ときで、そのとき条件付きの注記は一緒に消える。
   */
  card.append(el("small", { text: brokenHashCaveat(isOwner), className: "muted" }));

  if (isOwner) {
    const form = el("form");
    form.append(el("h4", { text: "ログインアカウントを作成する" }));
    form.append(field("ログインID", "loginId", "", "text"));
    form.append(field("表示名", "displayName", "", "text"));
    form.append(
      select("初期ロール", "role", [
        { value: "staff", label: "スタッフ" },
        { value: "leader", label: "社員・リーダー" },
        { value: "manager", label: "店長" },
        { value: "owner", label: "オーナー" },
      ]),
    );
    const submit = el("button", { text: "アカウントを作成する" });
    submit.type = "submit";
    const result = notice("", "info");
    result.hidden = true;
    form.append(submit, result);
    form.append(
      el("small", {
        text:
          "一時パスワードは作成後に一度だけ表示します。閉じる前に本人へ安全に渡してください。" +
          "オーナーを選ぶと、あなたがオーナーである全店舗の保守用オーナーとして作成します。",
        className: "muted",
      }),
    );
    // ★`onSubmit` を通らない保存フォーム。未保存の見張りへは手で登録する（`guardForm` の説明）
    guardForm(form);
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      submit.disabled = true;
      result.hidden = true;
      const data = new FormData(form);
      const role = String(data.get("role") ?? "staff") as NonNullable<Account["role"]>;
      void api
        .createAccount(me.storeId, {
          loginId: String(data.get("loginId") ?? "").trim(),
          displayName: String(data.get("displayName") ?? "").trim(),
          role,
        })
        .then(({ temporaryPassword }) => {
          form.reset();
          submit.hidden = true;
          void showTemporaryPassword(temporaryPassword, result);
        })
        .catch((error: unknown) => {
          result.textContent = error instanceof ApiError ? error.message : "作成できませんでした";
          result.className = "notice error";
          result.setAttribute("role", "alert");
          result.hidden = false;
          submit.disabled = false;
        });
    });
    card.append(form);
  }

  const list = el("ul", { className: "list" });
  for (const row of accountRowViews(accounts, {
    canWrite,
    canUnlock,
    isOwner,
    selfLoginId: me.loginId,
  })) {
    const li = el("li");
    li.append(el("span", { text: row.headline }));
    li.append(el("span", { text: row.roleLabel, className: "amount" }));
    li.append(el("small", { text: row.detail, className: "muted" }));

    const id = accounts.find((a) => a.loginId === row.loginId)?.id ?? "";
    const apply = (active: boolean, button: HTMLButtonElement, onError: (m: string) => void): void => {
      button.disabled = true;
      void api
        .setAccountActive(me.storeId, id, active)
        .then(() =>
          load(me, date, "settings", infoNotice(`${row.loginId} のアカウントを${active ? "戻し" : "止め"}ました`)),
        )
        .catch((e: unknown) => {
          button.disabled = false;
          onError(e instanceof ApiError ? e.message : "変更できませんでした");
        });
    };

    /*
     * ★**ロックの表示は `action` と独立に出す。** 止める／戻すの対象かどうかと、
     *   いま入れないかどうかは別の話（止まっている人がロックされていることもある）。
     * ★`manager` にはここで「解除できるのはオーナーのみ」が出る。**この行の中に**出すのが要点で、
     *   カードの末尾の注記では「今この人をどうすればいいか」への答えにならない（R-05）。
     */
    /*
     * ★**ロックの上に出す**（S5）。ロックと両方立つことがあり、
     *   そのとき「待てば入れる」ほうを先に読ませない。**待っても直らないのはこちら。**
     */
    if (row.brokenHash !== null) {
      li.append(notice(row.brokenHash.text, "error"));
    }

    if (row.lock !== null) {
      li.append(notice(row.lock.text, "error"));
      if (row.lock.canUnlock) {
        const unlock = el("button", { text: "ロックを解除する" });
        unlock.type = "button";
        // ★確認を挟まない。戻す側＝安全側（「戻す」ボタンと同じ扱い）
        unlock.addEventListener("click", () => {
          unlock.disabled = true;
          void api
            .unlockAccount(me.storeId, id)
            .then(() => load(me, date, "settings", infoNotice(`${row.loginId} のロックを解除しました`)))
            .catch((e: unknown) => {
              unlock.disabled = false;
              void load(
                me,
                date,
                "settings",
                errorNotice(e instanceof ApiError ? e.message : "解除できませんでした"),
              );
            });
        });
        li.append(unlock);
      }
    }

    if (row.canChangeRole) {
      const current = accounts.find((a) => a.loginId === row.loginId)?.role ?? null;
      const roleField = select(
        "ロール",
        "role",
        (Object.keys(ROLE_LABEL) as NonNullable<Account["role"]>[]).map((value) => ({
          value,
          label: ROLE_LABEL[value],
        })),
        current ?? undefined,
      );
      const roleNode = roleField.querySelector("select") as HTMLSelectElement;
      roleNode.setAttribute("aria-label", `${row.loginId} のロール`);
      const openRole = el("button", { text: "ロールを変更する" });
      openRole.type = "button";
      const roleConfirm = el("div", { className: "card" });
      roleConfirm.hidden = true;
      const roleConfirmText = notice("", "info");
      const runRole = el("button", { text: "変更する", className: "primary" });
      runRole.type = "button";
      const cancelRole = el("button", { text: "やめる" });
      cancelRole.type = "button";
      const roleError = notice("", "error");
      roleError.hidden = true;
      openRole.addEventListener("click", () => {
        const to = roleNode.value as NonNullable<Account["role"]>;
        if (to === current) {
          roleError.textContent = "いまと同じロールです。変更後のロールを選んでください";
          roleError.hidden = false;
          return;
        }
        roleError.hidden = true;
        roleConfirmText.textContent = roleChangeConfirmText(row, current, to);
        openRole.hidden = true;
        roleNode.disabled = true;
        roleConfirm.hidden = false;
      });
      cancelRole.addEventListener("click", () => {
        openRole.hidden = false;
        roleNode.disabled = false;
        roleConfirm.hidden = true;
      });
      runRole.addEventListener("click", () => {
        const to = roleNode.value as NonNullable<Account["role"]>;
        runRole.disabled = true;
        void api
          .changeAccountRole(me.storeId, id, to)
          .then(() =>
            load(me, date, "settings", infoNotice(`${row.loginId} のロールを「${ROLE_LABEL[to]}」に変更しました`)),
          )
          .catch((e: unknown) => {
            runRole.disabled = false;
            roleError.textContent = e instanceof ApiError ? e.message : "変更できませんでした";
            roleError.hidden = false;
          });
      });
      roleConfirm.append(roleConfirmText, runRole, cancelRole);
      li.append(roleField, openRole, roleConfirm, roleError);
    }

    if (row.canResetPassword) {
      const openReset = el("button", { text: "一時パスワードを再発行する" });
      openReset.type = "button";
      openReset.setAttribute("aria-label", `${row.loginId} の一時パスワードを再発行する`);
      const resetConfirm = el("div", { className: "card" });
      resetConfirm.hidden = true;
      resetConfirm.append(
        notice(
          `${row.headline}の既存セッションをすべて終了し、一時パスワードを再発行します。`,
          "info",
        ),
      );
      const runReset = el("button", { text: "再発行する", className: "primary" });
      runReset.type = "button";
      const cancelReset = el("button", { text: "やめる" });
      cancelReset.type = "button";
      const resetResult = notice("", "info");
      resetResult.hidden = true;
      openReset.addEventListener("click", () => {
        openReset.hidden = true;
        resetConfirm.hidden = false;
      });
      cancelReset.addEventListener("click", () => {
        openReset.hidden = false;
        resetConfirm.hidden = true;
      });
      runReset.addEventListener("click", () => {
        runReset.disabled = true;
        resetResult.hidden = true;
        void api
          .resetAccountPassword(me.storeId, id)
          .then(({ temporaryPassword }) => {
            runReset.hidden = true;
            cancelReset.hidden = true;
            void showTemporaryPassword(temporaryPassword, resetResult);
          })
          .catch((error: unknown) => {
            resetResult.textContent =
              error instanceof ApiError ? error.message : "再発行できませんでした";
            resetResult.className = "notice error";
            resetResult.setAttribute("role", "alert");
            resetResult.hidden = false;
            runReset.disabled = false;
          });
      });
      resetConfirm.append(runReset, cancelReset, resetResult);
      li.append(openReset, resetConfirm);
    }

    if (row.action === "stop") {
      /*
       * ★止めるほうだけ確認を挟む（AC-18）。戻すほうは安全側なので挟まない。
       */
      const open = el("button", { text: "このアカウントを止める", className: "danger" });
      open.type = "button";
      const confirm = el("div", { className: "card" });
      confirm.hidden = true;
      confirm.append(notice(stopConfirmText(row), "info"));
      const run = el("button", { text: "止める", className: "danger primary" });
      run.type = "button";
      const cancel = el("button", { text: "やめる" });
      cancel.type = "button";
      const error = notice("", "error");
      error.hidden = true;

      open.addEventListener("click", () => {
        confirm.hidden = false;
        open.hidden = true;
      });
      cancel.addEventListener("click", () => {
        confirm.hidden = true;
        open.hidden = false;
      });
      run.addEventListener("click", () => {
        apply(false, run, (m) => {
          error.textContent = m;
          error.hidden = false;
        });
      });
      confirm.append(run, cancel, error);
      li.append(open, confirm);
    } else if (row.action === "restore") {
      const back = el("button", { text: "戻す" });
      back.type = "button";
      back.addEventListener("click", () => {
        apply(true, back, (m) => void load(me, date, "settings", errorNotice(m)));
      });
      li.append(back);
    }
    list.append(li);
  }
  card.append(list);

  card.append(
    el("small", {
      text:
        "「最終ログインから何日で放置」という基準は決めていません（未確定事項）。" +
        "最終ログインの古い順に並べています。",
      className: "muted",
    }),
  );
  return card;
}

/**
 * 固定費の科目マスタと月額（§3.3 の2行 ／ `docs/02` §6.1・§6.2。M5-FIXED-COST）。
 *
 * ★**対象月はヘッダの「基準日」の月**。目標（`targetsCard`）と同じ約束で、
 *   `date` に3つ目の意味を持たせない。
 *
 * ★**読めるのは manager 以上、書けるのは owner だけ**（原典 §3.3）。
 *   ここの出し分けは見た目の都合でしかなく、担保はサーバと DB にある。
 * ★**締め状態でボタンを止めない**のは目標・パラメータと同じ理由——
 *   どの月なら通るかは DB のトリガ（`migrations/0018`）が決め、弾かれたら本文で理由が返る。
 * ★**科目の変更・削除の口を作らない。** `alloc_method` は適用期間を持たないので、
 *   直せる口を作ると締めた月の日次営業利益が静かに変わる
 *   （`.loop/issues/ALLOC-METHOD-PERIOD.md`）。
 */
export function fixedCostCard(
  ctx: ScreenContext,
  month: string,
  fixedCosts: FixedCost[],
  /** ★書ける人にだけ返る（`undefined` なら `maxlength` は付かない） */
  ceilings: FixedCostCeilings | undefined,
): HTMLElement {
  const { me, date } = ctx;
  const canWrite = me.role === "owner";
  const rows = fixedCostRows(fixedCosts);
  const card = el("section", { className: "card" });
  card.append(el("h3", { text: `${month} の固定費` }));
  card.append(
    el("p", {
      className: "muted",
      text: "対象月は上の基準日から決まります。別の月を入れるときは基準日を動かしてください。",
    }),
  );
  card.append(el("p", { className: "muted", text: fixedCostSummary(rows) }));

  const list = el("ul", { className: "list" });
  for (const r of rows) {
    const li = el("li");
    li.append(el("span", { text: r.name }));
    li.append(
      el("span", {
        // ★「未入力」と「0円」を混ぜない
        text: r.note === null ? yen(r.value) : `—（${r.note}）`,
        className: "amount",
      }),
    );
    li.append(el("small", { className: "muted", text: r.allocLabel }));
    if (r.hint !== null) li.append(el("small", { className: "muted", text: r.hint }));
    list.append(li);
  }
  if (rows.length > 0) card.append(list);

  if (!canWrite) {
    card.append(
      el("p", {
        className: "muted",
        text: "科目の登録と月額の入力ができるのはオーナーだけです。",
      }),
    );
    return card;
  }

  // ---- 月額（★手入力できる科目だけを選ばせる） ----

  const editable = rows.filter((r) => r.editable);
  if (editable.length > 0) {
    const amountForm = el("form");
    amountForm.append(el("h3", { text: "月額を入力する" }));
    amountForm.append(
      select(
        "科目",
        "accountId",
        editable.map((r) => ({ value: r.id, label: r.name })),
      ),
    );
    /*
     * ★★**空欄のまま送らせない。** `Number("")` は **0** なので、欄を空にして押すと
     *   既に入っている月額を **0 で上書きする**（2026-09-10・code 確認が実測）。
     *   ★「未入力（`null`）と 0円 を分ける」というこの課題の原則を、画面の1箇所だけが破っていた。
     *     `fixedCosts.ts` も `fixed-cost-view.ts` もその区別を最重要と書いており、
     *     C-01 は**行が無い科目を数えない**ので、この1箇所が集計の正しさに直結する。
     * ★`required` はブラウザに止めさせる分。**空白だけの入力は `required` を素通りする**ので、
     *   下の送信側で数値に化かさず送り、サーバの `assertAmount` に断らせる（最後の砦はあちら）。
     */
    const amountField = capped(field("月額（円）", "amount", ""), ceilings?.amount);
    const amountInput = amountField.querySelector("input");
    if (amountInput !== null) amountInput.required = true;
    amountForm.append(amountField);
    const amountSubmit = el("button", { text: "月額を保存する" });
    amountSubmit.type = "submit";
    amountForm.append(amountSubmit);
    amountForm.append(
      el("small", {
        className: "muted",
        text:
          "同じ科目・同じ月へ入れ直すと上書きします。締め済みの月には保存できません。" +
          "入力しなかった科目は未入力のまま残ります。" +
          "0円と決めた科目は 0 と入れてください（欄を空のままでは保存できません）。",
      }),
    );
    onSubmit(
      amountForm,
      amountSubmit,
      (d) =>
        api.putFixedCost(me.storeId, month, {
          accountId: String(d.get("accountId") ?? ""),
          /* ★空欄・空白だけを 0 に化かさない（判断は `fixed-cost-view.ts` が持つ） */
          amount: amountToSend(String(d.get("amount") ?? "")),
        }),
      (m) => void load(me, date, "settings", m),
      "月額を保存しました",
    );
    card.append(amountForm);
  }

  // ---- 科目の登録（★追加だけ。`source` は送らない） ----

  const accountForm = el("form");
  accountForm.append(el("h3", { text: "科目を登録する" }));
  accountForm.append(field("科目コード", "accountCode", "", "text"));
  accountForm.append(field("科目名", "name", "", "text"));
  accountForm.append(
    select(
      "配賦方式",
      "allocMethod",
      (Object.keys(ALLOC_LABEL) as FixedCost["allocMethod"][]).map((k) => ({
        value: k,
        label: ALLOC_LABEL[k],
      })),
    ),
  );
  const accountSubmit = el("button", { text: "科目を登録する" });
  accountSubmit.type = "submit";
  accountForm.append(accountSubmit);
  accountForm.append(
    el("small", {
      className: "muted",
      text:
        "配賦方式はあとから変えられません（過去の月の営業利益が変わってしまうため）。" +
        "人件費・消耗品費・雑費は他の機能から自動で計上されるので、ここでは登録しません。",
    }),
  );
  onSubmit(
    accountForm,
    accountSubmit,
    (d) =>
      api.addExpenseAccount(me.storeId, {
        accountCode: String(d.get("accountCode") ?? "").trim(),
        name: String(d.get("name") ?? "").trim(),
        allocMethod: String(d.get("allocMethod") ?? ""),
      }),
    (m) => void load(me, date, "settings", m),
    "科目を登録しました",
  );
  card.append(accountForm);

  return card;
}

/**
 * 目標の入力（§3.4「月次目標」「日別売上目標」／R-03 の C-2）。
 *
 * ★**対象月はヘッダの「基準日」の月**。`date` に3つ目の意味を持たせない
 *   （`.loop/memory.md`「1つの入れ物に2つの意味を入れた失敗は、同じ課題の中で2回出る」）。
 *   設定タブは月のタブ（`isMonthlyTab`）ではないので、ここでは**読むだけ**にする。
 *
 * ★**未設定は空欄のまま出す**（0 を置かない）。判断は `targets-view.ts` が持つ。
 * ★**締め状態でボタンを止めない**のはパラメータ改定と同じ理由——
 *   どの月なら通るかは DB のトリガ（`migrations/0018`）が決め、弾かれたら本文で理由が返る。
 */
export function targetsCard(
  ctx: ScreenContext,
  month: string,
  monthly: MonthlyTarget[],
  daily: DailySalesTarget[],
  /** ★入力上限（0065）。書けない人には `undefined`＝`maxlength` は付かない */
  ceilings: {
    monthly?: MonthlyTargetCeilings | undefined;
    daily?: SalesTargetCeilings | undefined;
  },
): HTMLElement {
  const { me, date } = ctx;
  const card = el("section", { className: "card" });
  card.append(el("h3", { text: `${month} の目標` }));
  card.append(
    el("p", {
      className: "muted",
      text: "対象月は上の基準日から決まります。別の月を入れるときは基準日を動かしてください。",
    }),
  );

  // ---- 月次目標（達成率の分母。docs/02 §8.1 の2キーだけ） ----

  const monthlyForm = el("form");
  monthlyForm.append(el("h3", { text: "月次目標" }));
  for (const f of monthlyTargetFields(monthly)) {
    /*
     * ★★**上限はキーごと**（0065）。この列には円と人が同居しており、共用だと
     *   売上に合う上限（3,000万）が客数へ当たって 3,000万人 が通る。
     */
    monthlyForm.append(
      capped(field(`${f.label}（${f.unit}）`, `m_${f.key}`, f.value), ceilings.monthly?.[f.key]),
    );
    if (f.note !== null) {
      monthlyForm.append(el("small", { className: "muted", text: `${f.label}は${f.note}です` }));
    }
  }
  const monthlySubmit = el("button", { text: "月次目標を保存する" });
  monthlySubmit.type = "submit";
  monthlyForm.append(monthlySubmit);
  onSubmit(
    monthlyForm,
    monthlySubmit,
    (d) =>
      api.putMonthlyTargets(
        me.storeId,
        month,
        monthly
          .map((t) => ({ key: t.key, raw: String(d.get(`m_${t.key}`) ?? "").trim() }))
          // ★空欄は送らない。「触っていない項目」を 0 で上書きしない
          .filter((t) => t.raw !== "")
          .map((t) => ({ key: t.key, value: Number(t.raw) })),
      ),
    (m) => void load(me, date, "settings", m),
    "月次目標を保存しました",
  );
  card.append(monthlyForm);

  // ---- 日別売上目標（暦日を全日ぶん。営業日カレンダーは見ていない） ----

  const rows = dailyTargetRows(daily);
  const dailyForm = el("form");
  dailyForm.append(el("h3", { text: "日別売上目標" }));
  dailyForm.append(el("p", { className: "muted", text: dailyTargetsSummary(rows) }));
  /*
   * ★**日別の客数目標は入れない**（`docs/02` §8）。
   *   「月間客数目標 ÷ 営業日数」の派生値なので、日付別に入れる欄を作ると
   *   入力した値がどの式にも乗らない（2026-09-03・spec-reviewer が差し戻し）。
   */
  dailyForm.append(
    el("p", {
      className: "muted",
      text:
        "日ごとの客数目標は入れません。月間客数目標を営業日数で割った値になります" +
        "（営業日カレンダーが未整備のあいだは出せません）。",
    }),
  );
  for (const r of rows) {
    const line = el("div", { className: "target-day" });
    line.append(el("span", { text: r.label, className: r.note === null ? "" : "muted" }));
    line.append(capped(field("売上（円）", `s_${r.businessDate}`, r.salesValue), ceilings.daily?.targetSales));
    dailyForm.append(line);
  }
  const dailySubmit = el("button", { text: "日別売上目標を保存する" });
  dailySubmit.type = "submit";
  dailyForm.append(dailySubmit);
  dailyForm.append(
    el("small", {
      className: "muted",
      text:
        "空欄の日は保存しません（未設定のまま残ります）。" +
        "定休日を「目標0円」にするなら 0 と入れてください。締め済みの月には保存できません。",
    }),
  );
  onSubmit(
    dailyForm,
    dailySubmit,
    (d) =>
      api.putSalesTargets(
        me.storeId,
        month,
        rows
          .map((r) => ({
            businessDate: r.businessDate,
            sales: String(d.get(`s_${r.businessDate}`) ?? "").trim(),
          }))
          // ★空欄の日は送らない（未設定のまま残す）。0 と入れた日は送る
          .filter((r) => r.sales !== "")
          .map((r) => ({ businessDate: r.businessDate, targetSales: Number(r.sales) })),
      ),
    (m) => void load(me, date, "settings", m),
    "日別売上目標を保存しました",
  );
  card.append(dailyForm);

  return card;
}

/**
 * 営業日カレンダーの登録（§3.3 / `docs/02` §1.1 / D-06。M5）。
 *
 * ★**対象月はヘッダの「基準日」の月**。目標の入力（`targetsCard`）と同じ約束で、
 *   `date` に3つ目の意味を持たせない。
 *
 * ★★**月まるごと保存する。1日だけ保存する口を作らない。**
 *   欠けた月は `findMonthCalendar` が `CalendarError` で失敗する状態そのもので、
 *   営業日数・日商平均・固定費の配賦（D-06）・自動検算 C-09 が同時に止まる。
 *   チェックを1つ変えただけでも全日ぶん送る。
 *
 * ★**祝日（`isHoliday`）の入力欄は作らない**（Q-18 未回答・M5 の対象外）。
 *   保存されている値をそのまま往復させる。休業にするのは「営業日」を外す操作。
 *
 * ★**締め状態でボタンを止めない**のは目標の入力と同じ理由——
 *   どの月なら通るかはサーバ（`assertPeriodOpen`）と DB のトリガが決め、
 *   弾かれたら本文で理由が返る。
 */
export function calendarCard(ctx: ScreenContext, draft: CalendarDraft): HTMLElement {
  const { me, date } = ctx;
  const rows = calendarRows(draft.days);
  const card = el("section", { className: "card" });
  card.append(el("h3", { text: `${draft.month} の営業日カレンダー` }));
  card.append(el("p", { className: "muted", text: calendarSummary(draft) }));
  card.append(
    el("p", {
      className: "muted",
      text:
        "営業日数はこの表から数えます（手で入れる項目ではありません）。" +
        "対象月は上の基準日から決まります。",
    }),
  );

  const form = el("form");
  for (const r of rows) {
    const line = el("div", { className: "target-day" });
    line.append(el("span", { text: r.label }));

    // ★2列グリッドの右側に縦積みで入れる（空の `span` で桁を埋めない）
    const controls = el("div");
    const toggle = el("label", { className: "checkbox" });
    const box = el("input");
    box.type = "checkbox";
    box.name = `o_${r.businessDate}`;
    box.checked = r.isOpen;
    toggle.append(box, el("span", { text: "営業日" }));
    controls.append(toggle);
    controls.append(field("休業の理由（任意）", `r_${r.businessDate}`, r.closeReason, "text"));
    if (r.note !== null) {
      controls.append(el("small", { className: "muted", text: r.note }));
    }
    line.append(controls);
    form.append(line);
  }

  const submit = el("button", { text: "営業日カレンダーを保存する" });
  submit.type = "submit";
  form.append(submit);
  form.append(
    el("small", {
      className: "muted",
      text:
        "月まるごと保存します（1日だけの保存はできません）。" +
        "締め済み・締め申請済みの月には保存できません。",
    }),
  );
  onSubmit(
    form,
    submit,
    (d) =>
      api.putCalendar(
        me.storeId,
        draft.month,
        rows.map((r) => ({
          businessDate: r.businessDate,
          isOpen: d.get(`o_${r.businessDate}`) !== null,
          // ★祝日は画面から触らない。読み込んだ値をそのまま戻す（Q-18 未回答）
          isHoliday: r.isHoliday,
          closeReason: String(d.get(`r_${r.businessDate}`) ?? "").trim() || null,
        })),
      ),
    (m) => void load(me, date, "settings", m),
    "営業日カレンダーを保存しました",
  );
  card.append(form);
  return card;
}

/** ★設定タブで開いているカード。描き直し（保存の成功）をまたいで保つ。ページを離れれば消えてよい */
const openSettingsCards = new Set<string>();

/**
 * 設定タブのカードを畳む（`docs/10` §5・依頼者判断 案2）。
 *
 * ★カードの作りは変えない——先頭の見出し（h3）を `<summary>` へ移し、残りを `<details>` へ入れるだけ。
 *   開閉の仕組みは素の HTML。★覚えるのは「開いているか」だけ（保存のたびに閉じると続けて入力できない）。
 * @param forceOpen 気づかせる中身があるとき。畳んだ中に隠さない
 */
function foldCard(key: string, card: HTMLElement, forceOpen = false): HTMLElement {
  const fold = el("details");
  fold.open = forceOpen || openSettingsCards.has(key);
  const summary = el("summary");
  const [heading, ...rest] = [...card.childNodes];
  if (heading !== undefined) summary.append(heading);
  fold.append(summary, ...rest);
  fold.addEventListener("toggle", () => {
    if (fold.open) openSettingsCards.add(key);
    else openSettingsCards.delete(key);
  });
  card.append(fold);
  return card;
}

/**
 * 改定フォームに出す書式の手がかり。★判定は DB（`fn_validate_parameter_value`・0024）で、
 *   これは打つ前に形を知らせるだけ。enum / text は候補が定義側にあるので、ここでは言わない
 *   （違えば保存時に候補つきで断られる）。
 */
function formatHint(valueType: string, unit: string | null): string {
  const suffix = unit === null ? "" : `（単位: ${unit}）`;
  switch (valueType) {
    case "time":
      return `24時間表記で入力（例: 22:00）。`;
    case "date":
      return `日付で入力（例: 2026-10-01）。`;
    case "decimal":
      return `数値で入力（例: 0.25）${suffix}。`;
    case "integer":
      return `整数で入力${suffix}。`;
    case "boolean":
      return `true または false で入力。`;
    default:
      return "";
  }
}

function renderParameters(
  ctx: ScreenContext,
  data: {
    parameters: ParametersResult;
    minimumWage: MinimumWageCheck;
    /** ★owner だけが書けるキー（原典 §3.4.1 / R-05）。**サーバから受け取る。写しを持たない** */
    ownerOnlyKeys: string[];
  },
  /** ★C-14 を直す手立てに要る（M5c）。判定そのものは `data.minimumWage` のまま */
  wages: WagesResult,
  /** ★確認画面に「誰を動かすのか」を出すため。氏名の可視性はサーバが決めている */
  staff: Staff[],
  /** ★F-21 の棚卸。読めるのは manager 以上で、設定タブの到達条件と同じ */
  accounts: Account[],
  /** ★§3.4 の目標（R03-C-2）。対象月は `ctx.date` の月 */
  targets: {
    month: string;
    monthly: MonthlyTarget[];
    daily: DailySalesTarget[];
    /*
     * ★上限は**書ける人にだけ**返る（`GET /api/monthly-targets` の read は全ロール）。
     *   読むだけの人には `undefined` で、そのとき `maxlength` は付かない。
     */
    monthlyCeilings?: MonthlyTargetCeilings | undefined;
    dailyCeilings?: SalesTargetCeilings | undefined;
  },
  /** ★§3.3 の営業日カレンダー（M5）。対象月は目標と同じく `ctx.date` の月 */
  calendar: CalendarDraft,
  /** ★§3.3 の固定費（M5-FIXED-COST）。対象月は目標・カレンダーと同じ */
  fixedCosts: {
    rows: FixedCost[];
    /** ★書ける人（owner）にだけ返る */
    ceilings?: FixedCostCeilings | undefined;
  },
  message?: Notice,
): void {
  const { me, date } = ctx;
  const { resolved, usingDefault, unresolved } = data.parameters;
  const section = el("section");
  section.append(el("h2", { text: `${date} 時点の設定` }));
  if (message !== undefined) section.append(noticeOf(message));

  section.append(
    minimumWageCard(data.minimumWage, {
      me,
      date,
      tab: "settings",
      minimumWageFrom: wages.minimumWageFrom,
      exclusions: wages.exclusions,
      staff,
    }),
  );

  section.append(foldCard("accounts", accountReviewCard(ctx, accounts)));

  section.append(
    foldCard(
      "targets",
      targetsCard(ctx, targets.month, targets.monthly, targets.daily, {
        monthly: targets.monthlyCeilings,
        daily: targets.dailyCeilings,
      }),
    ),
  );

  section.append(foldCard("fixedCosts", fixedCostCard(ctx, targets.month, fixedCosts.rows, fixedCosts.ceilings)));

  section.append(foldCard("calendar", calendarCard(ctx, calendar)));

  // ★一覧と改定フォームも1枚のカードにして畳む
  const params = el("section", { className: "card" });
  params.append(el("h3", { text: "設定値の一覧と改定" }));

  /*
   * ★**全項目を1行ずつ出す**（SETTINGS-NAV・2026-09-24 依頼者「何をどこで設定できてどう効くのか分からない」）。
   *   値のある項目だけを行にして残りを段落に名前で詰めていたときは、
   *   開発DBで29項目中2行しか出ず、深夜の時間帯（未設定）は赤い段落の中にしか無かった。
   * ★手を付けるべき順（未設定→既定値→設定済み）に並べる。改定フォームの選択肢も同じ順。
   * ★説明文は定義から来る（0067）。**画面側に説明の対応表を持たない**（`ResolvedBase.label` と同じ理由）。
   */
  const rows: {
    p: { key: string; label: string; unit: string | null; description: string | null; valueType: string };
    value: string | null;
    state: string;
  }[] = [
    ...unresolved.map((p) => ({ p, value: null, state: "未設定（この日に有効な値がありません）" })),
    ...usingDefault.map((p) => ({ p, value: p.value, state: "既定値（適用期間を持ちません）" })),
    ...resolved.map((p) => ({
      p,
      value: p.value,
      state: `設定済み ${p.validFrom} 〜 ${p.validTo ?? "（次の改定まで）"}`,
    })),
  ];

  /*
   * ★賃金に効くキーは owner だけが書ける（原典 §3.4.1 / R-05）。
   *   ★**本数を書かない**——この一覧は増える（`wage_rounding` を 2026-09-07 に足した）。
   *   ★数えるなら `OWNER_ONLY_PARAMETER_KEYS`（`src/auth/permissions.ts`）をその場で見ること。
   *   **選択肢から外すのは使いやすさのため。** 禁止しているのはサーバ側
   *   （`assertCanWriteParameter`）と DB（`migrations/0037`）で、ここではない。
   *   ★外した項目は行に理由を出す。**黙って消すと「無くなった」と読まれる。**
   */
  const canWrite = (key: string): boolean => me.role === "owner" || !data.ownerOnlyKeys.includes(key);

  const list = el("ul", { className: "list param-list" });
  params.append(list);

  // ---- 改定フォーム ----

  const form = el("form");
  form.append(el("h3", { text: "値を改定する" }));
  const writable = rows.filter((r) => canWrite(r.p.key));
  const keyField = select(
    "項目",
    "key",
    writable.map((r) => ({ value: r.p.key, label: r.p.label })),
  );
  form.append(keyField);
  const keySelect = keyField.querySelector("select")!;
  // ★選んだ項目が何に効くか・どう書くかをフォームの中で読めるようにする（AC-6）
  const hint = el("p", { className: "muted param-hint" });
  hint.setAttribute("aria-live", "polite");
  const showHint = (): void => {
    const r = writable.find((w) => w.p.key === keySelect.value);
    hint.textContent =
      r === undefined ? "" : [formatHint(r.p.valueType, r.p.unit), r.p.description ?? ""].filter((s) => s !== "").join(" ");
  };
  keySelect.addEventListener("change", showHint);
  form.append(hint);
  showHint();

  for (const r of rows) {
    const li = el("li", { className: "param-row" });
    const head = el("div", { className: "param-head" });
    head.append(el("strong", { text: r.p.label }));
    head.append(
      el("span", { text: r.value === null ? "未設定" : `${r.value}${r.p.unit ?? ""}`, className: "amount" }),
    );
    li.append(head);
    li.append(el("small", { text: r.state, className: r.value === null ? "param-state error" : "param-state muted" }));
    if (r.p.description !== null) li.append(el("p", { text: r.p.description, className: "param-desc" }));
    if (canWrite(r.p.key)) {
      const edit = el("button", { text: "改定する →", className: "link" });
      edit.type = "button";
      edit.setAttribute("aria-label", `${r.p.label}を改定する`);
      // ★行から直接フォームへ（AC-5）。フォームは同じカードの下にある
      edit.addEventListener("click", () => {
        keySelect.value = r.p.key;
        showHint();
        form.scrollIntoView?.({ block: "start" });
        form.querySelector<HTMLInputElement>("input[name=value]")?.focus({ preventScroll: true });
      });
      li.append(edit);
    } else {
      li.append(el("small", { text: "オーナーのみ変更できます（賃金に直接効くため）", className: "muted" }));
    }
    list.append(li);
  }

  /*
   * ★既定値・未設定の件数は一覧の上でも言う（行の状態だけだと 29 行を読み切らないと気づけない）。
   *   既定値は適用期間を持たないので、過去日を引いても同じ値が返る。
   *   「当時の値で計算する」が成立していない状態（C-11 が failed とすべきもの）。
   */
  if (unresolved.length > 0) {
    list.before(notice(`${unresolved.length}件はこの日に有効な値がありません（一覧の「未設定」の行）。`, "error"));
  }
  if (usingDefault.length > 0) {
    list.before(
      notice(
        `${usingDefault.length}件が既定値で動いています。適用期間を持たないため、過去日も同じ値で計算されます。`,
        "info",
      ),
    );
  }

  form.append(field("新しい値", "value", "", "text"));
  form.append(field("適用開始日", "validFrom", date, "date"));
  form.append(field("出典・理由（任意）", "note", "", "text"));

  /*
   * ★締め状態でボタンを止めない。
   *
   *   締めた月があっても、**それ以降の日付から始まる改定は正当**。
   *   むしろ最低賃金の改定はいつもその形（過去は締まっていて、10月から新しい値）。
   *   ここで `locked` を見て止めると、0021 が
   *   「一度でも月を締めたら二度と改定できなくなる」を避けるために
   *   わざわざ差分判定にしたのを、画面側で台無しにする。
   *   ★どの日付なら通るかは DB が決める。弾かれたらその理由が本文で返る。
   */
  const submit = el("button", { text: "改定を保存する" });
  submit.type = "submit";
  form.append(submit);

  form.append(
    el("small", {
      text:
        "上書きではありません。適用開始日の前日で古い値を閉じ、履歴として積みます。" +
        "締め済みの月に有効な値が変わる改定は保存できません。",
      className: "muted",
    }),
  );

  onSubmit(
    form,
    submit,
    (d) =>
      api.reviseParameter(me.storeId, String(d.get("key") ?? ""), {
        value: String(d.get("value") ?? "").trim(),
        validFrom: String(d.get("validFrom") ?? ""),
        note: String(d.get("note") ?? "").trim() || null,
      }),
    (m) => void load(me, date, "settings", m),
    "改定を保存しました",
  );
  params.append(form);
  // ★この日に有効な値が無い項目は、畳んだ中に隠さない
  section.append(foldCard("parameters", params, unresolved.length > 0));
  ctx.root.append(section);
}

// ---------------------------------------------------------------------

/**
 * その日のデータを読んで描く。
 *
 * ★`staff` はロス記録を**書けるが読めない**（権限マトリクス §3.1 の注記）。
 *   403 が返るのは正常なので、エラーにせず `null` として扱い、理由を画面に出す。
 */
export async function load(me: Me, date: string, tab: Tab = "sales", message?: Notice): Promise<void> {
  // ★この1行が「いま求めている画面」を決める。遅れて返った古い応答は renderShell が捨てる
  latestRequest = { date, tab };
  /*
   * ★M5b：**画面遷移あたり1回だけ**取る（AC-5）。タブの分岐へ入れると
   *   タブごとに書くことになり、足し忘れたタブが黙る。
   *
   * ★`try` の外に置く。中の `const` にしていたとき、**`catch` の再描画では
   *   警告が消えていた**（2026-08-18・code-reviewer 指摘）。
   *
   * ★`staff` / `leader` には出さない（AC-4）。ここで呼ばないのは表示の都合で、
   *   **担保はサーバの 403**（`staff_wage` の read は manager 以上）。
   */
  let wageAlert: WageAlertState | undefined;
  /*
   * ★シフトの変更・公開への気づき（R0102 B-7）。**画面遷移あたり1回だけ**取る
   *   （`wageAlert` と同じ理由——タブの分岐へ入れると、足し忘れたタブが黙る）。
   *
   * ★`try` の外に置く（`wageAlert` と同じ。`catch` の再描画で帯が消えないように）。
   *
   * ★★**シフトタブでは聞かない。** サーバは `GET /api/shifts` を返した時点で既読にするが、
   *   ここで同時に聞くと**既読になる前の答え**が返り、シフトを開いた画面に
   *   「変更があります」が残る（AC-8 の「開く→印が消える」と食い違う）。
   *   ★意味の上でも、**シフトを見ている最中に「シフトを見に行け」とは言わない。**
   */
  let shiftNotice: ShiftNoticeState | undefined;
  try {
    // ★kintai へ移った後は聞かない（サーバは 410。`docs/19` D-1）
    if (tab !== "shift" && me.attendanceInKintai !== true) {
      shiftNotice = await api.getShiftNotice(me.storeId).catch((e: unknown) => {
        /*
         * ★業務画面は出す（気づきの失敗で日次入力が止まるほうが害が大きい）。
         *   ただし**取れなかったことは画面に出す**（M5b の差し戻しと同じ形）。
         */
        console.error(
          "[ui] シフトの気づきを取得できなかった",
          e instanceof Error ? e.message : String(e),
        );
        return "unavailable" as const;
      });
    }
    /*
     * ★M5b：**画面遷移あたり1回だけ**取る（AC-5）。タブの分岐へ入れると
     *   タブごとに書くことになり、足し忘れたタブが黙る。
     *
     * ★`staff` / `leader` には出さない（AC-4）。ここで呼ばないのは表示の都合で、
     *   **担保はサーバの 403**（`staff_wage` の read は manager 以上）。
     *   叩かれても返らないことは `tests/authz` が見ている。
     */
    if (me.role === "manager" || me.role === "owner") {
      wageAlert = await api.getMinimumWageAlert(me.storeId).catch((e: unknown) => {
        /*
         * ★業務画面は出す（警告の失敗で日次入力が止まるほうが害が大きい）。
         *   ただし**取れなかったことは画面に出す。** `undefined` で返さない。
         */
        console.error(
          "[ui] 最低賃金の警告を取得できなかった",
          e instanceof Error ? e.message : String(e),
        );
        return "unavailable" as const;
      });
    }

    if (tab === "loss") {
      const records = await api
        .getLoss(me.storeId, date)
        .then((r) => r.records)
        .catch((e: unknown) => {
          if (e instanceof ApiError && e.status === 403) return null; // ★staff は読めない
          throw e;
        });
      // 締め状態は売上側から取る（staff は売上を読めないので、その場合は入力中扱い）
      const closing = await api
        .getSales(me.storeId, date)
        .then((r) => r.closingStatus)
        .catch(() => "OPEN" as ClosingStatus);
      renderLoss(renderShell(me, date, closing, tab, wageAlert, shiftNotice), records, message);
      return;
    }

    if (tab === "punch") {
      /*
       * ★ボタンの状態はクライアント時計を読まない（`nextPunchOptions` が
       *   `events` の `businessDate` から決める）。ここで読む「今月」は
       *   **何を取りに行くかの範囲**でしかない（自分の直近の勤務を含む月であれば足りる）。
       */
      const month = punchMonth();
      // ★`scope=own`：manager / owner でも自分の分だけ（A-5 AC-8。全員分と混ぜない）
      const [punchLog, { anomalies }, closing, staffRes] = await Promise.all([
        api.getPunches(me.storeId, month, { scope: "own" }),
        api.getAttendanceAnomalies(me.storeId, month),
        api
          .getSales(me.storeId, date)
          .then((r) => r.closingStatus)
          .catch(() => "OPEN" as ClosingStatus),
        // ★従業員選択（A-5 AC-2）の選択肢。staff / leader は呼ばない（担保はサーバの 403）
        me.role === "manager" || me.role === "owner" ? api.getStaff(me.storeId) : null,
      ]);
      renderPunch(
        renderShell(me, date, closing, tab, wageAlert, shiftNotice),
        punchLog,
        anomalies,
        staffRes === null ? null : staffRes.staff,
        message ?? (punchLog.message === undefined ? undefined : infoNotice(punchLog.message)),
      );
      return;
    }

    if (tab === "shift") {
      /*
       * ★★**表示中の月は、ヘッダの「対象月」ピッカーが決める**（R0102 B-8）。
       *   ここが `punchMonth()`＝**クライアント時計の今月**だったあいだ、
       *   翌月のシフトは誰にも見られず、店長は来月ぶんを画面から公開できなかった
       *   （`publishControls` は `min`/`max` も件数プレビューもこの `month` から出す）。
       * ★**月の決定は1箇所だけ**——`renderShifts` にも `publishControls` にも同じ値を渡す
       *   （2箇所で決めると、表と公開範囲が別の月を指す日が来る）。
       * ★従業員の選択肢は manager / owner のときだけ取りに行く（担保はサーバの 403）。
       */
      const month = date.slice(0, 7);
      const canManage = me.role === "manager" || me.role === "owner";
      /*
       * ★★**確定シフトが取れたなら、必ず画面に出す**（2026-09-03・周回3 / spec の差し戻し3）。
       *
       *   サーバは `GET /api/shifts` を返した時点で**気づきを既読にする**。
       *   ★以前はここの兄弟（希望・修正申請・従業員マスタ）に `.catch` が無く、
       *   そのどれか1本が転ぶと `Promise.all` ごと reject して**売上タブのエラー画面**へ落ちた——
       *   ★**シフトを1文字も見ていないのに既読だけ確定し、その事象は二度と知らされない**
       *   （spec が `fetch` を差し替えて実機で再現）。履歴一覧も外部通知も対象外なので**回復手段が無い。**
       *
       *   ★**既読の意味（＝本人が見た）を、シフトと無関係な並列リクエストの成否に握らせない。**
       *   転んだ側は空で描き、**転んだことは画面に出す**（黙って空にしない）。
       */
      let degraded: Notice | undefined;
      const onFail =
        <T,>(what: string, empty: T) =>
        (e: unknown): T => {
          console.error(`[ui] ${what}を取得できなかった`, e instanceof Error ? e.message : String(e));
          degraded = errorNotice("一部の情報を読み込めませんでした。時間をおいて開き直してください。");
          return empty;
        };
      const [shiftRes, requestRes, changeRequestRes, closing, staffRes] = await Promise.all([
        api.getShifts(me.storeId, month),
        // ★希望の一覧は全ロールが読む（自分の分は §3.7 のとおりサーバが絞る）
        api
          .getShiftRequests(me.storeId, month)
          // ★`linked` は嘘をつかない側（`true`）に倒す。未紐付けの案内を誤って出さないため
          .catch(onFail("希望シフトの一覧", { requests: [], window: null, linked: true })),
        // ★修正申請も同じく全ロールが読む（R0102 B-4）
        api
          .getShiftChangeRequests(me.storeId, month)
          .catch(onFail("修正申請の一覧", { requests: [], linked: true })),
        api
          .getSales(me.storeId, date)
          .then((r) => r.closingStatus)
          .catch(() => "OPEN" as ClosingStatus),
        canManage ? api.getStaff(me.storeId).catch(onFail("従業員マスタ", { staff: [] })) : null,
      ]);
      renderShifts(
        renderShell(me, date, closing, tab, wageAlert, shiftNotice),
        shiftRes,
        requestRes,
        changeRequestRes,
        staffRes === null ? null : staffRes.staff,
        month,
        message ?? degraded,
      );
      return;
    }

    if (tab === "settings") {
      /*
       * ★目標の対象月は**基準日の月**（R03-C-2）。`date` に3つ目の意味を持たせず、
       *   ここで読むだけにする（設定タブは `isMonthlyTab` に入れない）。
       */
      const targetMonth = date.slice(0, 7);
      const [
        data,
        wages,
        { staff },
        { accounts },
        closing,
        monthlyTargets,
        dailyTargets,
        { calendar },
        fixedCosts,
      ] = await Promise.all([
        api.getParameters(me.storeId, date),
        /*
         * ★C-14 のカードから一括改定できるようにするため（M5c）。
         *   時給マスタの read も §3.4 と同じ manager 以上なので、この画面に来られる
         *   相手は必ず読める。判定そのものは `data.minimumWage` を使い続ける
         *   （同じ指標を2箇所から取ると、片方だけ古い値で描く余地ができる）。
         */
        api.getWages(me.storeId, date),
        /*
         * ★確認画面に「誰を動かすのか」を出すため（AC-8）。
         *   §3.3「従業員マスタ」の read も manager 以上なので、この画面に来られる相手は読める。
         *   氏名が届くかどうかはサーバの `redactPayroll` が決めている。
         */
        api.getStaff(me.storeId),
        /*
         * ★F-21 の棚卸（M5f）。§3.5 の read は manager 以上で、
         *   この画面に来られる相手は必ず読める（設定タブは manager 以上）。
         *   ★止められるのは owner だけ。その線はサーバと DB が引いている。
         */
        api.getAccounts(me.storeId),
        // 締め状態は表示のためだけ。改定できるかどうかは DB が日付で決める
        api.getSales(me.storeId, date).then((r) => r.closingStatus),
        /*
         * ★§3.4 の目標（R03-C-2）。どちらも read は manager 以下ではないので、
         *   設定タブ（manager 以上）に来られる相手は必ず読める。
         *   ★**2本に分けて取る。** 原典 §3.4 で別の行であり、
         *   C-4 で片方（月次目標）だけが `staff`/`leader` へ開く。
         */
        api.getMonthlyTargets(me.storeId, targetMonth),
        api.getSalesTargets(me.storeId, targetMonth),
        /*
         * ★§3.3 の営業日カレンダー（M5）。read は全ロールなので、
         *   設定タブ（manager 以上）に来られる相手は必ず読める。
         *   ★まだ登録していない日は定休日パラメータから作った**下書き**が入って返る
         *   （`registered: false`）。保存するまで営業日数は確定しない。
         */
        api.getCalendar(me.storeId, targetMonth),
        /*
         * ★§3.3 の固定費（M5-FIXED-COST）。read は manager 以上で、
         *   設定タブ（manager 以上）に来られる相手は必ず読める。
         *   ★上限（`ceilings`）は **owner にだけ**返る。読むだけの人には `undefined`。
         */
        api.getFixedCosts(me.storeId, targetMonth),
      ]);
      renderParameters(
        renderShell(me, date, closing, tab, wageAlert, shiftNotice),
        data,
        wages,
        staff,
        accounts,
        {
          month: targetMonth,
          monthly: monthlyTargets.targets,
          daily: dailyTargets.targets,
          monthlyCeilings: monthlyTargets.ceilings,
          dailyCeilings: dailyTargets.ceilings,
        },
        calendar,
        { rows: fixedCosts.fixedCosts, ceilings: fixedCosts.ceilings },
        message,
      );
      return;
    }

    if (tab === "staff") {
      const [{ staff }, wages, { accounts }, { anomalies }, closing] = await Promise.all([
        api.getStaff(me.storeId),
        api.getWages(me.storeId, date),
        /*
         * ★紐付け欄の選択肢（未紐付けアカウント＋現在の紐付け先）を出すため（R0102 A-2 / AC-10）。
         *   §3.5「退職者処理・アカウント棚卸」の read も manager 以上なので、
         *   この画面に来られる相手（manager 以上）は必ず読める。
         */
        api.getAccounts(me.storeId),
        /*
         * ★docs/02 §4.3「検出して出す」の宛先（R0102 A-3・AC-10）。
         *   manager 以上がこの画面へ来た時点で店舗全員分の未退勤・不整合を見られる
         *   （導線：ログイン → 「従業員」タブを開く。追加のクリック無し）。
         */
        api.getAttendanceAnomalies(me.storeId, date.slice(0, 7)),
        // 締め状態は表示のためだけ。登録できるかどうかは DB が日付で決める（設定タブと同じ）
        api.getSales(me.storeId, date).then((r) => r.closingStatus),
      ]);
      renderStaff(
        renderShell(me, date, closing, tab, wageAlert, shiftNotice),
        staff,
        wages,
        accounts,
        anomalies,
        message,
      );
      return;
    }

    if (tab === "audit") {
      const page = await api.getAudit(me.storeId, auditQueryParams(auditFilter));
      const closing = await api.getSales(me.storeId, date).then((r) => r.closingStatus);
      renderAudit(renderShell(me, date, closing, tab, wageAlert, shiftNotice), page, message);
      return;
    }

    if (tab === "diagnosis") {
      const diagnosis = await api.getSelfDiagnosis(me.storeId);
      const closing = await api.getSales(me.storeId, date).then((r) => r.closingStatus);
      renderSelfDiagnosis(renderShell(me, date, closing, tab, wageAlert, shiftNotice), diagnosis, message);
      return;
    }

    if (tab === "workflow") {
      /*
       * ★日報は **manager 以上**（docs/05 §3.1）。`leader` には聞きに行かない——
       *   聞けば 403 が返るだけだが、**画面に「取得できません」が出て入力の邪魔になる。**
       *   ★出し分けは表示の都合で、担保はサーバの 403（絶対ルール1）。
       */
      const canReadReport = me.role === "manager" || me.role === "owner";
      const [{ documents }, sales, report, { purchases }, { entries: pettyEntries }] = await Promise.all([
        api.getDocuments(me.storeId, date),
        api.getSales(me.storeId, date),
        canReadReport ? api.getDailyReport(me.storeId, date) : Promise.resolve({ report: null }),
        // ★「今日の数字」のため（TODAY-HOME-ASTRA）。どちらも leader 以上の read で、タブの到達条件と同じ
        api.getPurchases(me.storeId, date),
        api.getPettyCash(me.storeId, date),
      ]);
      const ctx = renderShell(me, date, sales.closingStatus, tab, wageAlert, shiftNotice);
      if (message !== undefined) ctx.root.append(noticeOf(message));
      /*
       * ★ホーム／伝票／振り返りの3画面（TODAY-HOME-ASTRA）。**切り替えは隠すだけ。**
       *   作り直すと、撮った写真や打ちかけの振り返りが消える。戻るときだけ取り直す（`confirmLeave` を通す）。
       */
      const leave = (next: Tab) => (): void => {
        if (confirmLeave()) void load(me, date, next);
      };
      const pane = (content: HTMLElement): HTMLElement => {
        const wrap = el("div", { className: "today-pane" });
        const back = el("button", { text: "← 今日の業務へ戻る", className: "link" });
        back.type = "button";
        back.addEventListener("click", leave("workflow"));
        wrap.append(back, content);
        wrap.hidden = true;
        return wrap;
      };
      const intakePane = pane(documentIntakeView(documents, {
        upload: async (input) => { await api.uploadDocument(me.storeId, { businessDate: date, ...input }); },
        openImage: (id) => api.getDocumentImage(me.storeId, id),
        list: () => api.getDocuments(me.storeId, date).then((result) => result.documents),
        runOcr: (id) => api.runDocumentOcr(me.storeId,id),
        skipOcr: (id) => api.skipDocumentOcr(me.storeId,id),
        saveDraft: (id,version,draft) => api.saveDocumentDraft(me.storeId,id,version,draft),
        registrationOptions: async () => { const [vendors,petty]=await Promise.all([api.getVendors(me.storeId),api.getPettyCash(me.storeId,date)]);return {vendors:vendors.vendors.map((v)=>({id:v.id,name:v.name,category:v.category})),accounts:petty.accounts.map((a)=>({id:a.id,name:a.name}))}; },
        register: (id,kind,payload) => api.registerDocument(me.storeId,id,kind,payload),
        /*
         * ★登録先の画面へ移動する（引き継ぎ §8.4）。**同じ営業日のまま**移る。
         * ★ID はサーバの再検証を省く材料ではない——移動先の画面は今までどおり
         *   `storeId` つきで取り直す。ここで使うのは「どの行を指すか」だけ。
         */
        openRegistered: (target, recordId) => {
          const tabOfTarget: Record<string, Tab | undefined> = { purchases: "purchases", petty_cash_entries: "petty", daily_sales: "sales" };
          const next = tabOfTarget[target];
          if (next === undefined) return;
          highlightRecord = recordId === null ? null : { target, id: recordId };
          void load(me, date, next);
        },
      },{canApprovePurchase:me.role==="manager"||me.role==="owner"}));
      let reportPane: HTMLElement | null = null;
      if (canReadReport) {
        /*
         * ★報告文の数値は**登録済みの売上**から作る（OCR の読取候補は使わない）。
         *   ★`sales.sales` は文字列で来る（`numeric` をそのまま運んでいる）ので、
         *     ここで数値へ直してから渡す。渡せるのは6つの数値だけ（型で塞いである）。
         */
        const s = sales.sales;
        reportPane = pane(dailyReportView({
          businessDate: date,
          saved: report.report?.comment ?? null,
          sales: s === null ? null : {
            salesInclTaxDinner: Number(s.salesInclTaxDinner),
            guestsDinner: s.guestsDinner,
            groupsDinner: s.groupsDinner,
            cashAmount: Number(s.cashAmount),
            cardAmount: Number(s.cardAmount),
            otherAmount: Number(s.otherAmount),
          },
          api: {
            save: (comment) => api.saveDailyReport(me.storeId, date, comment).then((r) => r.report),
          },
        }));
      }
      const panes: HTMLElement[] = [];
      const show = (target: HTMLElement) => (): void => {
        for (const p of panes) p.hidden = p !== target;
        window.scrollTo(0, 0);
      };
      const home = todayHomeView({
        businessDate: date,
        documents,
        sales: sales.sales,
        purchases,
        pettyEntries,
        reportSaved: canReadReport ? report.report !== null : null,
        open: {
          intake: show(intakePane),
          report: reportPane === null ? show(intakePane) : show(reportPane),
          records: leave("sales"),
        },
      });
      panes.push(home, intakePane, ...(reportPane === null ? [] : [reportPane]));
      ctx.root.append(...panes);
      return;
    }

    if (tab === "closing") {
      // ★売上要約は C-4 で「売上要約」タブへ移した（同じ意味の表示を2箇所に持たない）
      const data = await api.getClosingChecks(me.storeId, date.slice(0, 7));
      renderClosing(
        renderShell(me, date, data.closingStatus, tab, wageAlert, shiftNotice),
        data,
        message,
      );
      return;
    }

    if (tab === "summary") {
      /*
       * ★売上要約（R-03 / C-4）。**全ロールが開ける唯一の売上の面。**
       *
       * ★`date` は日次のまま渡す。**当月は日付から決まる**ので期間の選択欄を作らない——
       *   作ると `date` が3つ目の意味（グラフの期間）を持ち、`isMonthlyTab` の門
       *   （R0102 B-8）を1本増やすことになる。`isMonthlyTab` にも足さない。
       * ★締め状態はシェルの表示のためだけ。**`staff` は `GET /api/sales` に届かない**（§3.1）ので
       *   **聞きに行かない**（従業員の選択肢と同じ形。担保はサーバの 403）。
       *   ★他のタブは 403 を `.catch` で受けているが、**結果は同じ「OPEN」で、
       *     コンソールに毎回 403 が残るだけ**なので、ここでは往復ごと落とす。
       *   ★天井：`staff` には常に「入力中」と出る（他のタブと同じ。締め済みでも変わらない）。
       * ★要約が 400（税率・端数処理が未登録）のときは、その理由をカードに出す——**黙って空にしない。**
       */
      const [summary, closing, profitLossResult] = await Promise.all([
        api
          .getSalesSummary(me.storeId, date)
          .then((r) => r.summary)
          .catch((e: unknown) =>
            e instanceof ApiError ? e.message : "売上要約を読み込めませんでした",
          ),
        me.role === "staff"
          ? Promise.resolve("OPEN" as ClosingStatus)
          : api
              .getSales(me.storeId, date)
              .then((r) => r.closingStatus)
              .catch(() => "OPEN" as ClosingStatus),
        /*
         * ★損益は §3.2（manager 以上）。それ以外は聞きに行かない（担保はサーバの 403）。
         * ★異常検知（F-16）は同じ応答に同梱されている（`docs/05` §3.2 の注記）。
         *   別ルートを聞きに行かない——聞けば聞くほど 403 の経路が増える。
         */
        me.role === "manager" || me.role === "owner"
          ? api
              .getProfitLoss(me.storeId, date)
              .then((r) => ({ profitLoss: r.profitLoss, anomalies: r.anomalies }))
              .catch((e: unknown) => (e instanceof ApiError ? e.message : "損益を読み込めませんでした"))
          : Promise.resolve(null),
      ]);
      const ctx = renderShell(me, date, closing, tab, wageAlert, shiftNotice);
      if (message !== undefined) ctx.root.append(noticeOf(message));
      ctx.root.append(salesSummaryCard(summary));
      if (profitLossResult !== null) {
        ctx.root.append(
          typeof profitLossResult === "string"
            ? profitLossCard(profitLossResult)
            : profitLossCard(profitLossResult.profitLoss, profitLossResult.anomalies),
        );
      }
      return;
    }

    if (tab === "inventory") {
      const {
        inventories,
        closingStatus,
        ceilings: inventoryCeilings,
      } = await api.getInventory(me.storeId, date.slice(0, 7));
      renderInventory(
        renderShell(me, date, closingStatus, tab, wageAlert, shiftNotice),
        inventories,
        inventoryCeilings,
        message,
      );
      return;
    }

    if (tab === "petty") {
      const data = await api.getPettyCash(me.storeId, date);
      renderPettyCash(renderShell(me, date, data.closingStatus, tab, wageAlert, shiftNotice), data, message);
      return;
    }

    if (tab === "purchases") {
      const [{ purchases, closingStatus, ceilings: purchaseCeilings }, { vendors }] =
        await Promise.all([api.getPurchases(me.storeId, date), api.getVendors(me.storeId)]);
      renderPurchases(
        renderShell(me, date, closingStatus, tab, wageAlert, shiftNotice),
        purchases,
        vendors,
        purchaseCeilings,
        message,
      );
      return;
    }

    const { sales, closingStatus, ceilings, missingDates } = await api.getSales(me.storeId, date);
    renderSalesTab(
      renderShell(me, date, closingStatus, tab, wageAlert, shiftNotice),
      sales,
      ceilings,
      message,
      missingDates ?? null,
    );
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      renderLogin(error.message);
      return;
    }
    /*
     * ★★**読み込めなかったタブに、売上の入力欄を出さない**（2026-09-20）。
     *
     *   それまでここは `renderSalesTab(…, null, null, エラー)` を呼んでいた——
     *   ★どのタブで失敗しても、**そのタブの名前の下に「売上」の入力フォームが出ていた。**
     *   - `staff` は売上を読めも書けもしないのに（`docs/05` §3.1）、
     *     打刻が読めなかっただけで**売上の入力欄を見せられ**、打てば 403 で断られる。
     *   - 締め済みの月でも `"OPEN"` を渡していたので、**「変更できません」が消えて**押せる形で出ていた
     *     （止めるのはサーバだけになる。絶対ルール7 はアプリ層とDBの両方で禁じろと言っている）。
     *
     * ★**再読み込みは置く。** ナビから同じタブを押しても戻れるが、
     *   失敗した画面に操作が1つも無いと「壊れて終わった」に見える。
     */
    /*
     * ★`"OPEN"` を渡しているのは、この画面に**書ける操作が1つも無い**から
     *   （`renderShell` は `OPEN` のとき締め状態の行を出さない）。
     *   ★★**ここに操作を1つでも足すなら、締め済みの月が `"OPEN"` 扱いになる**——
     *   そのときは締め状態を取り直すか、`locked` を立てる側へ寄せること（security の A-5）。
     */
    const ctx = renderShell(me, date, "OPEN", tab, wageAlert, shiftNotice);
    const failed = el("section");
    failed.append(
      noticeOf(errorNotice(error instanceof ApiError ? error.message : "読み込めませんでした")),
    );
    const retry = el("button", { text: "もう一度読み込む" });
    retry.type = "button";
    retry.addEventListener("click", () => void load(me, date, tab));
    failed.append(retry);
    ctx.root.append(failed);
  }
}

/**
 * 初回に開くタブ（R0102 B-4 AC-6）。
 *
 * ★**`staff` は「打刻」にする**（旧「ロス」から変更）。打刻は毎日必ず使う面で、
 *   ロスは書けるが読めない（§3.1 の注記）——書くだけの面を毎回の入口にしない。
 * ★staff は売上・仕入を読めないので、それらは選ばない（開いた瞬間に 403 を見せない）。
 * ★純粋関数として切り出し、`start()` を経由しなくても直接検査できるようにする。
 */
/**
 * そのロールに出すタブ。★打刻・シフトが kintai へ移っていれば（`docs/19` D-1）、勤怠のまとまりを出さない。
 * ★見た目の都合。サーバは同じ経路を 410 で閉じている
 */
function tabsFor(role: Me["role"], attendanceInKintai: boolean): (typeof TABS)[number][] {
  return TABS.filter((t) => t.roles.includes(role) && !(attendanceInKintai && t.group === "attendance"));
}

export function initialTabFor(role: Me["role"], attendanceInKintai = false): Tab {
  if (role !== "staff") return "workflow";
  return attendanceInKintai ? "loss" : "punch";
}

/**
 * ★店に貼る QR（R-07 案 (a)）：URL の `#punch` で、どのロールでも打刻タブから開く。
 * 指定が無い・そのロールに無いタブなら、従来どおり `initialTabFor`。
 */
export function startTabFor(role: Me["role"], hash: string, attendanceInKintai = false): Tab {
  const key = hash.replace(/^#/, "");
  const tab = tabsFor(role, attendanceInKintai).find((t) => t.key === key);
  return tab === undefined ? initialTabFor(role, attendanceInKintai) : tab.key;
}

async function start(): Promise<void> {
  if (!root) return;
  root.setAttribute("aria-busy", "false");
  try {
    const me = await api.me();
    if (me.mustChangePassword) {
      renderPasswordChange(me);
      return;
    }
    await load(me, yesterday(), startTabFor(me.role, location.hash, me.attendanceInKintai === true));
  } catch (error) {
    renderLogin(error instanceof ApiError && error.status !== 401 ? error.message : undefined);
  }
}

/**
 * デモ環境の帯（`docs/12` §8）。★ホスト名が `demo` で始まるときだけ出す（`demostoresystem.osawagase.jp`）。
 * 本番と同じ画面・同じ版が別のホストで動くので、**見た目で区別できる唯一の印**。
 * `#app` の外（body 直下）に置く——`renderShell` は `#app` を描き直すが、ここは残る。
 */
export function demoHostBand(hostname: string): HTMLElement | null {
  if (!/^demo/.test(hostname)) return null;
  const band = el("p", { className: "notice error demo-band", text: "デモ環境です（練習用）。ここに入れた記録は実務に使いません" });
  band.setAttribute("role", "status");
  return band;
}

/**
 * 対になる kintai（打刻・シフト）のアドレス。★本番↔本番、デモ↔デモ（`docs/12` §8）。
 * 知らないホスト（開発の localhost など）では出さない——行き先を推測しない。
 */
export function kintaiUrlFor(hostname: string): string | null {
  const pairs: Record<string, string> = {
    "app.osawagase.jp": "https://kintai.osawagase.jp/",
    "demostoresystem.osawagase.jp": "https://demokintai.osawagase.jp/",
  };
  return Object.hasOwn(pairs, hostname) ? pairs[hostname]! : null;
}

/**
 * 文字を打っている欄があるか。★画面下に固定した主ナビは、打っている間だけ隠す
 *   （ソフトキーボードの上に浮いて入力欄を覆うため。`styles.css` の `body.typing`）。
 */
export function isTypingField(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    target.matches("textarea, select, input:not([type=checkbox], [type=radio], [type=button], [type=submit], [type=file])")
  );
}

async function main(): Promise<void> {
  window.addEventListener("beforeunload", onBeforeUnload);
  document.addEventListener("focusin", (e) => document.body.classList.toggle("typing", isTypingField(e.target)));
  document.addEventListener("focusout", (e) => document.body.classList.toggle("typing", isTypingField(e.relatedTarget)));
  const band = demoHostBand(location.hostname);
  if (band !== null) document.body.prepend(band);
  await start();

  // Service Worker は本番ビルドでのみ登録する。
  // 開発中に古いシェルがキャッシュされると、修正が反映されない原因の切り分けに時間を取られる。
  if (!import.meta.env.DEV && "serviceWorker" in navigator) {
    await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  }
}

void main();
